import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import TimezoneSelect from 'react-timezone-select';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../context/ToastContext';
import Navigation from '../components/Navigation';
import Button from '../components/Button';
import TeamReminderSettingsCard from '../components/TeamReminderSettingsCard';
import LocationAutocomplete, { LocationData } from '../components/LocationAutocomplete';
import AddTeamMember from '../components/AddTeamMember';
import PublishLineupDialog from '../components/PublishLineupDialog';
import PlayerDropdown from '../components/PlayerDropdown';
import SuggestLineupsModal from '../components/SuggestLineupsModal';
import DifficultyGauge from '../components/DifficultyGauge';
import AnalyticsTab from '../components/analytics/AnalyticsTab';
import { formatMatchType } from '../utils/formatMatchType';
import { extractScoreWarnings } from '../utils/scoreWarnings';
import { MatchTypeIcon } from '../components/icons/MatchTypeIcon';
import axios from 'axios';
import { Users, Calendar, CalendarCheck, Target, Settings, Eye, Trophy, Trash2, UserCheck, UserX, X, Plus, Save, Upload, FileUp, MapPin, Edit, Mail, Copy, ChevronDown, FileText, Info, AlertTriangle, Brain, Layout, Search, ArrowLeftRight, CheckCircle, XCircle, AlertCircle, HelpCircle, Check, Filter, Clock, ArrowUp, ArrowDown, WandSparkles, Printer, BarChart3 } from 'lucide-react';
import { formatMatchDateFull, formatMatchDateCompact } from '../utils/dateFormatting';
import { usePostHog } from 'posthog-js/react';
import { useFeatureFlag, useFeatureFlagVariant } from '../utils/useFeatureFlag';
import { confirmLineupPosition, unconfirmLineupPosition } from '../services/lineupConfirmationService';
import { validateMatchLineup as validateMatchLineupUtil, ValidationError, LineupPosition as ValidationLineupPosition, LineupFormat as ValidationLineupFormat } from '../utils/lineupValidation';
import { getEligiblePlayersForPosition, filterPlayer1Options, filterPlayer2Options } from '../utils/lineupPlayerFiltering';
import { NTRP_RATINGS, formatNtrpRating } from '../utils/ntrpRatings';
import { calculateFirstMatchDate, calculateLastMatchDate, Match as LeagueDateMatch } from '../utils/leagueDates';
import { getDisplayName } from '../utils/displayName';

interface TeamMember {
  id: number;
  team_id: number;
  user_id: number;
  join_date: string;
  active_status: boolean;
  is_captain: boolean;
  user: {
    id: number;
    name: string;
    display_name?: string;
    email: string;
    role: 'ADMIN' | 'PLAYER';
    google_id?: string | null;
    firebase_uid?: string | null;
    tennisrecord_name?: string | null;
    gender?: 'male' | 'female' | null;
    ntrp_rating?: number | null;
  };
  // Rating fields from competitive intelligence
  ntrp_rating?: number | null;
  dynamic_rating?: number | null;
  rating_last_updated?: string | null;
  // Team membership count (populated when team is fetched)
  team_memberships_count?: number | null;
  // Ordered lineup type preferences (index 0 = most preferred)
  lineup_type_preferences?: string[] | null;
}

interface Team {
  id: number;
  name: string;
  league?: string;
  section?: string;
  district_area?: string;
  flight_subflight?: string;
  usta_team_id?: string;
  league_start_date?: string;
  league_end_date?: string;
  use_first_match_for_start_date?: boolean;
  use_last_match_for_end_date?: boolean;
  timezone?: string;
  tennisrecord_url?: string;
  competitive_intel_enabled: boolean;
  competitive_intel_last_fetch?: string;
  max_combined_ntrp_rating?: number | null;
  match_win_criteria?: string | null;
  reminder_player_days_ahead?: number | null;
  reminder_captain_days_ahead?: number | null;
  reminder_player_enabled?: boolean;
  reminder_captain_enabled?: boolean;
  created_at: string;
  created_by?: number;
  members: TeamMember[];
  lineup_format?: {
    id: number;
    total_lines: number;
    max_sets_per_line: number;
    positions: Array<{
      position_number: number;
      match_type: string;
    }>;
  };
}

interface Match {
  id: number;
  team_id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  home_away?: string;
  notes?: string;
  match_result_url?: string;
  match_completed: boolean;
  created_at: string;
}

interface MatchFormData {
  opponent_name: string;
  match_date: string;
  match_time: string;
  location: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  home_away: string;
  notes: string;
}


interface AvailabilityData {
  team_id: number;
  team_name: string;
  members: Array<{
    id: number;
    name: string;
    display_name?: string;
    email: string;
    is_captain: boolean;
    gender?: 'male' | 'female' | null;
    ntrp_rating?: number | null;
    dynamic_rating?: number | null;
  }>;
  matches: Array<{
    id: number;
    opponent_name: string;
    match_date: string;
    location?: string;
    home_away?: string;
  }>;
  availability: {
    [playerId: number]: {
      [matchId: number]: {
        status: string;
        notes?: string;
        updated_at: string;
      };
    };
  };
}

interface StrategizeMember {
  id: number;
  name: string;
  display_name?: string;
  email: string;
  gender?: 'male' | 'female' | null;
  is_captain: boolean;
  ntrp_rating?: number | null;
  dynamic_rating?: number | null;
  lineup_type_preferences?: string[] | null;
}

interface LineupDifficulty {
  team_avg_rating: number;
  opponent_avg_rating: number;
  rating_diff: number;
  category: 'much-easier' | 'easier' | 'even' | 'harder' | 'much-harder';
}

interface StrategizeMatch {
  id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  home_away?: string;
  notes?: string;
  match_result_url?: string;
  match_completed: boolean;
  lineup_published?: boolean;
  /** Aggregate per-match difficulty computed from actual_result lineups whose
   *  opponent ratings resolved. Null when no contributing lineups exist. */
  actual_difficulty?: LineupDifficulty | null;
}

interface LineupPositionFormat {
  position_number: number;
  match_type: string;
}

interface LineupFormatData {
  id: number;
  total_lines: number;
  max_sets_per_line: number;
  allow_multi_line_selection: boolean;
  positions: LineupPositionFormat[];
}

interface LineupConfirmation {
  player_id: number;
  confirmed_at: string;
  confirmed_by: number;
}

interface StrategizeData {
  team_id: number;
  team_name: string;
  members: StrategizeMember[];
  matches: StrategizeMatch[];
  availability: {
    [playerId: number]: {
      [matchId: number]: {
        status: string;
        notes?: string;
        updated_at: string;
      };
    };
  };
  lineups: {
    [matchId: number]: Array<{
      id: number;
      position_number: number;
      player1_id: number;
      player2_id?: number | null;
      score?: string;
      won?: boolean;
      is_actual_result: boolean;
      confirmations?: LineupConfirmation[];
      difficulty?: LineupDifficulty | null;
    }>;
  };
  lineup_format: LineupFormatData | null;
}

interface LineupEdit {
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  score?: string | null;
  won?: boolean | null;
  is_actual_result?: boolean;
  confirmations?: LineupConfirmation[];
  difficulty?: LineupDifficulty | null;
}

interface ManualLineResult {
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  opponent_player1_name?: string | null;
  opponent_player2_name?: string | null;
  score?: string | null;
  won?: boolean | null;
}

interface MatchResultsFormData {
  line_results: ManualLineResult[];
}

interface PlayerWithRating {
  id: number;
  name: string;
  display_name?: string;
  email: string;
  ntrp_rating?: number | null;
  dynamic_rating?: number | null;
  current_ntrp_rating?: number | null;
  pending_ntrp_update?: boolean;
}

interface CompetitiveIntelligence {
  team_id: number;
  team_name: string;
  tennisrecord_url?: string | null;
  last_updated?: string | null;
  team_players: PlayerWithRating[];
  opponent_teams: any[];
}

const TeamManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedTeamId, teams, loading: teamLoading, refreshTeams, setSelectedTeamId, showTeamSelector, setShowTeamSelector } = useTeam();
  const posthog = usePostHog();
  const isTennisRecordEnabled = useFeatureFlag('tennisrecord-enabled', false);
  const availabilityDisplayVariant = useFeatureFlagVariant('strategize-tab-availability-display', 'control');
  const isLineupEmailEnabled = useFeatureFlag('send-lineup-notification-emails', false);
  const toast = useToast();
  const navigate = useNavigate();
  
  // URL parameter reading
  const { tab: urlTab } = useParams<{ tab: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Extract tab from pathname as fallback if urlTab doesn't update
  const pathnameTab = location.pathname.split('/')[2]; // /teams/:tab -> get tab
  
  // Helper function to get stored tab (global across all teams)
  const getStoredTab = (): 'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics' => {
    const stored = localStorage.getItem('teamManagementActiveTab');
    // Backward compatibility: migrate old keys to new ones
    if (stored === 'scheduling') {
      localStorage.setItem('teamManagementActiveTab', 'strategize');
      return 'strategize';
    }
    if (stored === 'matches') {
      localStorage.setItem('teamManagementActiveTab', 'teamSchedule');
      return 'teamSchedule';
    }
    if (stored && ['members', 'teamSchedule', 'availability', 'strategize', 'configuration', 'analytics'].includes(stored)) {
      return stored as 'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics';
    }
    return 'strategize';
  };
  
  // Helper functions to map between URL tab names and internal tab names
  const urlTabToInternalTab = (urlTab: string): 'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics' | null => {
    // Map 'schedule' in URL to 'teamSchedule' internally
    if (urlTab === 'schedule') return 'teamSchedule';
    if (['members', 'teamSchedule', 'availability', 'strategize', 'configuration', 'analytics'].includes(urlTab)) {
      return urlTab as 'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics';
    }
    return null;
  };
  
  const internalTabToUrlTab = (internalTab: 'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics'): string => {
    // Map 'teamSchedule' internally to 'schedule' in URL
    if (internalTab === 'teamSchedule') return 'schedule';
    return internalTab;
  };
  
  // Read tab from URL path, fall back to localStorage
  const getInitialTab = (): 'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics' => {
    if (urlTab) {
      const mappedTab = urlTabToInternalTab(urlTab);
      if (mappedTab) {
        return mappedTab;
      }
    }
    return getStoredTab();
  };
  
  
  // Read strategize match filter from URL query params
  const getInitialMatchFilter = (): 'all' | 'upcoming' | 'past' => {
    // Only read if we're on strategize tab (to avoid conflicts with availability tab)
    const currentTab = getInitialTab();
    if (currentTab !== 'strategize') {
      return 'all';
    }
    // On mobile, default to 'upcoming' only
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      const urlMatchFilter = searchParams.get('matchFilter');
      if (urlMatchFilter && ['all', 'upcoming', 'past'].includes(urlMatchFilter)) {
        return urlMatchFilter as 'all' | 'upcoming' | 'past';
      }
      return 'upcoming';
    }
    const urlMatchFilter = searchParams.get('matchFilter');
    if (urlMatchFilter && ['all', 'upcoming', 'past'].includes(urlMatchFilter)) {
      return urlMatchFilter as 'all' | 'upcoming' | 'past';
    }
    return 'all';
  };
  
  const getInitialShowStats = (): boolean => {
    // Stats are hidden by default
    return false;
  };
  
  // Read availability match filter from URL query params (uses same param name as strategize)
  const getInitialAvailabilityMatchFilter = (): 'all' | 'upcoming' | 'past' => {
    // Only read from URL if we're on availability tab (to avoid conflicts with strategize)
    const currentTab = getInitialTab();
    if (currentTab !== 'availability') {
      return 'all';
    }
    const urlMatchFilter = searchParams.get('matchFilter');
    if (urlMatchFilter && ['all', 'upcoming', 'past'].includes(urlMatchFilter)) {
      return urlMatchFilter as 'all' | 'upcoming' | 'past';
    }
    return 'all';
  };
  
  // Read schedule match filter from URL query params (uses same param name as strategize and availability)
  const getInitialScheduleMatchFilter = (): 'all' | 'upcoming' | 'past' => {
    // Only read from URL if we're on schedule tab (to avoid conflicts with other tabs)
    const currentTab = getInitialTab();
    if (currentTab !== 'teamSchedule') {
      return 'all';
    }
    const urlMatchFilter = searchParams.get('matchFilter');
    if (urlMatchFilter && ['all', 'upcoming', 'past'].includes(urlMatchFilter)) {
      return urlMatchFilter as 'all' | 'upcoming' | 'past';
    }
    return 'all';
  };
  
  // State management
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<Array<{
    id: number;
    invitee_id: number;
    inviter_id: number;
    status: string;
    invited_at: string;
    expires_at: string;
  }>>([]);
  const [userPendingInvitationsCount, setUserPendingInvitationsCount] = useState(0);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize tab state from URL path, fall back to localStorage
  const [activeTab, setActiveTab] = useState<'members' | 'teamSchedule' | 'availability' | 'strategize' | 'configuration' | 'analytics'>(() => getInitialTab());
  
  // Initialize matchFilter from URL, default to 'all' (must be before useEffect that uses it)
  const [matchFilter, setMatchFilter] = useState<'all' | 'upcoming' | 'past'>(() => getInitialMatchFilter());
  
  // Initialize availability match filter from URL, default to 'all'
  const [availabilityMatchFilter, setAvailabilityMatchFilter] = useState<'all' | 'upcoming' | 'past'>(() => getInitialAvailabilityMatchFilter());
  
  // Initialize schedule match filter from URL, default to 'all'
  const [scheduleMatchFilter, setScheduleMatchFilter] = useState<'all' | 'upcoming' | 'past'>(() => getInitialScheduleMatchFilter());
  
  // Initialize isStatsPanelOpen from URL, default to false (must be before useEffect that uses it)
  const [isStatsPanelOpen, setIsStatsPanelOpen] = useState<boolean>(() => getInitialShowStats());
  
  
  // Persist tab changes to localStorage (global, not per-team)
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('teamManagementActiveTab', activeTab);
    }
  }, [activeTab]);
  
  // Sync activeTab to URL path - IMMEDIATELY
  // Use a ref to prevent reading useEffect from interfering when we programmatically update
  const isUpdatingUrlRef = useRef(false);
  
  useEffect(() => {
    // Use pathnameTab as fallback if urlTab is stale
    const currentUrlTab = urlTab || pathnameTab;
    // Map URL tab to internal tab for comparison
    const currentInternalTab = currentUrlTab ? urlTabToInternalTab(currentUrlTab) : null;
    
    // Only update URL if activeTab is different from current URL tab
    // This prevents infinite loops while ensuring URL updates when tab changes
    if (activeTab && activeTab !== currentInternalTab) {
      isUpdatingUrlRef.current = true;
      
      // Build query params with current values
      const currentParams = new URLSearchParams();
      
      // Only include strategize params when on strategize tab
      if (activeTab === 'strategize') {
        currentParams.set('matchFilter', matchFilter);
        currentParams.set('showStats', isStatsPanelOpen.toString());
      }
      
      // Build the full path with both path and query params
      // Map internal tab name to URL tab name (teamSchedule -> schedule)
      const urlTabName = internalTabToUrlTab(activeTab);
      const queryString = currentParams.toString();
      const newPath = `/teams/${urlTabName}${queryString ? `?${queryString}` : ''}`;
      
      // Force navigation - use replace to avoid history entries
      navigate(newPath, { replace: true });
      
      // Reset flag after navigation completes
      setTimeout(() => {
        isUpdatingUrlRef.current = false;
      }, 150);
    }
  }, [activeTab, urlTab, pathnameTab, matchFilter, isStatsPanelOpen, navigate]); // Include all dependencies needed for query params
  
  // Sync selectedTeamId, matchFilter, and isStatsPanelOpen to URL query params - IMMEDIATELY
  // But skip if we're updating the path (to avoid conflicts)
  useEffect(() => {
    // Skip if we're in the middle of updating the path
    if (isUpdatingUrlRef.current) {
      return;
    }
    
    const params = new URLSearchParams(searchParams);
    
    // Only include strategize params when on strategize tab
    if (activeTab === 'strategize') {
      params.set('matchFilter', matchFilter);
      // Only include showStats param when it's true, remove it when false
      if (isStatsPanelOpen) {
        params.set('showStats', 'true');
      } else {
        params.delete('showStats');
      }
    } else if (activeTab === 'availability') {
      // Include availability match filter when on availability tab (uses same param name)
      params.set('matchFilter', availabilityMatchFilter);
      // Remove strategize-specific params
      params.delete('showStats');
    } else if (activeTab === 'teamSchedule') {
      // Include schedule match filter when on schedule tab (uses same param name)
      params.set('matchFilter', scheduleMatchFilter);
      // Remove strategize-specific params
      params.delete('showStats');
    } else {
      // Remove all tab-specific params when not on those tabs
      params.delete('matchFilter');
      params.delete('showStats');
    }
    
    // Preserve other existing params
    searchParams.forEach((value, key) => {
      if (key !== 'matchFilter' && key !== 'showStats') {
        params.set(key, value);
      }
    });
    
    // Update immediately - no debouncing or delays
    setSearchParams(params, { replace: true });
  }, [matchFilter, isStatsPanelOpen, availabilityMatchFilter, scheduleMatchFilter, activeTab, searchParams, setSearchParams]);
  
  
  // Read URL params and update state when URL changes (e.g., browser back/forward)
  // This should only run when URL actually changes, not when we programmatically update it
  useEffect(() => {
    // Skip if we're programmatically updating the URL
    if (isUpdatingUrlRef.current) {
      return;
    }
    
    // Update activeTab from URL path (only if different to avoid loops)
    if (urlTab) {
      const mappedTab = urlTabToInternalTab(urlTab);
      if (mappedTab && mappedTab !== activeTab) {
        setActiveTab(mappedTab);
      }
    }
    
    // Update matchFilter and isStatsPanelOpen from URL query params (only when on strategize tab)
    // Update availabilityMatchFilter from URL query params (only when on availability tab)
    const currentTab = urlTab ? (urlTabToInternalTab(urlTab) || activeTab) : activeTab;
    
    if (currentTab === 'strategize') {
      const urlMatchFilter = getInitialMatchFilter();
      if (urlMatchFilter !== matchFilter) {
        setMatchFilter(urlMatchFilter);
      }
      
      // Read showStats directly from URL params (only when on strategize tab)
      const urlShowStats = searchParams.get('showStats');
      const shouldShowStats = urlShowStats === 'true';
      if (shouldShowStats !== isStatsPanelOpen) {
        setIsStatsPanelOpen(shouldShowStats);
      }
    } else if (currentTab === 'availability') {
      // Read matchFilter from URL for availability tab (same param name as strategize)
      const urlMatchFilter = searchParams.get('matchFilter');
      if (urlMatchFilter && ['all', 'upcoming', 'past'].includes(urlMatchFilter)) {
        const urlAvailabilityMatchFilter = urlMatchFilter as 'all' | 'upcoming' | 'past';
        if (urlAvailabilityMatchFilter !== availabilityMatchFilter) {
          setAvailabilityMatchFilter(urlAvailabilityMatchFilter);
        }
      }
    } else if (currentTab === 'teamSchedule') {
      // Read matchFilter from URL for schedule tab (same param name as other tabs)
      const urlMatchFilter = searchParams.get('matchFilter');
      if (urlMatchFilter && ['all', 'upcoming', 'past'].includes(urlMatchFilter)) {
        const urlScheduleMatchFilter = urlMatchFilter as 'all' | 'upcoming' | 'past';
        if (urlScheduleMatchFilter !== scheduleMatchFilter) {
          setScheduleMatchFilter(urlScheduleMatchFilter);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab, searchParams.toString()]); // Use searchParams.toString() to detect changes, not the object itself
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [editingMatchId, setEditingMatchId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [availabilityData, setAvailabilityData] = useState<AvailabilityData | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [activeCell, setActiveCell] = useState<{ matchId: number; playerId: number } | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const [showImportReport, setShowImportReport] = useState(false);
  const [importReport, setImportReport] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Strategize tab state
  const [strategizeData, setStrategizeData] = useState<StrategizeData | null>(null);
  const [strategizeLoading, setStrategizeLoading] = useState(false);
  const [editedAvailability, setEditedAvailability] = useState<{ [key: string]: string }>({});
  const [editedLineups, setEditedLineups] = useState<{ [matchId: number]: LineupEdit[] }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [matchId: number]: ValidationError[] }>({});
  const [saving, setSaving] = useState(false);
  const [suggestingLineups, setSuggestingLineups] = useState(false);
  const [showSuggestLineupsModal, setShowSuggestLineupsModal] = useState(false);
  const [unfilledPositions, setUnfilledPositions] = useState<Set<string>>(new Set());
  const [activeStrategizeCell, setActiveStrategizeCell] = useState<{ matchId: number; playerId: number } | null>(null);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isAvailabilitySectionCollapsed, setIsAvailabilitySectionCollapsed] = useState(true);
  const [isLineupsSectionCollapsed, setIsLineupsSectionCollapsed] = useState(false);
  const [isAvailabilitySummaryCollapsed, setIsAvailabilitySummaryCollapsed] = useState(true);
  const [expandedPlayerId, setExpandedPlayerId] = useState<number | null>(null);
  const [showClearLineupsModal, setShowClearLineupsModal] = useState(false);
  const [showMobileStatsModal, setShowMobileStatsModal] = useState(false);
  const [mobileMatchCollapsed, setMobileMatchCollapsed] = useState<{ [matchId: number]: { competitiveIntel?: boolean; availability?: boolean; card?: boolean } }>({});
  const [showDeleteTeamModal, setShowDeleteTeamModal] = useState(false);
  const [showClearAvailabilityModal, setShowClearAvailabilityModal] = useState(false);
  const [pendingMatchUpdate, setPendingMatchUpdate] = useState<{ matchId: number; payload: any; availabilityCount: number } | null>(null);
  const [originalMatchData, setOriginalMatchData] = useState<{ [matchId: number]: MatchFormData }>({});
  const [showLineupTextModal, setShowLineupTextModal] = useState(false);
  const [lineupTextContent, setLineupTextContent] = useState('');
  
  // Mobile detection for desktop optimization warning
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [mobileWarningDismissed, setMobileWarningDismissed] = useState(() => {
    return sessionStorage.getItem('teamManagementMobileWarningDismissed') === 'true';
  });
  const mobileWarningEventSentRef = useRef(false);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [deleteConfirmationName, setDeleteConfirmationName] = useState('');
  
  
  // Configuration tab state
  const [tennisRecordUrl, setTennisRecordUrl] = useState('');
  const [isEditingIntelligence, setIsEditingIntelligence] = useState(false);
  const [tempTennisRecordUrl, setTempTennisRecordUrl] = useState('');
  const [fetchingIntelligence, setFetchingIntelligence] = useState(false);
  const [progressMessages, setProgressMessages] = useState<Array<{type: string, message: string, timestamp: Date}>>([]);
  const [eventSourceRef, setEventSourceRef] = useState<EventSource | null>(null);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [configFormData, setConfigFormData] = useState({
    name: '',
    league: '',
    section: '',
    district_area: '',
    flight_subflight: '',
    usta_team_id: '',
    league_start_date: '',
    league_end_date: '',
    use_first_match_for_start_date: true,
    use_last_match_for_end_date: true,
    timezone: '',
    max_combined_ntrp_rating: null as number | null,
    match_win_criteria: null as string | null
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configErrors, setConfigErrors] = useState<{ league_start_date?: string; league_end_date?: string }>({});
  
  // Tooltip state for info icons
  const [tooltipState, setTooltipState] = useState<{ text: string; x: number; y: number } | null>(null);
  
  // Lineup format edit state
  const [showLineupFormatWarning, setShowLineupFormatWarning] = useState(false);
  const [showLineupFormatEdit, setShowLineupFormatEdit] = useState(false);
  const [lineupFormatFormData, setLineupFormatFormData] = useState<{
    total_lines: number;
    max_sets_per_line: number;
    allow_multi_line_selection: boolean;
    positions: Array<{ position_number: number; match_type: string }>;
  } | null>(null);
  const [lineupFormatErrors, setLineupFormatErrors] = useState<Record<string, string>>({});
  const [savingLineupFormat, setSavingLineupFormat] = useState(false);
  const [lineupCount, setLineupCount] = useState<number | null>(null);
  
  // Access control state
  const [hasCaptainAccess, setHasCaptainAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);
  const [checkingCaptainStatus, setCheckingCaptainStatus] = useState(true);
  
  
  // Competitive Intelligence state
  const [competitiveIntel, setCompetitiveIntel] = useState<CompetitiveIntelligence | null>(null);
  const [pendingChanges, setPendingChanges] = useState<any | null>(null);
  const [fetchSessionId, setFetchSessionId] = useState<string | null>(null);
  const [showPendingChangesModal, setShowPendingChangesModal] = useState(false);
  const [hoveredOpponentCell, setHoveredOpponentCell] = useState<number | null>(null);
  const [matchTooltipPosition, setMatchTooltipPosition] = useState<{ top: number; left: number; match: StrategizeMatch } | null>(null);
  
  const [matchForm, setMatchForm] = useState<MatchFormData>({
    opponent_name: '',
    match_date: '',
    match_time: '12:00',
    location: '',
    formatted_address: undefined,
    latitude: undefined,
    longitude: undefined,
    home_away: 'home',
    notes: '',
  });

  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  // Member filter state - column-specific filters
  const [memberFilters, setMemberFilters] = useState({
    nameFilter: '',
    emailFilter: '',
    captainFilter: 'all' as 'all' | 'captain' | 'not_captain',
    genderFilter: 'all' as 'all' | 'male' | 'female' | 'not_set',
    ntrpFilter: [] as (number | 'not_set')[]
  });

  // Member sorting state
  const [memberSortColumn, setMemberSortColumn] = useState<string | null>('name');
  const [memberSortDirection, setMemberSortDirection] = useState<'asc' | 'desc'>('asc');

  // Handle member column sorting
  const handleMemberSort = (column: string) => {
    if (memberSortColumn === column) {
      // Toggle direction if same column
      setMemberSortDirection(memberSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setMemberSortColumn(column);
      setMemberSortDirection('asc');
    }
  };
  const [ntrpDropdownOpen, setNtrpDropdownOpen] = useState(false);
  const ntrpDropdownRef = useRef<HTMLDivElement | null>(null);
  const ntrpButtonRef = useRef<HTMLButtonElement | null>(null);
  const [ntrpDropdownPosition, setNtrpDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [mobileAvailabilityFiltersExpanded, setMobileAvailabilityFiltersExpanded] = useState(false);
  const [scheduleSearchQuery, setScheduleSearchQuery] = useState('');
  const [isScheduleSearchExpanded, setIsScheduleSearchExpanded] = useState(false);
  const scheduleSearchInputRef = useRef<HTMLInputElement>(null);
  const scheduleSearchContainerRef = useRef<HTMLDivElement>(null);
  // Member edit modal state
  const [editingMemberId, setEditingMemberId] = useState<number | null>(null);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [memberEditForm, setMemberEditForm] = useState<{
    email: string;
    gender: 'male' | 'female' | null;
    ntrp_rating: number | null;
    tennisrecord_name: string;
    lineup_type_preferences: string[];
  }>({
    email: '',
    gender: null,
    ntrp_rating: null,
    tennisrecord_name: '',
    lineup_type_preferences: []
  });
  const [showMergeConfirmationModal, setShowMergeConfirmationModal] = useState(false);
  const [mergeConfirmationData, setMergeConfirmationData] = useState<{
    memberId: number;
    userId: number;
    sourceUser: { id: number; name: string; email: string | null; team_memberships_count?: number };
    targetUser: { id: number; name: string; email: string; team_memberships_count?: number };
    message: string;
  } | null>(null);

  // Match Results Entry state
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    matchId: number;
    lineupId: number;
    playerId: number;
    playerName: string;
    isConfirming: boolean;
  } | null>(null);
  const [publishDialogMatchId, setPublishDialogMatchId] = useState<number | null>(null);
  const [publishDialogIsResend, setPublishDialogIsResend] = useState(false);
  const [selectedMatchForResults, setSelectedMatchForResults] = useState<Match | null>(null);
  const [resultsFormData, setResultsFormData] = useState<MatchResultsFormData>({
    line_results: []
  });
  const [savingResults, setSavingResults] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);

  // Fetch selected team details when selectedTeamId changes
  useEffect(() => {
    const fetchTeamDetails = async () => {
      if (!selectedTeamId) {
        setSelectedTeam(null);
        setLoading(false);
        setAccessLoading(false);
        setHasCaptainAccess(false);
        return;
      }

      try {
        setLoading(true);
        setAccessLoading(true);
        
        // Check captain access first
        const accessResponse = await axios.get(`/teams/${selectedTeamId}/is-captain`);
        const isCaptain = accessResponse.data.is_captain || accessResponse.data.is_admin;
        setHasCaptainAccess(isCaptain);
        
        // If user doesn't have captain access, clear the selected team from localStorage
        if (!isCaptain && selectedTeamId) {
          localStorage.removeItem('selectedTeamId');
          setSelectedTeam(null);
          setLoading(false);
          setAccessLoading(false);
          // Clear selectedTeamId to prevent loop - this will trigger TeamContext to update
          // Note: We can't set to null directly, so we'll let refreshTeams() handle it
          // Try to find another team the user has access to
          // If found, select it; otherwise, refresh teams to clear selection
          if (teams.length > 0) {
            // Check teams in parallel to find one with access
            const accessChecks = await Promise.all(
              teams
                .filter(t => t.id !== selectedTeamId)
                .map(async (team) => {
                  try {
                    const resp = await axios.get(`/teams/${team.id}/is-captain`);
                    return { team, hasAccess: resp.data.is_captain || resp.data.is_admin };
                  } catch {
                    return { team, hasAccess: false };
                  }
                })
            );
            const accessibleTeam = accessChecks.find(check => check.hasAccess);
            if (accessibleTeam) {
              setSelectedTeamId(accessibleTeam.team.id);
              return;
            }
          }
          // No accessible teams found - refresh teams to clear selection
          // This will cause TeamContext to clear selectedTeamId since it's not in localStorage
          await refreshTeams();
          return;
        }
        
        const [teamResponse, invitationsResponse, userInvitationsResponse] = await Promise.all([
          axios.get<Team>(`/teams/${selectedTeamId}`),
          axios.get(`/invitations/teams/${selectedTeamId}/invitations`, {
            params: { status: 'pending' }
          }),
          axios.get('/invitations/invitations/me').catch(() => ({ data: [] }))
        ]);
        setSelectedTeam(teamResponse.data);
        setPendingInvitations(invitationsResponse.data || []);
        const pendingUserInvitations = (userInvitationsResponse.data || []).filter((inv: any) => inv.status === 'pending');
        setUserPendingInvitationsCount(pendingUserInvitations.length);
      } catch (error) {
        console.error('Failed to fetch team details:', error);
        toast.error('Failed to load team details. Please try again.');
      } finally {
        setLoading(false);
        setAccessLoading(false);
      }
    };

    if (!teamLoading) {
      fetchTeamDetails();
    }
    // Note: We intentionally exclude 'teams' from dependencies to prevent infinite loop
    // when refreshTeams() updates teams after access loss
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId, teamLoading]);

  // Show team selector when on this page and no team is selected
  useEffect(() => {
    // Check if URL has showTeamSelector parameter (for pending invitations)
    const shouldForceOpen = searchParams.get('showTeamSelector') === 'true';
    
    // Remove the query parameter if present (clean up URL)
    if (shouldForceOpen) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('showTeamSelector');
      setSearchParams(newParams, { replace: true });
    }
    
    // Show modal if:
    // 1. Teams are loaded
    // 2. There are teams
    // 3. Either no team is selected OR we're forcing it open (for pending invitations)
    if (!teamLoading && teams.length > 0 && (!selectedTeamId || shouldForceOpen)) {
      setShowTeamSelector(true);
    } else if (!shouldForceOpen) {
      // Only hide modal if we're not forcing it open
      setShowTeamSelector(false);
    }
  }, [teamLoading, teams.length, selectedTeamId, setShowTeamSelector, searchParams, setSearchParams]);

  // Keyboard shortcut: Cmd+K (Mac) or Ctrl+K (Windows/Linux) to toggle team selector
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isModKey = isMac ? event.metaKey : event.ctrlKey;
      
      if (isModKey && event.key === 'k') {
        // Don't trigger if user is typing in an input, textarea, or contenteditable element
        const target = event.target as HTMLElement;
        const isInputElement = 
          target.tagName === 'INPUT' || 
          target.tagName === 'TEXTAREA' || 
          target.isContentEditable;
        
        if (!isInputElement) {
          event.preventDefault();
          setShowTeamSelector(!showTeamSelector);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTeamSelector, setShowTeamSelector]);

  // Check if user is a captain of any team (for loading state)
  useEffect(() => {
    const checkCaptainStatus = async () => {
      if (!user || teamLoading) {
        setCheckingCaptainStatus(false);
        return;
      }

      if (user.role === 'ADMIN') {
        // Admins are captains of all teams
        setCheckingCaptainStatus(false);
        return;
      }

      // Check if user is captain of any team
      try {
        if (teams.length === 0) {
          setCheckingCaptainStatus(false);
          return;
        }

        await Promise.all(
          teams.map(async (team) => {
            try {
              await axios.get(`/teams/${team.id}/is-captain`);
            } catch (error: any) {
              // If it's a 403, user is not a captain (or not a member)
              // If it's any other error, log it
              if (error.response?.status !== 403) {
                console.error(`Error checking captain status for team ${team.id}:`, error);
              }
            }
          })
        );
      } catch (error) {
        console.error('Failed to check captain status:', error);
      } finally {
        setCheckingCaptainStatus(false);
      }
    };

    checkCaptainStatus();
  }, [user, teams, teamLoading]);

  // Fetch matches when selected team changes
  useEffect(() => {
    if (selectedTeam) {
      fetchMatches(selectedTeam.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam?.id]);

  const fetchMatches = async (teamId: number) => {
    try {
      const response = await axios.get<Match[]>(`/matches/teams/${teamId}/matches`);
      const matchesData = response.data;
      
      // Sort matches by date (upcoming first)
      matchesData.sort((a, b) => 
        new Date(a.match_date).getTime() - new Date(b.match_date).getTime()
      );
      
      setMatches(matchesData);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches.');
    }
  };

  const fetchAvailability = async (teamId: number) => {
    setAvailabilityLoading(true);
    try {
      const response = await axios.get<AvailabilityData>(`/teams/${teamId}/availability`);
      setAvailabilityData(response.data);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      toast.error('Failed to load availability data.');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Helper function to calculate availability summary for a match
  const getMatchAvailabilitySummary = useCallback((matchId: number, members: AvailabilityData['members'], availability: AvailabilityData['availability']) => {
    const summary: {
      [status: string]: {
        [gender: string]: {
          [ntrp: string]: number;
        };
      };
    } = {
      available: { male: {}, female: {}, unspecified: {} },
      maybe: { male: {}, female: {}, unspecified: {} },
      unavailable: { male: {}, female: {}, unspecified: {} },
      not_set: { male: {}, female: {}, unspecified: {} }
    };

    members.forEach(member => {
      const status = availability[member.id]?.[matchId]?.status || 'not_set';
      const gender = member.gender === 'male' ? 'male' : member.gender === 'female' ? 'female' : 'unspecified';
      const ntrp = member.ntrp_rating != null ? member.ntrp_rating.toFixed(1) : 'no_rating';
      
      if (!summary[status][gender][ntrp]) {
        summary[status][gender][ntrp] = 0;
      }
      summary[status][gender][ntrp]++;
    });

    return summary;
  }, []);

  // Fetch availability when tab changes to availability
  useEffect(() => {
    if (activeTab === 'availability' && selectedTeam) {
      fetchAvailability(selectedTeam.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTeam?.id]);

  // Fetch strategize data when tab changes to strategize
  useEffect(() => {
    if (activeTab === 'strategize' && selectedTeam) {
      fetchStrategizeData(selectedTeam.id);
      // Also fetch competitive intelligence
      fetchCompetitiveIntelligence(selectedTeam.id);
      
      // Always hide stats panel by default
      setIsStatsPanelOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTeam?.id]);

  // Initialize TennisRecord URL when team is selected
  useEffect(() => {
    if (selectedTeam) {
      setTennisRecordUrl(selectedTeam.tennisrecord_url || '');
      setIsEditingIntelligence(false);
    }
  }, [selectedTeam]);

  // Cleanup EventSource on unmount or when switching teams/tabs
  useEffect(() => {
    return () => {
      if (eventSourceRef) {
        eventSourceRef.close();
        setEventSourceRef(null);
      }
    };
  }, [selectedTeamId, activeTab, eventSourceRef]);

  // Detect mobile device for desktop optimization warning
  useEffect(() => {
    const checkMobile = () => {
      // Check if device is mobile (tablets and phones)
      const isMobile = window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobileDevice(isMobile);
    };

    // Check on mount
    checkMobile();

    // Check on window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track PostHog event when mobile warning modal is shown
  useEffect(() => {
    if (isMobileDevice && !mobileWarningDismissed && !mobileWarningEventSentRef.current && posthog) {
      posthog.capture('team_management_mobile_access', {
        team_id: selectedTeamId || null,
        user_agent: navigator.userAgent,
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
      });
      mobileWarningEventSentRef.current = true;
    }
  }, [isMobileDevice, mobileWarningDismissed, posthog, selectedTeamId]);

  // Auto-scroll progress messages to bottom in modal
  useEffect(() => {
    if (progressMessages.length > 0 && fetchingIntelligence) {
      // Use requestAnimationFrame to ensure DOM has fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const progressContainer = document.getElementById('modal-progress-container');
          if (progressContainer) {
            progressContainer.scrollTop = progressContainer.scrollHeight;
          }
        });
      });
    }
  }, [progressMessages, fetchingIntelligence]);

  // Handle team creation - navigate to wizard
  const handlePublishLineup = async (matchId: number, sendEmail: boolean, emailNote: string | null, recipientType: string, recipientIds?: number[]) => {
    if (hasUnsavedChanges) {
      toast.error('Please save all changes before publishing the lineup');
      throw new Error('Unsaved changes');
    }
    
    try {
      const requestBody: any = {
        send_email: sendEmail,
        email_note: emailNote,
        recipient_type: recipientType
      };
      
      if (recipientType === 'specific_people' && recipientIds) {
        requestBody.recipient_ids = recipientIds;
      }
      
      await axios.put(`/lineups/matches/${matchId}/publish`, requestBody);
      
      if (selectedTeam) {
        await fetchStrategizeData(selectedTeam.id);
      }
      
      toast.success(sendEmail ? 'Lineup published and email sent successfully!' : 'Lineup published successfully!');
    } catch (error: any) {
      console.error('Failed to publish lineup:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to publish lineup. Please try again.';
      toast.error(errorMsg);
      throw error;
    }
  };

  const handleResendLineupEmail = async (matchId: number, emailNote: string | null, recipientType: string, recipientIds?: number[]) => {
    try {
      const requestBody: any = {
        email_note: emailNote,
        recipient_type: recipientType
      };
      
      if (recipientType === 'specific_people' && recipientIds) {
        requestBody.recipient_ids = recipientIds;
      }
      
      await axios.post(`/lineups/matches/${matchId}/lineup/resend-email`, requestBody);
      
      toast.success('Email sent successfully!');
    } catch (error: any) {
      console.error('Failed to resend email:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email. Please try again.';
      toast.error(errorMsg);
      throw error;
    }
  };

  const handleCopyLineupText = async (matchId: number) => {
    try {
      const response = await axios.get(`/lineups/matches/${matchId}/lineup/formatted-text`);
      const text = response.data.formatted_text;
      
      // Show modal with copy button for consistent experience across all platforms
      setLineupTextContent(text);
      setShowLineupTextModal(true);
    } catch (error: any) {
      console.error('Failed to copy lineup text:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to fetch lineup text. Please try again.';
      toast.error(errorMsg);
    }
  };

  const handleDismissMobileWarning = () => {
    setMobileWarningDismissed(true);
    sessionStorage.setItem('teamManagementMobileWarningDismissed', 'true');
  };

  const handleCreateTeam = () => {
    navigate('/teams/create-wizard');
  };


  // Helper function to check if tab switching is allowed
  const canSwitchTab = (newTab: string): boolean => {
    // Prevent tab switching while fetching competitive intelligence (modal will block UI)
    if (fetchingIntelligence) {
      return false;
    }
    
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return false;
      }
      setHasUnsavedChanges(false);
    }
    
    return true;
  };

  const fetchStrategizeData = async (teamId: number) => {
    setStrategizeLoading(true);
    try {
      const response = await axios.get<StrategizeData>(`/teams/${teamId}/scheduling`);
      console.log('Strategize data received:', response.data);
      console.log('Number of matches:', response.data.matches?.length || 0);
      console.log('Number of members:', response.data.members?.length || 0);
      setStrategizeData(response.data);
      // Clear any previous edits when reloading
      setEditedAvailability({});
      setEditedLineups({});
      setHasUnsavedChanges(false);
      setValidationErrors({});
    } catch (error) {
      console.error('Failed to fetch strategize data:', error);
      console.error('Error details:', error);
      toast.error('Failed to load strategize data.');
    } finally {
      setStrategizeLoading(false);
    }
  };

  const handleEditIntelligence = () => {
    setTempTennisRecordUrl(tennisRecordUrl);
    setIsEditingIntelligence(true);
  };

  const handleCancelIntelligenceEdit = () => {
    setTennisRecordUrl(tempTennisRecordUrl);
    setIsEditingIntelligence(false);
  };

  const handleTennisRecordUrlUpdate = async () => {
    if (!selectedTeam) return;
    
    try {
      await axios.put(`/teams/${selectedTeam.id}/tennisrecord-url`, {
        tennisrecord_url: tennisRecordUrl
      });
      toast.success('TennisRecord URL saved successfully');
      setIsEditingIntelligence(false);
      // Refresh team data
      const response = await axios.get(`/teams/${selectedTeam.id}`);
      setSelectedTeam(response.data);
    } catch (error: any) {
      console.error('Failed to update TennisRecord URL:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save TennisRecord URL';
      toast.error(errorMessage);
    }
  };

  const handleEditConfig = () => {
    if (!selectedTeam) return;
    
    setConfigFormData({
      name: selectedTeam.name,
      league: selectedTeam.league || '',
      section: selectedTeam.section || '',
      district_area: selectedTeam.district_area || '',
      flight_subflight: selectedTeam.flight_subflight || '',
      usta_team_id: selectedTeam.usta_team_id || '',
      league_start_date: selectedTeam.league_start_date ? selectedTeam.league_start_date.split('T')[0] : '',
      league_end_date: selectedTeam.league_end_date ? selectedTeam.league_end_date.split('T')[0] : '',
      use_first_match_for_start_date: selectedTeam.use_first_match_for_start_date ?? false,
      use_last_match_for_end_date: selectedTeam.use_last_match_for_end_date ?? false,
      timezone: selectedTeam.timezone || 'America/Los_Angeles',
      max_combined_ntrp_rating: selectedTeam.max_combined_ntrp_rating || null,
      match_win_criteria: selectedTeam.match_win_criteria || null
    });
    setIsEditingConfig(true);
  };

  const handleCancelConfigEdit = () => {
    setIsEditingConfig(false);
    setConfigErrors({}); // Clear errors when cancelling
  };

  // Calculate dates from matches when flags are true
  useEffect(() => {
    if (!isEditingConfig || !selectedTeam) return;

    if (configFormData.use_first_match_for_start_date && matches.length > 0) {
      const calculatedDate = calculateFirstMatchDate(matches as LeagueDateMatch[]);
      setConfigFormData(prev => {
        if (prev.league_start_date !== calculatedDate) {
          return { ...prev, league_start_date: calculatedDate };
        }
        return prev;
      });
    }

    if (configFormData.use_last_match_for_end_date && matches.length > 0) {
      const calculatedDate = calculateLastMatchDate(matches as LeagueDateMatch[]);
      setConfigFormData(prev => {
        if (prev.league_end_date !== calculatedDate) {
          return { ...prev, league_end_date: calculatedDate };
        }
        return prev;
      });
    }
  }, [matches, configFormData.use_first_match_for_start_date, configFormData.use_last_match_for_end_date, isEditingConfig, selectedTeam]);

  const validateConfigDates = (): boolean => {
    const newErrors: { league_start_date?: string; league_end_date?: string } = {};
    
    // Get effective start date (calculated or manual)
    let effectiveStartDate: string | null = null;
    if (configFormData.use_first_match_for_start_date && matches.length > 0) {
      effectiveStartDate = calculateFirstMatchDate(matches as LeagueDateMatch[]);
    } else if (!configFormData.use_first_match_for_start_date) {
      effectiveStartDate = configFormData.league_start_date || null;
    }
    
    // Get effective end date (calculated or manual)
    let effectiveEndDate: string | null = null;
    if (configFormData.use_last_match_for_end_date && matches.length > 0) {
      effectiveEndDate = calculateLastMatchDate(matches as LeagueDateMatch[]);
    } else if (!configFormData.use_last_match_for_end_date) {
      effectiveEndDate = configFormData.league_end_date || null;
    }
    
    // Validate that end date is after start date when both are provided
    if (effectiveStartDate && effectiveEndDate) {
      const startDate = new Date(effectiveStartDate);
      const endDate = new Date(effectiveEndDate);
      if (endDate <= startDate) {
        newErrors.league_end_date = 'End date must be after start date';
        // Also set start date error if it's manually set
        if (!configFormData.use_first_match_for_start_date) {
          newErrors.league_start_date = 'Start date must be before end date';
        }
      }
    }
    
    setConfigErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveConfig = async () => {
    if (!selectedTeam) return;
    
    // Validate dates before saving
    if (!validateConfigDates()) {
      toast.error('Please fix the date validation errors before saving');
      return;
    }
    
    setSavingConfig(true);
    try {
      // Use the response from PUT to update state directly (optimistic update)
      const response = await axios.put(`/teams/${selectedTeam.id}`, {
        name: configFormData.name,
        league: configFormData.league || null,
        section: configFormData.section || null,
        district_area: configFormData.district_area || null,
        flight_subflight: configFormData.flight_subflight || null,
        usta_team_id: configFormData.usta_team_id || null,
        league_start_date: configFormData.league_start_date ? `${configFormData.league_start_date}T00:00:00` : null,
        league_end_date: configFormData.league_end_date ? `${configFormData.league_end_date}T00:00:00` : null,
        use_first_match_for_start_date: configFormData.use_first_match_for_start_date,
        use_last_match_for_end_date: configFormData.use_last_match_for_end_date,
        timezone: configFormData.timezone,
        max_combined_ntrp_rating: configFormData.max_combined_ntrp_rating || null,
        match_win_criteria: configFormData.match_win_criteria || null
      });
      
      // Update selectedTeam state with response data
      setSelectedTeam(response.data);
      
      // Refresh teams list to ensure consistency
      refreshTeams();
      
      toast.success('Team configuration updated successfully');
      setIsEditingConfig(false);
      setConfigErrors({}); // Clear errors on success
    } catch (error: any) {
      console.error('Failed to update team configuration:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update team configuration';
      toast.error(errorMessage);
      // On error, refetch to ensure we have the correct state
      try {
        const response = await axios.get(`/teams/${selectedTeam.id}`);
        setSelectedTeam(response.data);
      } catch (refetchError) {
        console.error('Failed to refetch team data:', refetchError);
      }
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    
    // Verify confirmation name matches
    if (deleteConfirmationName !== selectedTeam.name) {
      toast.error('Team name does not match. Please type the team name exactly to confirm deletion.');
      return;
    }
    
    setDeletingTeam(true);
    try {
      const response = await axios.delete(`/teams/${selectedTeam.id}`);
      
      // Refresh teams list
      await refreshTeams();
      
      // If deleted team was selected, clear selection
      if (selectedTeam.id === selectedTeamId) {
        setSelectedTeamId(0);
        setShowTeamSelector(true);
      }
      
      // Close modal and reset confirmation
      setShowDeleteTeamModal(false);
      setDeleteConfirmationName('');
      
      // Show success message
      toast.success(response.data.message || 'Team deleted successfully');
    } catch (error: any) {
      console.error('Error deleting team:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete team';
      toast.error(errorMessage);
    } finally {
      setDeletingTeam(false);
    }
  };

  const handleFetchIntelligence = async () => {
    if (!selectedTeam) return;
    
    if (!tennisRecordUrl) {
      toast.error('Please enter a TennisRecord URL first');
      return;
    }
    
    // Close any existing EventSource
    if (eventSourceRef) {
      eventSourceRef.close();
      setEventSourceRef(null);
    }
    
    setFetchingIntelligence(true);
    setProgressMessages([]);
    
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setFetchingIntelligence(false);
        return;
      }
      
      // Create EventSource connection to SSE endpoint with token in URL
      const url = `${axios.defaults.baseURL}/teams/${selectedTeam.id}/fetch-intelligence-stream?token=${encodeURIComponent(token)}`;
      console.log('Creating EventSource to:', url.replace(/token=[^&]+/, 'token=***'));
      
      const eventSource = new EventSource(url);
      
      setEventSourceRef(eventSource);
      
      eventSource.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          setProgressMessages(prev => [...prev, {
            type: data.type,
            message: data.message,
            timestamp: new Date()
          }]);
          
          // If complete or error, close connection
          if (data.type === 'complete' || data.type === 'error') {
            eventSource.close();
            setEventSourceRef(null);
            
            if (data.type === 'error') {
              setFetchingIntelligence(false);
              // Show error message
              toast.error(data.message || 'Failed to fetch competitive intelligence');
              
              // Track error in PostHog
              posthog?.capture('competitive_intelligence_fetched', {
                team_id: selectedTeam.id,
                team_name: selectedTeam.name,
                error: data.error_type || 'unknown_error',
                error_message: data.message || 'Unknown error',
                success: false,
                user_id: user?.id,
                user_role: user?.role,
              });
            } else {
              // Fetch session ID from response and check for pending changes
              // Note: Session ID is nested in data.data.fetch_session_id
              const sessionId = data.data?.fetch_session_id;
              setFetchSessionId(sessionId);
              
              try {
                // Fetch competitive intelligence data for pending rating updates
                const intelResponse = await axios.get(`/teams/${selectedTeam.id}/competitive-intelligence`);
                const intelData = intelResponse.data;
                setCompetitiveIntel(intelData);
                
                // Check if there are pending rating updates
                const hasPendingRatingUpdates = intelData.team_players?.some((p: PlayerWithRating) => p.pending_ntrp_update);
                
                // Fetch pending changes (matches, users, lineups)
                let hasPendingChanges = false;
                if (sessionId) {
                  try {
                    const pendingResponse = await axios.get(`/teams/${selectedTeam.id}/pending-changes?fetch_session_id=${sessionId}`);
                    const pendingData = pendingResponse.data;
                    setPendingChanges(pendingData);
                    hasPendingChanges = pendingData.total_pending > 0;
                  } catch (err) {
                    console.error('Failed to fetch pending changes:', err);
                  }
                }
                
                // Turn off spinner now that we have all the data
                setFetchingIntelligence(false);
                
                if (hasPendingRatingUpdates || hasPendingChanges) {
                  // Keep modal open for user to review changes
                  // Show the pending changes modal if there are pending changes
                  if (hasPendingChanges) {
                    setShowPendingChangesModal(true);
                  }
                } else {
                  // No pending updates, close modal and refresh data
                  setFetchingIntelligence(false);
                  setCompetitiveIntel(null);
                  setProgressMessages([]);
                  setPendingChanges(null);
                  
                  // Refresh other data
                  fetchMatches(selectedTeam.id);
                  if (activeTab === 'strategize') {
                    fetchStrategizeData(selectedTeam.id);
                  }
                  
                  // Show success message
                  toast.success(data.message || 'Competitive intelligence updated successfully');
                  
                  // Track PostHog event from frontend (includes session ID automatically)
                  posthog?.capture('competitive_intelligence_fetched', {
                    team_id: selectedTeam.id,
                    team_name: selectedTeam.name,
                    updated_players: data.updated_players || 0,
                    created_users: data.created_users || 0,
                    added_team_members: data.added_team_members || 0,
                    updated_genders: data.gender_updated_count || 0,
                    updated_opponents: data.updated_opponents || 0,
                    created_matches: data.created_matches || 0,
                    pending_matches: data.pending_matches || 0,
                    pending_users: data.pending_users || 0,
                    pending_lineups: data.pending_lineups || 0,
                    matches_with_results: data.matches_with_results || 0,
                    lineups_created: data.lineups_created || 0,
                    lineups_updated: data.lineups_updated || 0,
                    user_id: user?.id,
                    user_role: user?.role,
                  });
                }
              } catch (error) {
                console.error('Failed to fetch competitive intelligence after completion:', error);
                setFetchingIntelligence(false);
              }
            }
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        console.error('EventSource readyState:', eventSource.readyState);
        eventSource.close();
        setEventSourceRef(null);
        setFetchingIntelligence(false);
        
        // Only show error toast if we don't already have a complete/error message
        const lastMessage = progressMessages[progressMessages.length - 1];
        if (!lastMessage || (lastMessage.type !== 'complete' && lastMessage.type !== 'error')) {
          toast.error('Connection error while fetching intelligence. Check console for details.');
        }
      };
      
    } catch (error: any) {
      console.error('Failed to start intelligence fetch:', error);
      toast.error('Failed to start competitive intelligence fetch');
      setFetchingIntelligence(false);
    }
  };

  const fetchCompetitiveIntelligence = async (teamId: number) => {
    try {
      const response = await axios.get(`/teams/${teamId}/competitive-intelligence`);
      setCompetitiveIntel(response.data);
    } catch (error) {
      console.error('Failed to fetch competitive intelligence:', error);
      // Silent fail - intelligence is optional
    }
  };

  const handleConfirmRatingUpdate = async (userId: number, action: 'apply' | 'ignore') => {
    if (!selectedTeam) return;

    try {
      const response = await axios.post(
        `/teams/${selectedTeam.id}/confirm-rating-update`,
        {
          user_id: userId,
          action: action
        }
      );

      toast.success(response.data.message || `Rating update ${action === 'apply' ? 'applied' : 'ignored'} successfully`);
      
      // Refresh competitive intelligence data to reflect the change
      await fetchCompetitiveIntelligence(selectedTeam.id);
      
      // Check if there are any remaining pending updates
      // If not, check for pending changes or close the modal
      const updatedIntel = await axios.get(`/teams/${selectedTeam.id}/competitive-intelligence`);
      const hasPendingRatingUpdates = updatedIntel.data.team_players?.some((p: PlayerWithRating) => p.pending_ntrp_update);
      
      if (!hasPendingRatingUpdates) {
        // All rating updates resolved
        setCompetitiveIntel(null);
        
        // Check if there are pending changes to review
        const hasPendingChanges = pendingChanges && pendingChanges.total_pending > 0;
        
        if (hasPendingChanges) {
          // Transition to pending changes modal
          setFetchingIntelligence(false);
          setProgressMessages([]);
          setShowPendingChangesModal(true);
        } else {
          // Nothing left to review, close modal and refresh data
          setFetchingIntelligence(false);
          setProgressMessages([]);
          fetchMatches(selectedTeam.id);
          if (activeTab === 'strategize') {
            fetchStrategizeData(selectedTeam.id);
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to confirm rating update:', error);
      toast.error(error.response?.data?.detail || `Failed to ${action === 'apply' ? 'apply' : 'ignore'} rating update`);
    }
  };

  // Handlers for pending changes confirmation
  const [selectedMatchIds, setSelectedMatchIds] = useState<Set<number>>(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectedLineupIds, setSelectedLineupIds] = useState<Set<number>>(new Set());

  const handleConfirmPendingMatch = async (pendingId: number, action: 'approve' | 'ignore') => {
    if (!selectedTeam) return;

    try {
      await axios.post(`/teams/${selectedTeam.id}/confirm-pending-changes`, {
        match_actions: [{ pending_id: pendingId, action }],
        user_actions: [],
        lineup_actions: []
      });

      toast.success(`Match ${action === 'approve' ? 'approved' : 'ignored'} successfully`);

      // Remove from pending list
      if (pendingChanges) {
        setPendingChanges({
          ...pendingChanges,
          matches: pendingChanges.matches.filter((m: any) => m.id !== pendingId),
          total_pending: pendingChanges.total_pending - 1
        });
      }
      
      // Remove from selection
      setSelectedMatchIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pendingId);
        return newSet;
      });
    } catch (error: any) {
      console.error('Failed to confirm match:', error);
      toast.error(error.response?.data?.detail || `Failed to ${action} match`);
    }
  };

  const handleConfirmPendingUser = async (pendingId: number, action: 'approve' | 'ignore') => {
    if (!selectedTeam) return;

    try {
      await axios.post(`/teams/${selectedTeam.id}/confirm-pending-changes`, {
        match_actions: [],
        user_actions: [{ pending_id: pendingId, action }],
        lineup_actions: []
      });

      toast.success(`Player ${action === 'approve' ? 'approved' : 'ignored'} successfully`);

      // Remove from pending list
      if (pendingChanges) {
        setPendingChanges({
          ...pendingChanges,
          users: pendingChanges.users.filter((u: any) => u.id !== pendingId),
          total_pending: pendingChanges.total_pending - 1
        });
      }
      
      // Remove from selection
      setSelectedUserIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pendingId);
        return newSet;
      });
    } catch (error: any) {
      console.error('Failed to confirm user:', error);
      toast.error(error.response?.data?.detail || `Failed to ${action} player`);
    }
  };

  const handleConfirmPendingLineup = async (pendingId: number, action: 'approve' | 'ignore') => {
    if (!selectedTeam) return;

    try {
      await axios.post(`/teams/${selectedTeam.id}/confirm-pending-changes`, {
        match_actions: [],
        user_actions: [],
        lineup_actions: [{ pending_id: pendingId, action }]
      });

      toast.success(`Lineup ${action === 'approve' ? 'approved' : 'ignored'} successfully`);

      // Remove from pending list
      if (pendingChanges) {
        setPendingChanges({
          ...pendingChanges,
          lineups: pendingChanges.lineups.filter((l: any) => l.id !== pendingId),
          total_pending: pendingChanges.total_pending - 1
        });
      }
      
      // Remove from selection
      setSelectedLineupIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pendingId);
        return newSet;
      });
    } catch (error: any) {
      console.error('Failed to confirm lineup:', error);
      toast.error(error.response?.data?.detail || `Failed to ${action} lineup`);
    }
  };

  const handleBulkConfirm = async () => {
    if (!selectedTeam) return;

    const matchActions = Array.from(selectedMatchIds).map(id => ({ pending_id: id, action: 'approve' as const }));
    const userActions = Array.from(selectedUserIds).map(id => ({ pending_id: id, action: 'approve' as const }));
    const lineupActions = Array.from(selectedLineupIds).map(id => ({ pending_id: id, action: 'approve' as const }));

    if (matchActions.length === 0 && userActions.length === 0 && lineupActions.length === 0) {
      toast.error('Please select items to approve');
      return;
    }

    try {
      const response = await axios.post(`/teams/${selectedTeam.id}/confirm-pending-changes`, {
        match_actions: matchActions,
        user_actions: userActions,
        lineup_actions: lineupActions
      });

      toast.success(response.data.message || 'Changes approved successfully');

      // Clear selections
      setSelectedMatchIds(new Set());
      setSelectedUserIds(new Set());
      setSelectedLineupIds(new Set());

      // Refresh pending changes
      if (fetchSessionId) {
        const pendingResponse = await axios.get(`/teams/${selectedTeam.id}/pending-changes?fetch_session_id=${fetchSessionId}`);
        setPendingChanges(pendingResponse.data);

        if (pendingResponse.data.total_pending === 0) {
          // All changes processed, close modal and refresh
          setShowPendingChangesModal(false);
          setPendingChanges(null);
          setFetchingIntelligence(false);
          setProgressMessages([]);
          fetchMatches(selectedTeam.id);
          if (activeTab === 'strategize') {
            fetchStrategizeData(selectedTeam.id);
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to bulk confirm:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve changes');
    }
  };

  const handleBulkIgnore = async () => {
    if (!selectedTeam) return;

    const matchActions = Array.from(selectedMatchIds).map(id => ({ pending_id: id, action: 'ignore' as const }));
    const userActions = Array.from(selectedUserIds).map(id => ({ pending_id: id, action: 'ignore' as const }));
    const lineupActions = Array.from(selectedLineupIds).map(id => ({ pending_id: id, action: 'ignore' as const }));

    if (matchActions.length === 0 && userActions.length === 0 && lineupActions.length === 0) {
      toast.error('Please select items to ignore');
      return;
    }

    try {
      const response = await axios.post(`/teams/${selectedTeam.id}/confirm-pending-changes`, {
        match_actions: matchActions,
        user_actions: userActions,
        lineup_actions: lineupActions
      });

      toast.success(response.data.message || 'Changes ignored successfully');

      // Clear selections
      setSelectedMatchIds(new Set());
      setSelectedUserIds(new Set());
      setSelectedLineupIds(new Set());

      // Refresh pending changes
      if (fetchSessionId) {
        const pendingResponse = await axios.get(`/teams/${selectedTeam.id}/pending-changes?fetch_session_id=${fetchSessionId}`);
        setPendingChanges(pendingResponse.data);

        if (pendingResponse.data.total_pending === 0) {
          // All changes processed, close modal and refresh
          setShowPendingChangesModal(false);
          setPendingChanges(null);
          setFetchingIntelligence(false);
          setProgressMessages([]);
          fetchMatches(selectedTeam.id);
          if (activeTab === 'strategize') {
            fetchStrategizeData(selectedTeam.id);
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to bulk ignore:', error);
      toast.error(error.response?.data?.detail || 'Failed to ignore changes');
    }
  };

  const handleAvailabilityCellClick = (matchId: number, playerId: number) => {
    // Toggle dropdown for the clicked cell
    if (activeCell?.matchId === matchId && activeCell?.playerId === playerId) {
      setActiveCell(null);
    } else {
      setActiveCell({ matchId, playerId });
    }
  };

  const handleAvailabilityChange = async (matchId: number, playerId: number, newStatus: string, event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedTeam || !availabilityData) return;
    
    // Prevent default and blur to prevent focus-related scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
    }
    
    setActiveCell(null); // Close dropdown
    
    // Store original value for potential rollback
    const originalStatus = availabilityData.availability[playerId]?.[matchId]?.status || null;
    
    // If clicking the same status, toggle it off (clear)
    if (originalStatus === newStatus) {
      await handleClearAvailability(matchId, playerId, event);
      return;
    }
    
    // Optimistically update the state immediately
    setAvailabilityData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [playerId]: {
            ...(prev.availability[playerId] || {}),
            [matchId]: {
              status: newStatus,
              updated_at: new Date().toISOString()
            }
          }
        }
      };
    });
    
    try {
      await axios.post(
        `/teams/${selectedTeam.id}/availability/${matchId}/${playerId}?availability_status=${newStatus}`
      );
      
      toast.success('Availability updated');
    } catch (error) {
      console.error('Failed to update availability:', error);
      
      // Revert optimistic update on error
      setAvailabilityData(prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          availability: {
            ...prev.availability,
            [playerId]: {
              ...prev.availability[playerId]
            }
          }
        };
        
        if (originalStatus === null) {
          // Remove the entry if it was originally null
          delete updated.availability[playerId][matchId];
          // Clean up empty player objects
          if (Object.keys(updated.availability[playerId]).length === 0) {
            delete updated.availability[playerId];
          }
        } else {
          // Restore original status - need to get the full original object
          const originalData = availabilityData.availability[playerId]?.[matchId];
          if (originalData) {
            updated.availability[playerId][matchId] = originalData;
          }
        }
        
        return updated;
      });
      
      toast.error('Failed to update availability');
    }
  };

  const handleClearAvailability = async (matchId: number, playerId: number, event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedTeam || !availabilityData) return;
    
    // Prevent default and blur to prevent focus-related scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
    }
    
    setActiveCell(null); // Close dropdown
    
    // Store original value for potential rollback
    const originalStatus = availabilityData.availability[playerId]?.[matchId]?.status || null;
    
    // Optimistically remove from availability immediately
    setAvailabilityData(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        availability: {
          ...prev.availability,
          [playerId]: {
            ...prev.availability[playerId]
          }
        }
      };
      
      // Remove the availability entry
      delete updated.availability[playerId][matchId];
      
      // Clean up empty player objects
      if (Object.keys(updated.availability[playerId]).length === 0) {
        delete updated.availability[playerId];
      }
      
      return updated;
    });
    
    try {
      await axios.delete(
        `/teams/${selectedTeam.id}/availability/${matchId}/${playerId}`
      );
      
      toast.success('Availability cleared');
    } catch (error) {
      console.error('Failed to clear availability:', error);
      
      // Revert optimistic update on error
      if (originalStatus !== null && availabilityData) {
        const originalData = availabilityData.availability[playerId]?.[matchId];
        if (originalData) {
          setAvailabilityData(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              availability: {
                ...prev.availability,
                [playerId]: {
                  ...prev.availability[playerId],
                  [matchId]: originalData
                }
              }
            };
          });
        }
      }
      
      toast.error('Failed to clear availability');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleImportAvailability = async () => {
    if (!selectedFile || !selectedTeam) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await axios.post(
        `/teams/${selectedTeam.id}/import-availability`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const result = response.data;
      
      // Debug: Log the full response
      console.log('Import response:', result);
      console.log('Created matches list:', result.created_matches_list);
      console.log('Created players list:', result.created_players_list);
      console.log('Matches without availability:', result.matches_without_availability);
      console.log('Players without availability:', result.players_without_availability);
      console.log('Team members not in import:', result.team_members_not_in_import);
      console.log('Team matches not in import:', result.team_matches_not_in_import);
      
      // Show detailed import report in modal
      setImportReport(result);
      setShowImportReport(true);
      
      // Refresh availability data
      await fetchAvailability(selectedTeam.id);
      
      // Reset file input and close menu
      setSelectedFile(null);
      setIsImportMenuOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      const errorMessage = error.response?.data?.detail || 'Import failed';
      toast.error(`Import failed: ${errorMessage}`);
    } finally {
      setImporting(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (activeCell) {
        const isInsideCell = target.closest('.availability-cell');
        const isInsideDropdown = target.closest('[data-availability-dropdown]');
        if (!isInsideCell && !isInsideDropdown) {
          setActiveCell(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeCell]);

  // Calculate dropdown position based on available space
  useEffect(() => {
    if (!activeCell) {
      setDropdownPosition(null);
      return;
    }

    const cellKey = `${activeCell.matchId}-${activeCell.playerId}`;
    const cellElement = cellRefs.current.get(cellKey);
    
    if (!cellElement) {
      setDropdownPosition(null);
      return;
    }

    const calculatePosition = () => {
      const rect = cellElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = 192; // w-48 = 12rem = 192px (md:w-40 = 160px on mobile, but we'll use 192 for consistency)
      const dropdownHeight = 150; // Approximate height for 3 buttons
      const spacing = 8; // mt-2 = 0.5rem = 8px

      // Check vertical space
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top: number;
      if (spaceBelow >= dropdownHeight + spacing) {
        // Enough space below, position below
        top = rect.bottom + spacing;
      } else if (spaceAbove >= dropdownHeight + spacing) {
        // Not enough space below, but enough above, position above
        top = rect.top - dropdownHeight - spacing;
      } else {
        // Not enough space either way, prefer below but allow scrolling
        top = rect.bottom + spacing;
      }

      // Check horizontal space
      const cellCenterX = rect.left + rect.width / 2;
      const dropdownHalfWidth = dropdownWidth / 2;
      
      let left: number;
      if (cellCenterX - dropdownHalfWidth < 0) {
        // Not enough space on left, align to left edge of viewport
        left = 0;
      } else if (cellCenterX + dropdownHalfWidth > viewportWidth) {
        // Not enough space on right, align to right edge of viewport
        left = viewportWidth - dropdownWidth;
      } else {
        // Enough space, center it on the cell
        left = cellCenterX - dropdownHalfWidth;
      }

      setDropdownPosition({
        top,
        left,
        width: dropdownWidth
      });
    };

    calculatePosition();

    // Recalculate on scroll and resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [activeCell]);

  // Warn on browser navigation if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Keep hasUnsavedChanges in sync with editedAvailability and editedLineups
  useEffect(() => {
    const hasLineupChanges = Object.keys(editedLineups).length > 0;
    const hasAvailabilityChanges = Object.keys(editedAvailability).length > 0;
    setHasUnsavedChanges(hasLineupChanges || hasAvailabilityChanges);
  }, [editedLineups, editedAvailability]);

  // Close strategize dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (activeStrategizeCell && !target.closest('.scheduling-availability-cell')) {
        setActiveStrategizeCell(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeStrategizeCell]);

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isActionsMenuOpen && !target.closest('.actions-menu-container')) {
        setIsActionsMenuOpen(false);
      }
    };

    if (isActionsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isActionsMenuOpen]);

  // Close import menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isImportMenuOpen && !target.closest('.import-menu-container')) {
        setIsImportMenuOpen(false);
      }
    };

    if (isImportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isImportMenuOpen]);


  const handleMatchFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setMatchForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (locationData: LocationData | null) => {
    if (locationData) {
      setMatchForm(prev => ({
        ...prev,
        location: locationData.name,
        formatted_address: locationData.formatted_address,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
      }));
    } else {
      setMatchForm(prev => ({
        ...prev,
        location: '',
        formatted_address: undefined,
        latitude: undefined,
        longitude: undefined,
      }));
    }
  };

  const handleSubmitMatch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTeam) {
      toast.error('Please select a team first');
      return;
    }

    // Validation
    if (!matchForm.opponent_name.trim()) {
      toast.error('Opponent name is required');
      return;
    }

    if (!matchForm.match_date) {
      toast.error('Match date is required');
      return;
    }

    if (!matchForm.match_time) {
      toast.error('Match time is required');
      return;
    }

    // Combine date and time into datetime format
    const combinedDateTime = `${matchForm.match_date}T${matchForm.match_time}`;
    
    const payload = {
      opponent_name: matchForm.opponent_name,
      match_date: combinedDateTime,
      location: matchForm.location || undefined,
      formatted_address: matchForm.formatted_address || undefined,
      latitude: matchForm.latitude || undefined,
      longitude: matchForm.longitude || undefined,
      home_away: matchForm.home_away || undefined,
      notes: matchForm.notes || undefined,
    };

    if (editingMatchId) {
      // Check if date, time, or location changed
      const originalData = originalMatchData[editingMatchId];
      const hasDateChanged = originalData && (
        originalData.match_date !== matchForm.match_date ||
        originalData.match_time !== matchForm.match_time
      );
      
      const hasLocationChanged = originalData && (
        originalData.location !== matchForm.location ||
        originalData.formatted_address !== matchForm.formatted_address ||
        originalData.latitude !== matchForm.latitude ||
        originalData.longitude !== matchForm.longitude
      );

      const hasRelevantChange = hasDateChanged || hasLocationChanged;

      // Check if availability exists for this match
      let availabilityCount = 0;
      if (hasRelevantChange) {
        // Try to get availability count from pre-loaded data first
        if (availabilityData) {
          Object.values(availabilityData.availability).forEach((playerAvail: any) => {
            if (playerAvail[editingMatchId]) {
              availabilityCount++;
            }
          });
        }
        
        // If no pre-loaded data or count is still 0, fetch availability for this specific match
        if (availabilityCount === 0) {
          try {
            const availabilityResponse = await axios.get(`/availability/matches/${editingMatchId}/availability`);
            availabilityCount = availabilityResponse.data ? availabilityResponse.data.length : 0;
          } catch (error) {
            console.error('Failed to fetch match availability:', error);
            // If fetch fails, assume no availability to avoid blocking the update
            availabilityCount = 0;
          }
        }
      }

      // If relevant fields changed and availability exists, prompt user
      if (hasRelevantChange && availabilityCount > 0) {
        setPendingMatchUpdate({ matchId: editingMatchId, payload, availabilityCount });
        setShowClearAvailabilityModal(true);
        return;
      }
    }

    // No relevant changes or no availability, proceed with update/create
    await performMatchUpdate(payload, false);
  };

  const performMatchUpdate = async (payload: any, clearAvailability: boolean) => {
    if (!selectedTeam) return;

    setSubmitting(true);
    try {
      if (editingMatchId) {
        // Clear availability if requested
        if (clearAvailability) {
          try {
            await axios.delete(`/availability/matches/${editingMatchId}/availability`);
            // Refresh availability data if it's loaded
            if (availabilityData) {
              const availabilityResponse = await axios.get(`/teams/${selectedTeam.id}/availability`);
              setAvailabilityData(availabilityResponse.data);
            }
          } catch (error) {
            console.error('Failed to clear availability:', error);
            // Continue with match update even if clearing availability fails
          }
        }

        // Update existing match
        await axios.put(`/matches/${editingMatchId}`, payload);
        toast.success('Match updated successfully!');
        
        // Update original match data
        const matchDate = new Date(payload.match_date);
        const formattedDate = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}-${String(matchDate.getDate()).padStart(2, '0')}`;
        const formattedTime = `${String(matchDate.getHours()).padStart(2, '0')}:${String(matchDate.getMinutes()).padStart(2, '0')}`;
        
        const updatedFormData = {
          opponent_name: payload.opponent_name,
          match_date: formattedDate,
          match_time: formattedTime,
          location: payload.location || '',
          formatted_address: payload.formatted_address || undefined,
          latitude: payload.latitude || undefined,
          longitude: payload.longitude || undefined,
          home_away: payload.home_away || 'home',
          notes: payload.notes || '',
        };
        setOriginalMatchData(prev => ({ ...prev, [editingMatchId]: updatedFormData }));
        
        setEditingMatchId(null);
      } else {
        // Create new match
        await axios.post(`/matches/teams/${selectedTeam.id}/matches`, payload);
        toast.success('Match created successfully!');
      }
      
      // Reset form
      setMatchForm({
        opponent_name: '',
        match_date: '',
        match_time: '12:00',
        location: '',
        formatted_address: undefined,
        latitude: undefined,
        longitude: undefined,
        home_away: 'home',
        notes: '',
      });
      
      setShowMatchForm(false);
      
      // Refresh matches list
      fetchMatches(selectedTeam.id);
    } catch (error: any) {
      console.error(`Failed to ${editingMatchId ? 'update' : 'create'} match:`, error);
      
      // Handle Pydantic validation errors (array of error objects)
      let errorMsg = `Failed to ${editingMatchId ? 'update' : 'create'} match`;
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
      setShowClearAvailabilityModal(false);
      setPendingMatchUpdate(null);
    }
  };

  const handleClearAvailabilityConfirm = () => {
    if (pendingMatchUpdate) {
      performMatchUpdate(pendingMatchUpdate.payload, true);
    }
  };

  const handleClearAvailabilityCancel = async () => {
    if (pendingMatchUpdate) {
      // Save the match without clearing availability
      await performMatchUpdate(pendingMatchUpdate.payload, false);
    } else {
      setShowClearAvailabilityModal(false);
      setPendingMatchUpdate(null);
    }
  };

  const handleStartEditMatch = (match: Match) => {
    // Split match date into separate date and time components
    const matchDate = new Date(match.match_date);
    const formattedDate = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}-${String(matchDate.getDate()).padStart(2, '0')}`;
    const formattedTime = `${String(matchDate.getHours()).padStart(2, '0')}:${String(matchDate.getMinutes()).padStart(2, '0')}`;
    
    const formData = {
      opponent_name: match.opponent_name || '',
      match_date: formattedDate,
      match_time: formattedTime,
      location: match.location || '',
      formatted_address: match.formatted_address || undefined,
      latitude: match.latitude || undefined,
      longitude: match.longitude || undefined,
      home_away: match.home_away || 'home',
      notes: match.notes || '',
    };
    
    setMatchForm(formData);
    setOriginalMatchData(prev => ({ ...prev, [match.id]: formData }));
    setEditingMatchId(match.id);
  };

  const handleDeleteMatch = async (matchId: number) => {
    if (!window.confirm('Are you sure you want to delete this match?')) {
      return;
    }

    try {
      await axios.delete(`/matches/${matchId}`);
      toast.success('Match deleted successfully');
      
      if (selectedTeam) {
        fetchMatches(selectedTeam.id);
      }
    } catch (error) {
      console.error('Failed to delete match:', error);
      toast.error('Failed to delete match');
    }
  };

  const handleOpenResultsModal = async (match: Match) => {
    if (!selectedTeam) return;
    
    setSelectedMatchForResults(match);
    setLoadingResults(true);
    
    try {
      // Fetch existing lineup to populate form
      const lineupResponse = await axios.get(`/lineups/matches/${match.id}/lineup`);
      const existingLineups = lineupResponse.data || [];
      
      // Separate actual results and planned lineups
      const actualResults = existingLineups.filter((l: any) => l.is_actual_result);
      const plannedLineups = existingLineups.filter((l: any) => !l.is_actual_result);
      
      // Get team's lineup format
      const teamResponse = await axios.get(`/teams/${selectedTeam.id}`);
      const lineupFormat = teamResponse.data.lineup_format;
      
      // Initialize form with lineup format positions
      const lineResults: ManualLineResult[] = [];
      if (lineupFormat && lineupFormat.positions) {
        for (const pos of lineupFormat.positions) {
          // Prefer actual results, but fall back to planned lineup if no results exist
          const actualResult = actualResults.find((l: any) => l.position_number === pos.position_number);
          const plannedLineup = plannedLineups.find((l: any) => l.position_number === pos.position_number);
          
          // Use actual result if it exists, otherwise use planned lineup
          const lineupToUse = actualResult || plannedLineup;
          
          lineResults.push({
            position_number: pos.position_number,
            player1_id: lineupToUse?.player1_id || 0,
            player2_id: lineupToUse?.player2_id || null,
            opponent_player1_name: actualResult?.opponent_player1_name ?? '',
            opponent_player2_name: actualResult?.opponent_player2_name ?? '',
            score: actualResult?.score || null,
            won: actualResult?.won ?? null
          });
        }
      }
      
      setResultsFormData({
        line_results: lineResults
      });
      
      setShowResultsModal(true);
    } catch (error) {
      console.error('Failed to load existing results:', error);
      // If no lineup exists, initialize with empty form based on lineup format
      try {
        const teamResponse = await axios.get(`/teams/${selectedTeam.id}`);
        const lineupFormat = teamResponse.data.lineup_format;
        if (lineupFormat && lineupFormat.positions) {
          const lineResults: ManualLineResult[] = lineupFormat.positions.map((pos: any) => ({
            position_number: pos.position_number,
            player1_id: 0,
            player2_id: null,
            opponent_player1_name: '',
            opponent_player2_name: '',
            score: null,
            won: null
          }));
          setResultsFormData({
            line_results: lineResults
          });
        }
        setShowResultsModal(true);
      } catch (err) {
        console.error('Failed to load lineup format:', err);
        toast.error('Failed to load match data');
      }
    } finally {
      setLoadingResults(false);
    }
  };

  const handleCloseResultsModal = () => {
    setShowResultsModal(false);
    setSelectedMatchForResults(null);
    setResultsFormData({
      line_results: []
    });
  };

  const handleResultsFormChange = (positionNumber: number, field: keyof ManualLineResult, value: any) => {
    setResultsFormData(prev => ({
      ...prev,
      line_results: prev.line_results.map(line =>
        line.position_number === positionNumber
          ? { ...line, [field]: value }
          : line
      )
    }));
  };

  const handleSaveMatchResults = async () => {
    if (!selectedMatchForResults) return;
    
    setSavingResults(true);
    
    try {
      const payload = {
        line_results: resultsFormData.line_results.map(line => ({
          position_number: line.position_number,
          player1_id: line.player1_id && line.player1_id !== 0 ? line.player1_id : undefined,
          player2_id: line.player2_id || undefined,
          opponent_player1_name: line.opponent_player1_name || undefined,
          opponent_player2_name: line.opponent_player2_name || undefined,
          score: line.score && line.score.trim() ? line.score.trim() : undefined,
          won: line.won
        }))
      };
      
      const response = await axios.put(`/matches/${selectedMatchForResults.id}/results`, payload);
      toast.success('Match results saved successfully');

      extractScoreWarnings(response.data).forEach((w) => toast.warning(w.message));
      
      // Refresh matches to show updated results
      if (selectedTeam) {
        fetchMatches(selectedTeam.id);
        // Refresh strategize data to show updated scores immediately
        fetchStrategizeData(selectedTeam.id);
      }
      
      handleCloseResultsModal();
    } catch (error: any) {
      console.error('Failed to save match results:', error);
      toast.error(error.response?.data?.detail || 'Failed to save match results');
    } finally {
      setSavingResults(false);
    }
  };

  const removeMember = async (memberId: number, userId: number) => {
    if (!selectedTeam) {
      toast.error('Unable to determine team');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }
    
    try {
      await axios.delete(`/teams/${selectedTeam.id}/members/${userId}`);
      
      // Update selected team members
      setSelectedTeam(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          members: prev.members.filter(m => m.id !== memberId)
        };
      });
      
      toast.success('Member removed successfully');
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('Failed to remove member');
    }
  };

  const toggleCaptainStatus = async (memberId: number, userId: number, currentStatus: boolean) => {
    if (!selectedTeam) {
      toast.error('Unable to determine team');
      return;
    }

    // Non-admin captains cannot directly promote non-captains to captain (must use invitations)
    // However, captains CAN demote other captains
    if (user?.role !== 'ADMIN' && !currentStatus) {
      toast.error('Only administrators can directly promote users to captain. Captains must use the invitation system to add co-captains.');
      return;
    }

    try {
      const response = await axios.patch(
        `/teams/${selectedTeam.id}/members/${userId}`,
        { is_captain: !currentStatus }
      );
      
      // Update selected team members
      setSelectedTeam(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          members: prev.members.map(m => 
            m.id === memberId ? { ...m, is_captain: response.data.is_captain } : m
          )
        };
      });
      
      toast.success(
        !currentStatus 
          ? 'Member promoted to captain' 
          : 'Captain status removed'
      );
    } catch (error: any) {
      console.error('Failed to update captain status:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update captain status';
      toast.error(errorMessage);
    }
  };

  const sendCaptainInvitation = async (userId: number, userName: string) => {
    if (!selectedTeam) {
      toast.error('Unable to determine team');
      return;
    }

    try {
      await axios.post(
        `/invitations/teams/${selectedTeam.id}/invitations`,
        { invitee_id: userId }
      );
      
      toast.success(`Invitation sent to ${userName}. They will receive a notification to accept.`);
      
      // Refresh invitations
      const invitationsResponse = await axios.get(`/invitations/teams/${selectedTeam.id}/invitations`, {
        params: { status: 'pending' }
      });
      setPendingInvitations(invitationsResponse.data || []);
    } catch (error: any) {
      console.error('Failed to send invitation:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send invitation';
      toast.error(errorMessage);
    }
  };

  const revokeInvitation = async (invitationId: number, userName: string) => {
    if (!selectedTeam) {
      toast.error('Unable to determine team');
      return;
    }

    try {
      await axios.post(`/invitations/invitations/${invitationId}/revoke`);
      toast.success(`Invitation to ${userName} has been revoked.`);
      
      // Refresh invitations
      const invitationsResponse = await axios.get(`/invitations/teams/${selectedTeam.id}/invitations`, {
        params: { status: 'pending' }
      });
      setPendingInvitations(invitationsResponse.data || []);
    } catch (error: any) {
      console.error('Failed to revoke invitation:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to revoke invitation';
      toast.error(errorMessage);
    }
  };

  const handleStartEditMember = (member: TeamMember) => {
    setEditingMemberId(member.id);
    setEditingMember(member);
    setMemberEditForm({
      email: member.user.email || '',
      gender: member.user.gender || null,
      ntrp_rating: member.user.ntrp_rating || null,
      tennisrecord_name: member.user.tennisrecord_name || '',
      lineup_type_preferences: member.lineup_type_preferences || []
    });
  };

  const handleCancelEditMember = () => {
    setEditingMemberId(null);
    setEditingMember(null);
    setMemberEditForm({
      email: '',
      gender: null,
      ntrp_rating: null,
      tennisrecord_name: '',
      lineup_type_preferences: []
    });
  };

  const handleConfirmMerge = async () => {
    if (!mergeConfirmationData || !selectedTeam || !editingMember) return;
    
    setSubmitting(true);
    try {
      await axios.patch(
        `/teams/${selectedTeam.id}/members/${mergeConfirmationData.userId}/email`,
        { email: (memberEditForm.email || '').trim(), confirm_merge: true }
      );
      
      // After merge, refresh the entire team to reflect changes
      const teamResponse = await axios.get(`/teams/${selectedTeam.id}`);
      setSelectedTeam(teamResponse.data);
      
      toast.success('Users merged successfully');
      setShowMergeConfirmationModal(false);
      setMergeConfirmationData(null);
      
      // Continue with other updates after merge (gender, NTRP, alias)
      const updates: string[] = ['email'];
      
      // Update gender if changed
      if (memberEditForm.gender !== editingMember.user.gender) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/gender`,
          { gender: memberEditForm.gender }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m => 
              m.id === editingMember.id 
                ? { ...m, user: { ...m.user, gender: response.data.user.gender } }
                : m
            )
          };
        });
        updates.push('gender');
      }

      // Update NTRP rating if changed
      if (memberEditForm.ntrp_rating !== editingMember.user.ntrp_rating) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/ntrp-rating`,
          { ntrp_rating: memberEditForm.ntrp_rating }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m => 
              m.id === editingMember.id 
                ? { ...m, user: { ...m.user, ntrp_rating: response.data.user.ntrp_rating } }
                : m
            )
          };
        });
        updates.push('NTRP rating');
      }

      // Update lineup type preferences if changed
      const currentPrefs = JSON.stringify(editingMember.lineup_type_preferences || []);
      const newPrefs = JSON.stringify(memberEditForm.lineup_type_preferences || []);
      if (newPrefs !== currentPrefs) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/lineup-preferences`,
          { lineup_type_preferences: memberEditForm.lineup_type_preferences.length > 0 ? memberEditForm.lineup_type_preferences : null }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m =>
              m.id === editingMember.id
                ? { ...m, lineup_type_preferences: response.data.lineup_type_preferences }
                : m
            )
          };
        });
        updates.push('lineup preferences');
      }

      // Update alias if changed
      const currentAlias = editingMember.user.tennisrecord_name || '';
      if (memberEditForm.tennisrecord_name.trim() !== currentAlias.trim()) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/tennisrecord-name`,
          { tennisrecord_name: memberEditForm.tennisrecord_name.trim() || null }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m => 
              m.id === editingMember.id 
                ? { ...m, user: { ...m.user, tennisrecord_name: response.data.user.tennisrecord_name } }
                : m
            )
          };
        });
        updates.push('alias');
      }

      if (updates.length > 1) {
        toast.success(`Updated ${updates.join(', ')} successfully`);
      }
      
      handleCancelEditMember();
    } catch (error: any) {
      console.error('Failed to merge users:', error);
      let errorMsg = 'Failed to merge users';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMemberChanges = async () => {
    if (!editingMember || !selectedTeam) {
      return;
    }

    setSubmitting(true);
    const updates: string[] = [];

    try {
      // Update email if changed and editable
      const canEditEmail = (!editingMember.user.google_id || editingMember.user.google_id === null) && 
                           (user?.role === 'ADMIN' || !editingMember.team_memberships_count || editingMember.team_memberships_count <= 1);
      
      if (canEditEmail && memberEditForm.email.trim() !== (editingMember.user.email || '').trim()) {
        const newEmail = memberEditForm.email.trim();
        if (!newEmail) {
          toast.error('Email is required');
          setSubmitting(false);
          return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
          toast.error('Please enter a valid email address');
          setSubmitting(false);
          return;
        }

        try {
          const response = await axios.patch(
            `/teams/${selectedTeam.id}/members/${editingMember.user_id}/email`,
            { email: newEmail, confirm_merge: false }
          );
          
          // Check if merge confirmation is required
          if (response.data.requires_merge_confirmation) {
            setMergeConfirmationData({
              memberId: editingMember.id,
              userId: editingMember.user_id,
              sourceUser: response.data.source_user,
              targetUser: response.data.target_user,
              message: response.data.message
            });
            setShowMergeConfirmationModal(true);
            setSubmitting(false);
            return; // Don't continue with other updates until merge is confirmed
          }
          
          // Refresh team data to get updated member information
          const teamResponse = await axios.get(`/teams/${selectedTeam.id}`);
          setSelectedTeam(teamResponse.data);
          updates.push('email');
        } catch (error: any) {
          console.error('Failed to update email:', error);
          let errorMsg = 'Failed to update email';
          if (error.response?.data?.detail) {
            errorMsg = error.response.data.detail;
          }
          toast.error(errorMsg);
          setSubmitting(false);
          return;
        }
      }

      // Update gender if changed
      if (memberEditForm.gender !== editingMember.user.gender) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/gender`,
          { gender: memberEditForm.gender }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m => 
              m.id === editingMember.id 
                ? { ...m, user: { ...m.user, gender: response.data.user.gender } }
                : m
            )
          };
        });
        updates.push('gender');
      }

      // Update NTRP rating if changed
      if (memberEditForm.ntrp_rating !== editingMember.user.ntrp_rating) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/ntrp-rating`,
          { ntrp_rating: memberEditForm.ntrp_rating }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m => 
              m.id === editingMember.id 
                ? { ...m, user: { ...m.user, ntrp_rating: response.data.user.ntrp_rating } }
                : m
            )
          };
        });
        updates.push('NTRP rating');
      }

      // Update lineup type preferences if changed
      const currentPrefs = JSON.stringify(editingMember.lineup_type_preferences || []);
      const newPrefs = JSON.stringify(memberEditForm.lineup_type_preferences || []);
      if (newPrefs !== currentPrefs) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/lineup-preferences`,
          { lineup_type_preferences: memberEditForm.lineup_type_preferences.length > 0 ? memberEditForm.lineup_type_preferences : null }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m =>
              m.id === editingMember.id
                ? { ...m, lineup_type_preferences: response.data.lineup_type_preferences }
                : m
            )
          };
        });
        updates.push('lineup preferences');
      }

      // Update alias if changed
      const currentAlias = editingMember.user.tennisrecord_name || '';
      if (memberEditForm.tennisrecord_name.trim() !== currentAlias.trim()) {
        const response = await axios.patch(
          `/teams/${selectedTeam.id}/members/${editingMember.user_id}/tennisrecord-name`,
          { tennisrecord_name: memberEditForm.tennisrecord_name.trim() || null }
        );
        setSelectedTeam(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            members: prev.members.map(m => 
              m.id === editingMember.id 
                ? { ...m, user: { ...m.user, tennisrecord_name: response.data.user.tennisrecord_name } }
                : m
            )
          };
        });
        updates.push('alias');
      }

      if (updates.length > 0) {
        toast.success(`Updated ${updates.join(', ')} successfully`);
      } else {
        toast.info('No changes to save');
      }

      handleCancelEditMember();
    } catch (error: any) {
      console.error('Failed to update member:', error);
      let errorMsg = 'Failed to update member';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Callback to refresh team members after adding a new member
  const handleMemberAdded = async () => {
    if (!selectedTeam) return;
    
    try {
      const teamResponse = await axios.get<Team>(`/teams/${selectedTeam.id}`);
      setSelectedTeam(teamResponse.data);
      setShowAddMemberForm(false);
    } catch (error) {
      console.error('Failed to refresh team members:', error);
    }
  };

  // Strategize helper functions
  const isMatchInPast = (matchDate: string): boolean => {
    return new Date(matchDate) < new Date();
  };

  const isSinglesPosition = (matchType: string): boolean => {
    return matchType.includes('singles');
  };

  // formatMatchType imported from ../utils/formatMatchType

  const getPlayerAvailabilityForMatch = (playerId: number, matchId: number): string | null => {
    if (!strategizeData) return null;
    return strategizeData.availability[playerId]?.[matchId]?.status || null;
  };

  const getPlayerRatings = (playerId: number) => {
    if (!competitiveIntel || !selectedTeam?.competitive_intel_enabled) return null;
    const playerData = competitiveIntel.team_players?.find((p: any) => p.id === playerId);
    return playerData || null;
  };

  const getOpponentStats = (opponentName: string) => {
    if (!competitiveIntel || !selectedTeam?.competitive_intel_enabled) return null;
    const oppData = competitiveIntel.opponent_teams?.find((o: any) =>
      o.opponent_name.toLowerCase() === opponentName.toLowerCase()
    );
    return oppData || null;
  };

  // Formats a season W-L record like "5-2"; null when no completed matches.
  const formatRecord = (oppStats: any): string | null => {
    const rec = oppStats?.record;
    if (!rec || rec.completed_count === 0) return null;
    return `${rec.wins}-${rec.losses}`;
  };

  // Recent-form chips, newest first. Empty array if nothing played.
  const renderRecentForm = (oppStats: any) => {
    const form: string[] = oppStats?.record?.recent_form || [];
    if (form.length === 0) return null;
    return (
      <span className="inline-flex gap-0.5">
        {form.map((r, idx) => (
          <span
            key={idx}
            className={`inline-block w-3.5 h-3.5 leading-[14px] text-center text-[9px] font-bold rounded-sm ${
              r === 'W'
                ? 'bg-green-200 text-green-900 dark:bg-green-700 dark:text-green-50'
                : 'bg-red-200 text-red-900 dark:bg-red-700 dark:text-red-50'
            }`}
            title={r === 'W' ? 'Win' : 'Loss'}
          >
            {r}
          </span>
        ))}
      </span>
    );
  };

  const getTeamAverageDynamicRating = (): number | null => {
    if (!competitiveIntel || !selectedTeam?.competitive_intel_enabled) return null;
    const teamPlayers = competitiveIntel.team_players || [];
    const ratings = teamPlayers
      .map((p: any) => p.dynamic_rating ? parseFloat(p.dynamic_rating) : null)
      .filter((r: number | null) => r !== null) as number[];
    
    if (ratings.length === 0) return null;
    return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  };

  const getTeamDynamicRatingRange = (): { min: number | null; max: number | null } => {
    if (!competitiveIntel || !selectedTeam?.competitive_intel_enabled) {
      return { min: null, max: null };
    }
    const teamPlayers = competitiveIntel.team_players || [];
    const ratings = teamPlayers
      .map((p: any) => p.dynamic_rating ? parseFloat(p.dynamic_rating) : null)
      .filter((r: number | null) => r !== null) as number[];
    
    if (ratings.length === 0) return { min: null, max: null };
    return {
      min: Math.min(...ratings),
      max: Math.max(...ratings)
    };
  };

  const getLineupAverageDynamicRating = (matchId: number): number | null => {
    if (!competitiveIntel || !selectedTeam?.competitive_intel_enabled || !strategizeData) return null;

    const lineup = editedLineups[matchId] || strategizeData.lineups[matchId] || [];
    const playerIds: number[] = [];
    lineup.forEach((pos: LineupEdit) => {
      if (pos.player1_id && pos.player1_id > 0) playerIds.push(pos.player1_id);
      if (pos.player2_id && pos.player2_id > 0) playerIds.push(pos.player2_id);
    });

    if (playerIds.length === 0) return null;

    const teamPlayers = competitiveIntel.team_players || [];
    const ratings = playerIds
      .map((id: number) => {
        const player = teamPlayers.find((p: any) => p.id === id);
        return player && player.dynamic_rating ? parseFloat(String(player.dynamic_rating)) : null;
      })
      .filter((r: number | null): r is number => r !== null);

    if (ratings.length === 0) return null;
    return ratings.reduce((sum: number, r: number) => sum + r, 0) / ratings.length;
  };

  const getRatingDifferenceColor = (teamAvg: number | null, opponentAvg: number | null): string => {
    if (!teamAvg || !opponentAvg) return 'text-gray-500';
    
    const diff = opponentAvg - teamAvg;
    const absDiff = Math.abs(diff);
    
    // Orange for higher-rated opponents (better/harder), green for lower-rated (worse/easier)
    if (absDiff < 0.01) {
      return 'text-gray-500'; // Similar/equal
    } else if (diff > 0) {
      // Opponent is higher (better/harder) - use orange
      return 'text-orange-600';
    } else {
      // Opponent is lower (worse/easier) - use green
      return 'text-green-600';
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getStrategizePlayerLabel = (player: StrategizeMember, matchId: number): string => {
    let label = '';
    
    const availability = getPlayerAvailabilityForMatch(player.id, matchId);
    
    // Add availability emoji indicator at the front
    if (availability === 'available') {
      label = '🟢 ';
    } else if (availability === 'maybe') {
      label = '🟡 ';
    } else if (availability === 'unavailable') {
      label = '🔴 ';
    } else if (!availability) {
      label = '❓ ';
    }
    
    label += getDisplayName(player);

    // Add NTRP rating if set
    if (player.ntrp_rating !== null && player.ntrp_rating !== undefined) {
      label += ` (${player.ntrp_rating.toFixed(1)})`;
    }

    // Add gender indicator if not set
    if (!player.gender) {
      label += ' (no gender set)';
    }

    return label;
  };

  // Helper function to get player label without emojis (for dropdown display)
  const getPlayerLabelWithoutEmoji = (player: StrategizeMember, matchId: number): string => {
    let label = getDisplayName(player);
    
    // Add NTRP rating if set
    if (player.ntrp_rating !== null && player.ntrp_rating !== undefined) {
      label += ` (${player.ntrp_rating.toFixed(1)})`;
    }
    
    // Add gender indicator if not set
    if (!player.gender) {
      label += ' (no gender set)';
    }
    
    return label;
  };

  const sortPlayersByAvailabilityForMatch = (players: StrategizeMember[], matchId: number): StrategizeMember[] => {
    // Define availability priority order
    const availabilityOrder: { [key: string]: number } = {
      'available': 1,
      'maybe': 2,
      'null': 3,  // no availability set
      'unavailable': 4
    };
    
    return [...players].sort((a, b) => {
      const aAvailability = getPlayerAvailabilityForMatch(a.id, matchId);
      const bAvailability = getPlayerAvailabilityForMatch(b.id, matchId);
      
      // Get availability priority (null becomes 'null' string for lookup)
      const aPriority = availabilityOrder[aAvailability || 'null'];
      const bPriority = availabilityOrder[bAvailability || 'null'];
      
      // First sort by availability
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // Then sort by name within same availability
      return getDisplayName(a).localeCompare(getDisplayName(b));
    });
  };

  // Helper function to get validation errors for a specific position and field
  const getErrorsForPosition = (matchId: number, positionNumber: number, field?: 'player1_id' | 'player2_id'): ValidationError[] => {
    const matchErrors = validationErrors[matchId] || [];
    return matchErrors.filter(err => 
      err.positionNumber === positionNumber && 
      (field ? err.field === field : true)
    );
  };


  // Handle blur on schedule search - collapse if empty
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        scheduleSearchContainerRef.current &&
        !scheduleSearchContainerRef.current.contains(event.target as Node) &&
        !scheduleSearchQuery.trim()
      ) {
        setIsScheduleSearchExpanded(false);
      }
    };

    if (isScheduleSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isScheduleSearchExpanded, scheduleSearchQuery]);

  // Handle click outside for NTRP dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ntrpDropdownOpen) {
        const target = event.target as Node;
        const isInsideButton = ntrpButtonRef.current?.contains(target);
        const isInsideDropdown = (event.target as HTMLElement)?.closest('[data-ntrp-dropdown]');
        
        if (!isInsideButton && !isInsideDropdown) {
          setNtrpDropdownOpen(false);
          setNtrpDropdownPosition(null);
        }
      }
    };

    if (ntrpDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [ntrpDropdownOpen]);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (ntrpDropdownOpen && ntrpButtonRef.current) {
      const updatePosition = () => {
        if (ntrpButtonRef.current) {
          const rect = ntrpButtonRef.current.getBoundingClientRect();
          setNtrpDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [ntrpDropdownOpen]);

  // Calculate available NTRP ratings from current team members
  const availableNtrpRatings = useMemo(() => {
    if (!selectedTeam || !selectedTeam.members || selectedTeam.members.length === 0) {
      return {
        ratings: [] as number[],
        hasUnset: false
      };
    }
    
    const ratingsSet = new Set<number>();
    let hasUnsetRating = false;
    
    selectedTeam.members.forEach((member) => {
      const rating = member.user.ntrp_rating ?? member.ntrp_rating;
      if (rating !== null && rating !== undefined) {
        ratingsSet.add(rating);
      } else {
        hasUnsetRating = true;
      }
    });
    
    // Filter NTRP_RATINGS to only include those present in the team
    const availableRatings = NTRP_RATINGS.filter(rating => ratingsSet.has(rating));
    
    return {
      ratings: availableRatings,
      hasUnset: hasUnsetRating
    };
  }, [selectedTeam]);

  // Filter team members based on search query and filters
  // Filter team members
  const filterTeamMembers = useCallback((membersList: TeamMember[]) => {
    let filtered = membersList;

    // Apply column-specific filters
    if (memberFilters.nameFilter.trim()) {
      const query = memberFilters.nameFilter.toLowerCase().trim();
      filtered = filtered.filter((member) =>
        getDisplayName(member.user).toLowerCase().includes(query) ||
        (member.user.name?.toLowerCase().includes(query) ?? false)
      );
    }

    if (memberFilters.emailFilter.trim()) {
      const query = memberFilters.emailFilter.toLowerCase().trim();
      filtered = filtered.filter((member) => 
        member.user.email?.toLowerCase().includes(query) ?? false
      );
    }

    if (memberFilters.captainFilter !== 'all') {
      if (memberFilters.captainFilter === 'captain') {
        filtered = filtered.filter((member) => member.is_captain);
      } else {
        filtered = filtered.filter((member) => !member.is_captain);
      }
    }

    if (memberFilters.genderFilter !== 'all') {
      if (memberFilters.genderFilter === 'not_set') {
        filtered = filtered.filter((member) => !member.user.gender || member.user.gender === null);
      } else {
        filtered = filtered.filter((member) => member.user.gender === memberFilters.genderFilter);
      }
    }

    if (memberFilters.ntrpFilter.length > 0) {
      filtered = filtered.filter((member) => {
        const memberRating = member.user.ntrp_rating ?? member.ntrp_rating;
        const hasNotSet = memberFilters.ntrpFilter.includes('not_set');
        const hasRating = memberFilters.ntrpFilter.some((rating) => 
          typeof rating === 'number' && memberRating === rating
        );
        if (hasNotSet && hasRating) {
          // Show members with any selected rating OR no rating
          return !memberRating || memberRating === null || hasRating;
        }
        if (hasNotSet) {
          // Show only members with no rating
          return !memberRating || memberRating === null;
        }
        // Show only members with selected ratings
        return hasRating;
      });
    }

    return filtered;
  }, [memberFilters]);

  // Sort team members
  const sortTeamMembers = useCallback((membersList: TeamMember[]) => {
    if (!memberSortColumn) return membersList;

    const sorted = [...membersList].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (memberSortColumn) {
        case 'name':
          aValue = getDisplayName(a.user).toLowerCase();
          bValue = getDisplayName(b.user).toLowerCase();
          break;
        case 'email':
          aValue = a.user.email?.toLowerCase() || '';
          bValue = b.user.email?.toLowerCase() || '';
          break;
        case 'captain':
          // Sort by captain status: captain > not captain
          aValue = a.is_captain ? 1 : 0;
          bValue = b.is_captain ? 1 : 0;
          break;
        case 'gender':
          aValue = a.user.gender || '';
          bValue = b.user.gender || '';
          break;
        case 'ntrp_rating':
          aValue = a.user.ntrp_rating ?? a.ntrp_rating ?? 0;
          bValue = b.user.ntrp_rating ?? b.ntrp_rating ?? 0;
          break;
        case 'join_date':
          aValue = a.join_date ? new Date(a.join_date).getTime() : 0;
          bValue = b.join_date ? new Date(b.join_date).getTime() : 0;
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Compare values
      if (aValue < bValue) return memberSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return memberSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [memberSortColumn, memberSortDirection]);

  // Filter and sort team members
  const filteredTeamMembers = useMemo(() => {
    if (!selectedTeam) {
      return [];
    }
    
    const filtered = filterTeamMembers(selectedTeam.members);
    return sortTeamMembers(filtered);
  }, [selectedTeam, filterTeamMembers, sortTeamMembers]);

  // Filter matches based on scheduleMatchFilter and search query
  const filteredScheduleMatches = useMemo(() => {
    if (!matches || matches.length === 0) {
      return [];
    }

    let filtered = matches;

    // First filter by scheduleMatchFilter (upcoming/past/all)
    if (scheduleMatchFilter === 'upcoming') {
      filtered = filtered.filter((match: Match) => !isMatchInPast(match.match_date));
    } else if (scheduleMatchFilter === 'past') {
      filtered = filtered.filter((match: Match) => isMatchInPast(match.match_date));
    }

    // Then filter by search query if present
    if (scheduleSearchQuery.trim()) {
      const query = scheduleSearchQuery.toLowerCase();
      filtered = filtered.filter((match: Match) => {
        const opponentName = match.opponent_name?.toLowerCase() || '';
        const location = (match.location || match.formatted_address || '')?.toLowerCase() || '';
        const notes = match.notes?.toLowerCase() || '';
        const matchDate = new Date(match.match_date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).toLowerCase();
        
        return (
          opponentName.includes(query) ||
          location.includes(query) ||
          notes.includes(query) ||
          matchDate.includes(query)
        );
      });
    }

    return filtered;
  }, [matches, scheduleMatchFilter, scheduleSearchQuery]);
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filterPlayersByGender = (
    players: StrategizeMember[], 
    matchType: string, 
    otherPlayerId?: number
  ): StrategizeMember[] => {
    const otherPlayer = otherPlayerId ? players.find(p => p.id === otherPlayerId) : null;
    
    if (matchType === 'mens_singles' || matchType === 'mens_doubles') {
      return players.filter(p => !p.gender || p.gender === 'male');
    }
    
    if (matchType === 'womens_singles' || matchType === 'womens_doubles') {
      return players.filter(p => !p.gender || p.gender === 'female');
    }
    
    if (matchType === 'mixed_doubles') {
      if (otherPlayer && otherPlayer.gender) {
        return players.filter(p => !p.gender || p.gender !== otherPlayer.gender);
      }
      return players;
    }
    
    return players;
  };

  const isLineupPositionChanged = (matchId: number, positionNumber: number, slot: 'player1' | 'player2'): boolean => {
    if (!strategizeData) return false;
    
    // Check if there's an edited lineup for this match
    const editedLineup = editedLineups[matchId];
    if (!editedLineup) return false;
    
    // Get the original lineup from strategizeData
    const originalLineup = strategizeData.lineups[matchId] || [];
    
    // Find the position in both lineups
    const editedPosition = editedLineup.find(p => p.position_number === positionNumber);
    const originalPosition = originalLineup.find(p => p.position_number === positionNumber);
    
    // Compare the values
    if (slot === 'player1') {
      const editedValue = editedPosition?.player1_id || 0;
      const originalValue = originalPosition?.player1_id || 0;
      return editedValue !== originalValue;
    } else {
      const editedValue = editedPosition?.player2_id || 0;
      const originalValue = originalPosition?.player2_id || 0;
      return editedValue !== originalValue;
    }
  };

  const isPositionUnfilled = (matchId: number, positionNumber: number, slot: 'player1' | 'player2'): boolean => {
    const key = `${matchId}_${positionNumber}_${slot}`;
    return unfilledPositions.has(key);
  };

  const getMemberName = (id: number | undefined | null): string => {
    if (!id) return '-';
    const member = strategizeData?.members.find(p => p.id === id);
    return member ? getDisplayName(member) : '-';
  };

  const calculatePlayerStats = () => {
    if (!strategizeData) return [];
    
    const stats = strategizeData.members.map(member => {
      let scheduledCount = 0;
      let playedCount = 0;
      let wins = 0;
      let losses = 0;
      const formatCounts: { [format: string]: number } = {};
      
      // Calculate stats for all matches (not filtered)
      strategizeData.matches.forEach(match => {
        // Use editedLineups first (unsaved changes), fall back to saved lineups
        const lineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
        const isPast = isMatchInPast(match.match_date);
        
        lineup.forEach(position => {
          const formatPos = strategizeData.lineup_format?.positions.find(
            p => p.position_number === position.position_number
          );
          const matchType = formatPos?.match_type || 'unknown';
          
          // Check if player is in this position
          const isInPosition = position.player1_id === member.id || position.player2_id === member.id;
          
          if (isInPosition) {
            if (isPast) {
              playedCount++;
              
              // Count wins/losses for actual results
              if (position.is_actual_result && position.won !== null && position.won !== undefined) {
                if (position.won) {
                  wins++;
                } else {
                  losses++;
                }
              }
            } else {
              scheduledCount++;
            }
            
            // Count by format type
            const formattedType = formatMatchType(matchType);
            formatCounts[formattedType] = (formatCounts[formattedType] || 0) + 1;
          }
        });
      });
      
      return {
        player: member,
        scheduledCount,
        playedCount,
        totalCount: scheduledCount + playedCount,
        wins,
        losses,
        formatCounts
      };
    });
    
    // Sort by total count descending, then by name
    return stats.sort((a, b) => {
      if (b.totalCount !== a.totalCount) {
        return b.totalCount - a.totalCount;
      }
      return getDisplayName(a.player).localeCompare(getDisplayName(b.player));
    });
  };

  const getColorForCount = (count: number, maxCount: number) => {
    if (maxCount === 0) return 'bg-gray-200';
    const ratio = count / maxCount;
    
    // Color scale: red (low) -> yellow (medium) -> green (high)
    if (ratio >= 0.8) return 'bg-green-500';
    if (ratio >= 0.6) return 'bg-green-400';
    if (ratio >= 0.4) return 'bg-yellow-400';
    if (ratio >= 0.2) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getLighterColorForCount = (count: number, maxCount: number) => {
    if (maxCount === 0) return 'bg-gray-200';
    const ratio = count / maxCount;
    
    // Lighter version of color scale for scheduled matches
    if (ratio >= 0.8) return 'bg-green-300';
    if (ratio >= 0.6) return 'bg-green-300';
    if (ratio >= 0.4) return 'bg-yellow-300';
    if (ratio >= 0.2) return 'bg-orange-300';
    return 'bg-red-300';
  };

  const handleStrategizeAvailabilityChange = async (matchId: number, playerId: number, status: string, event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedTeam || !strategizeData) return;
    
    // Prevent default and blur to prevent focus-related scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
    }
    
    // Store original value for potential rollback
    const originalStatus = strategizeData.availability[playerId]?.[matchId]?.status || null;
    
    // Optimistically update the state immediately
    setStrategizeData(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        availability: {
          ...prev.availability,
          [playerId]: {
            ...(prev.availability[playerId] || {})
          }
        }
      };
      
      if (status === '') {
        // Remove the availability entry when clearing
        if (updated.availability[playerId]) {
          delete updated.availability[playerId][matchId];
          // Clean up empty player objects
          if (Object.keys(updated.availability[playerId]).length === 0) {
            delete updated.availability[playerId];
          }
        }
      } else {
        // Set the availability status
        updated.availability[playerId][matchId] = {
          status: status,
          updated_at: new Date().toISOString()
        };
      }
      
      return updated;
    });
    
    // Also update editedAvailability to keep it in sync (for the save all changes flow)
    const key = `${matchId}_${playerId}`;
    if (status === '') {
      // Remove from editedAvailability if clearing
      setEditedAvailability(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } else {
      setEditedAvailability(prev => ({
        ...prev,
        [key]: status
      }));
    }
    
    try {
      // Save immediately to the API
      if (status === '') {
        // Clear availability
        await axios.delete(`/teams/${selectedTeam.id}/availability/${matchId}/${playerId}`);
      } else {
        // Set availability
        await axios.post(
          `/teams/${selectedTeam.id}/availability/${matchId}/${playerId}?availability_status=${status}`
        );
      }
      
      // Success - optimistic update is already applied
      // Remove from editedAvailability since it's now saved
      setEditedAvailability(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } catch (error) {
      console.error('Failed to update availability:', error);
      
      // Revert optimistic update on error
      setStrategizeData(prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          availability: {
            ...prev.availability,
            [playerId]: {
              ...prev.availability[playerId]
            }
          }
        };
        
        if (originalStatus === null || originalStatus === '') {
          // Remove the entry if it was originally null/empty
          delete updated.availability[playerId][matchId];
          // Clean up empty player objects
          if (Object.keys(updated.availability[playerId]).length === 0) {
            delete updated.availability[playerId];
          }
        } else {
          // Restore original status - need to get the full original object
          const originalData = strategizeData.availability[playerId]?.[matchId];
          if (originalData) {
            updated.availability[playerId][matchId] = originalData;
          }
        }
        
        return updated;
      });
      
      // Restore editedAvailability (failed saves will be retried when Save All Changes is clicked)
      if (originalStatus === null || originalStatus === '') {
        setEditedAvailability(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      } else {
        setEditedAvailability(prev => ({
          ...prev,
          [key]: originalStatus
        }));
      }
      
      toast.error('Failed to update availability');
    }
  };

  // Handle confirmation toggle click
  const handleConfirmationToggle = (
    matchId: number,
    lineupId: number,
    playerId: number,
    playerName: string,
    isCurrentlyConfirmed: boolean
  ) => {
    if (!canManageTeam) return;
    
    setPendingConfirmation({
      matchId,
      lineupId,
      playerId,
      playerName,
      isConfirming: !isCurrentlyConfirmed
    });
    setShowConfirmationModal(true);
  };

  // Perform confirmation/unconfirmation
  const performConfirmationToggle = async () => {
    if (!pendingConfirmation || !selectedTeam || !strategizeData) return;
    
    const { matchId, lineupId, playerId, playerName, isConfirming } = pendingConfirmation;
    
    // Optimistically update the state
    setStrategizeData(prev => {
      if (!prev) return prev;
      
      const updated = { ...prev };
      const matchLineups = { ...updated.lineups };
      const lineupPositions = [...(matchLineups[matchId] || [])];
      const positionIndex = lineupPositions.findIndex(p => p.id === lineupId);
      
      if (positionIndex >= 0) {
        const position = { ...lineupPositions[positionIndex] };
        const confirmations = [...(position.confirmations || [])];
        
        if (isConfirming) {
          // Add confirmation
          const now = new Date().toISOString();
          confirmations.push({
            player_id: playerId,
            confirmed_at: now,
            confirmed_by: user?.id || playerId
          });
        } else {
          // Remove confirmation
          const confirmationIndex = confirmations.findIndex(c => c.player_id === playerId);
          if (confirmationIndex >= 0) {
            confirmations.splice(confirmationIndex, 1);
          }
        }
        
        position.confirmations = confirmations;
        lineupPositions[positionIndex] = position;
        matchLineups[matchId] = lineupPositions;
        updated.lineups = matchLineups;
      }
      
      return updated;
    });
    
    setShowConfirmationModal(false);
    setPendingConfirmation(null);
    
    try {
      if (isConfirming) {
        await confirmLineupPosition(matchId, lineupId, playerId);
        toast.success(`Lineup position confirmed for ${playerName}`);
      } else {
        await unconfirmLineupPosition(matchId, lineupId, playerId);
        toast.success(`Lineup position unconfirmed for ${playerName}`);
      }
    } catch (error: any) {
      console.error('Failed to toggle confirmation:', error);
      toast.error(error.message || 'Failed to update confirmation status');
      
      // Revert optimistic update on error by refreshing data
      if (selectedTeam) {
        await fetchStrategizeData(selectedTeam.id);
      }
    }
  };

  const handleStrategizeLineupChange = (
    matchId: number, 
    positionNumber: number, 
    field: 'player1_id' | 'player2_id', 
    value: number
  ) => {
    let updatedLineup: LineupEdit[] = [];
    
    setEditedLineups(prev => {
      const matchLineup = prev[matchId] || (strategizeData?.lineups[matchId] || []).map(p => ({...p}));
      updatedLineup = [...matchLineup];
      const posIndex = updatedLineup.findIndex(p => p.position_number === positionNumber);
      
      if (posIndex >= 0) {
        updatedLineup[posIndex] = {
          ...updatedLineup[posIndex],
          [field]: value === 0 ? (field === 'player1_id' ? 0 : null) : value
        };
      } else {
        // Position doesn't exist yet, create it
        const newPosition: LineupEdit = {
          position_number: positionNumber,
          player1_id: field === 'player1_id' ? value : 0,
          player2_id: field === 'player2_id' ? (value === 0 ? null : value) : null
        };
        updatedLineup.push(newPosition);
      }
      
      return {
        ...prev,
        [matchId]: updatedLineup
      };
    });
    
    // Update hasUnsavedChanges based on whether there are any unsaved changes
    setHasUnsavedChanges(true);
    
    // If a player was selected (value > 0), remove this position from unfilled set
    if (value > 0) {
      const slot = field === 'player1_id' ? 'player1' : 'player2';
      const key = `${matchId}_${positionNumber}_${slot}`;
      setUnfilledPositions(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
    
    // Validate lineup with the updated data
    validateMatchLineupLocal(matchId, updatedLineup);
  };

  const validateMatchLineupLocal = (matchId: number, lineupToValidate?: LineupEdit[]) => {
    if (!strategizeData || !strategizeData.lineup_format) return;
    
    const matchLineup = lineupToValidate || editedLineups[matchId] || strategizeData.lineups[matchId] || [];
    
    // Convert to validation format
    const validationLineup: ValidationLineupPosition[] = matchLineup.map(pos => ({
      position_number: pos.position_number,
      player1_id: pos.player1_id || 0,
      player2_id: pos.player2_id || null
    }));
    
    // Convert lineup format to validation format
    const validationFormat: ValidationLineupFormat = {
      total_lines: strategizeData.lineup_format?.total_lines || 0,
      max_sets_per_line: strategizeData.lineup_format?.max_sets_per_line || 0,
      allow_multi_line_selection: strategizeData.lineup_format?.allow_multi_line_selection || false,
      positions: strategizeData.lineup_format?.positions.map(pos => ({
        position_number: pos.position_number,
        match_type: pos.match_type
      })) || []
    };
    
    // Convert players to validation format
    const players = strategizeData.members.map(m => ({
      id: m.id,
      name: m.name,
      gender: m.gender || null,
      ntrp_rating: m.ntrp_rating || null,
      dynamic_rating: m.dynamic_rating || null
    }));
    
    // Get team member IDs
    const teamMemberIds = strategizeData.members.map(m => m.id);
    
    // Run validation
    const result = validateMatchLineupUtil(
      matchId,
      validationLineup,
      validationFormat,
      players,
      teamMemberIds,
      selectedTeam?.max_combined_ntrp_rating
    );
    
    setValidationErrors(prev => ({
      ...prev,
      [matchId]: result.errors
    }));
  };

  const handleSaveAllChanges = async () => {
    if (!selectedTeam || !strategizeData) return;
    
    setSaving(true);
    try {
      // Check for validation errors
      const hasErrors = Object.values(validationErrors).some(errors => errors && errors.length > 0);
      if (hasErrors) {
        toast.error('Please fix validation errors before saving');
        setSaving(false);
        return;
      }
      
      // Prepare availability updates
      const availabilityUpdates = Object.entries(editedAvailability).map(([key, status]) => {
        const [matchId, playerId] = key.split('_').map(Number);
        return {
          match_id: matchId,
          player_id: playerId,
          availability_status: status
        };
      });
      
      // Prepare lineup updates - include all edited lineups, even if empty
      const lineupUpdates = Object.entries(editedLineups).map(([matchId, positions]) => ({
        match_id: parseInt(matchId),
        positions: positions.map(pos => ({
          position_number: pos.position_number,
          player1_id: pos.player1_id || 0,
          player2_id: pos.player2_id || null
        }))
      }));
      
      // Save availability if there are any pending changes (from failed immediate saves)
      if (availabilityUpdates.length > 0) {
        await axios.post(`/teams/${selectedTeam.id}/scheduling/availability`, availabilityUpdates);
      }
      
      // Save lineups if there are changes (including empty lineups)
      if (lineupUpdates.length > 0) {
        await axios.post(`/teams/${selectedTeam.id}/scheduling/lineups`, lineupUpdates);
      }
      
      toast.success('All changes saved successfully!');
      
      // Clear edits - availability is already saved optimistically, so we only clear lineups
      setEditedAvailability({});
      setEditedLineups({});
      setHasUnsavedChanges(false);
      setValidationErrors({});
      setUnfilledPositions(new Set());
      
      // Only refetch if we saved lineups (availability is already up to date via optimistic updates)
      if (lineupUpdates.length > 0) {
        fetchStrategizeData(selectedTeam.id);
      }
    } catch (error: any) {
      console.error('Failed to save changes:', error);
      
      let errorMsg = 'Failed to save changes';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        }
      }
      
      toast.error(errorMsg);
      // Form state is preserved on error
    } finally {
      setSaving(false);
    }
  };


  const handleClearAllLineups = () => {
    if (!strategizeData) return;
    
    const now = new Date();
    const newEditedLineups: { [matchId: number]: LineupEdit[] } = {};
    
    // Clear lineups for all upcoming matches
    strategizeData.matches.forEach(match => {
      if (new Date(match.match_date) >= now) {
        const emptyLineup: LineupEdit[] = [];
        strategizeData.lineup_format?.positions.forEach(pos => {
          emptyLineup.push({
            position_number: pos.position_number,
            player1_id: 0,
            player2_id: null
          });
        });
        newEditedLineups[match.id] = emptyLineup;
      }
    });
    
    setEditedLineups(newEditedLineups);
    setHasUnsavedChanges(true);
    setUnfilledPositions(new Set());
    
    const matchCount = Object.keys(newEditedLineups).length;
    toast.info(`Cleared lineups for ${matchCount} upcoming match${matchCount !== 1 ? 'es' : ''}. Click "Save All Changes" to apply.`);
  };

  const handleSuggestLineups = async () => {
    if (!selectedTeam || !strategizeData) return;
    
    setSuggestingLineups(true);
    try {
      const response = await axios.post<Array<{
        match_id: number;
        position_number: number;
        player1_id: number;
        player2_id: number | null;
      }>>(`/teams/${selectedTeam.id}/scheduling/suggest-lineups`);
      
      const suggestions = response.data;
      
      if (suggestions.length === 0) {
        toast.info('No lineup suggestions available. All positions may already be filled or no players are available.');
        return;
      }
      
      // Count total empty positions BEFORE processing suggestions
      const now = new Date();
      let totalEmptyPositions = 0;
      strategizeData.matches.forEach(match => {
        if (new Date(match.match_date) < now) return; // Skip past matches
        
        strategizeData.lineup_format?.positions.forEach(formatPos => {
          const currentLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
          const position = currentLineup.find(p => p.position_number === formatPos.position_number);
          
          // Check player1 slot
          if (!position || !position.player1_id || position.player1_id === 0) {
            totalEmptyPositions++;
          }
          // For doubles, also check player2 slot
          if (!isSinglesPosition(formatPos.match_type)) {
            if (!position || !position.player2_id || position.player2_id === 0) {
              totalEmptyPositions++;
            }
          }
        });
      });
      
      // Group suggestions by match_id and position_number
      // We need to merge multiple suggestions for the same position (player1 and player2 slots)
      const suggestionsByMatch: { [matchId: number]: LineupEdit[] } = {};
      
      // First, organize suggestions by match and position
      const suggestionMap: { [key: string]: { player1_id?: number; player2_id?: number | null } } = {};
      
      suggestions.forEach(suggestion => {
        const key = `${suggestion.match_id}_${suggestion.position_number}`;
        if (!suggestionMap[key]) {
          suggestionMap[key] = {};
        }
        
        // Merge player1 and player2 suggestions
        if (suggestion.player1_id !== undefined && suggestion.player1_id !== null) {
          suggestionMap[key].player1_id = suggestion.player1_id;
        }
        if (suggestion.player2_id !== undefined) {
          suggestionMap[key].player2_id = suggestion.player2_id;
        }
      });
      
      // Now build the lineup for each match
      Object.keys(suggestionMap).forEach(key => {
        const [matchId, positionNumber] = key.split('_').map(Number);
        const suggestedPlayers = suggestionMap[key];
        
        if (!suggestionsByMatch[matchId]) {
          // Start with a deep copy of existing lineup for this match (if any)
          const existingLineup = editedLineups[matchId] || strategizeData.lineups[matchId];
          suggestionsByMatch[matchId] = existingLineup 
            ? JSON.parse(JSON.stringify(existingLineup))
            : [];
        }
        
        // Find if this position already exists in the lineup
        const existingPositionIndex = suggestionsByMatch[matchId].findIndex(
          p => p.position_number === positionNumber
        );
        
        if (existingPositionIndex >= 0) {
          // Update existing position (only fill empty slots)
          const existing = suggestionsByMatch[matchId][existingPositionIndex];
          suggestionsByMatch[matchId][existingPositionIndex] = {
            position_number: positionNumber,
            player1_id: (suggestedPlayers.player1_id !== undefined && suggestedPlayers.player1_id > 0) 
              ? suggestedPlayers.player1_id 
              : (existing.player1_id || 0),
            player2_id: (suggestedPlayers.player2_id !== undefined && suggestedPlayers.player2_id !== null && suggestedPlayers.player2_id > 0)
              ? suggestedPlayers.player2_id
              : (existing.player2_id || null)
          };
        } else {
          // Add new position
          suggestionsByMatch[matchId].push({
            position_number: positionNumber,
            player1_id: suggestedPlayers.player1_id || 0,
            player2_id: suggestedPlayers.player2_id !== undefined ? suggestedPlayers.player2_id : null
          });
        }
      });
      
      // Merge suggestions with existing edited lineups
      const newEditedLineups = { ...editedLineups };
      Object.entries(suggestionsByMatch).forEach(([matchId, positions]) => {
        newEditedLineups[parseInt(matchId)] = positions;
      });
      
      setEditedLineups(newEditedLineups);
      setHasUnsavedChanges(true);
      
      // Track unfilled positions (those that were empty but not in suggestions)
      const positionsFilled = suggestions.length;
      const unfilledSet = new Set<string>();
      
      strategizeData.matches.forEach(match => {
        if (new Date(match.match_date) < now) return; // Skip past matches
        
        strategizeData.lineup_format?.positions.forEach(formatPos => {
          const currentLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
          const position = currentLineup.find(p => p.position_number === formatPos.position_number);
          
          // Check player1 slot
          if (!position || !position.player1_id || position.player1_id === 0) {
            // This slot was empty, check if it was filled by suggestions
            const wasFilled = suggestions.some(s => 
              s.match_id === match.id && 
              s.position_number === formatPos.position_number &&
              s.player1_id > 0
            );
            if (!wasFilled) {
              unfilledSet.add(`${match.id}_${formatPos.position_number}_player1`);
            }
          }
          
          // For doubles, check player2 slot
          if (!isSinglesPosition(formatPos.match_type)) {
            if (!position || !position.player2_id || position.player2_id === 0) {
              // This slot was empty, check if it was filled by suggestions
              const wasFilled = suggestions.some(s => 
                s.match_id === match.id && 
                s.position_number === formatPos.position_number &&
                s.player2_id !== null && s.player2_id !== undefined && s.player2_id > 0
              );
              if (!wasFilled) {
                unfilledSet.add(`${match.id}_${formatPos.position_number}_player2`);
              }
            }
          }
        });
      });
      
      setUnfilledPositions(unfilledSet);
      
      if (positionsFilled === totalEmptyPositions) {
        toast.success(`Filled all ${positionsFilled} empty position${positionsFilled !== 1 ? 's' : ''}! Review and click "Save All Changes" to apply.`);
      } else if (positionsFilled > 0) {
        toast.success(`Filled ${positionsFilled} of ${totalEmptyPositions} empty positions. Some positions couldn't be filled due to availability or gender constraints. Review and click "Save All Changes" to apply.`);
      } else {
        toast.info('Could not fill any positions. Check player availability and gender settings.');
      }
    } catch (error: any) {
      console.error('Failed to suggest lineups:', error);
      
      let errorMsg = 'Failed to generate lineup suggestions';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      toast.error(errorMsg);
    } finally {
      setSuggestingLineups(false);
    }
  };

  // Lineup format edit handlers
  const MATCH_TYPES = [
    { value: 'mens_singles', label: "Men's Singles" },
    { value: 'womens_singles', label: "Women's Singles" },
    { value: 'mens_doubles', label: "Men's Doubles" },
    { value: 'womens_doubles', label: "Women's Doubles" },
    { value: 'mixed_doubles', label: 'Mixed Doubles' },
  ];

  const handleEditLineupFormatClick = async () => {
    if (!selectedTeam?.lineup_format) return;
    
    // Count existing lineups for warning
    try {
      const schedulingResponse = await axios.get(`/teams/${selectedTeam.id}/scheduling`);
      const lineups = schedulingResponse.data.lineups || {};
      const totalLineups = Object.values(lineups).reduce((acc: number, lineup: any) => acc + (Array.isArray(lineup) ? lineup.length : 0), 0);
      setLineupCount(totalLineups);
    } catch (error) {
      console.error('Failed to fetch lineup count:', error);
      setLineupCount(null);
    }
    
    setShowLineupFormatWarning(true);
  };

  const handleConfirmLineupFormatEdit = () => {
    if (!selectedTeam?.lineup_format) return;
    
    // Initialize form data with current format
    setLineupFormatFormData({
      total_lines: selectedTeam.lineup_format.total_lines,
      max_sets_per_line: selectedTeam.lineup_format.max_sets_per_line,
      allow_multi_line_selection: (selectedTeam.lineup_format as any).allow_multi_line_selection || false,
      positions: selectedTeam.lineup_format.positions.map(pos => ({
        position_number: pos.position_number,
        match_type: pos.match_type
      }))
    });
    
    setShowLineupFormatWarning(false);
    setShowLineupFormatEdit(true);
    setLineupFormatErrors({});
  };

  const handleCancelLineupFormatEdit = () => {
    setShowLineupFormatEdit(false);
    setLineupFormatFormData(null);
    setLineupFormatErrors({});
  };

  const validateLineupFormat = (): boolean => {
    if (!lineupFormatFormData) return false;
    
    const newErrors: Record<string, string> = {};

    if (lineupFormatFormData.total_lines < 1 || lineupFormatFormData.total_lines > 15) {
      newErrors.total_lines = 'Number of lines must be between 1 and 15';
    }

    if (![1, 3, 5].includes(lineupFormatFormData.max_sets_per_line)) {
      newErrors.max_sets_per_line = 'Max sets per line must be 1, 3, or 5';
    }

    lineupFormatFormData.positions.forEach((pos) => {
      if (!pos.match_type) {
        newErrors[`position_${pos.position_number}`] = 'Match type is required for each line';
      }
    });

    setLineupFormatErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveLineupFormat = async () => {
    if (!lineupFormatFormData || !selectedTeam) return;
    
    if (!validateLineupFormat()) {
      return;
    }

    setSavingLineupFormat(true);
    try {
      await axios.put(`/teams/${selectedTeam.id}`, {
        lineup_format: {
          total_lines: lineupFormatFormData.total_lines,
          max_sets_per_line: lineupFormatFormData.max_sets_per_line,
          allow_multi_line_selection: lineupFormatFormData.allow_multi_line_selection,
          positions: lineupFormatFormData.positions
        }
      });

      toast.success('Lineup format updated successfully. All existing lineups have been cleared.');
      
      // Refresh team data
      const teamResponse = await axios.get<Team>(`/teams/${selectedTeam.id}`);
      setSelectedTeam(teamResponse.data);
      
      // Also refresh teams list to ensure consistency
      await refreshTeams();
      
      // Close modal
      setShowLineupFormatEdit(false);
      setLineupFormatFormData(null);
      setLineupFormatErrors({});
    } catch (error: any) {
      console.error('Failed to update lineup format:', error);
      let errorMsg = 'Failed to update lineup format';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setSavingLineupFormat(false);
    }
  };

  const handleLineupFormatPositionChange = (positionNumber: number, matchType: string) => {
    if (!lineupFormatFormData) return;
    
    setLineupFormatFormData({
      ...lineupFormatFormData,
      positions: lineupFormatFormData.positions.map((p) =>
        p.position_number === positionNumber ? { ...p, match_type: matchType } : p
      ),
    });
  };

  const handleLineupFormatTotalLinesChange = (totalLines: number) => {
    if (!lineupFormatFormData) return;
    
    const newPositions = Array.from({ length: totalLines }, (_, i) => {
      const existingPos = lineupFormatFormData.positions[i];
      return {
        position_number: i + 1,
        match_type: existingPos?.match_type || '',
      };
    });
    
    setLineupFormatFormData({
      ...lineupFormatFormData,
      total_lines: totalLines,
      positions: newPositions,
    });
  };

  const canManageTeam = hasCaptainAccess;

  // Show loading while checking captain status (check this first)
  if (checkingCaptainStatus || teamLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    );
  }

  // Check if user has captain access
  // If access is denied, the fetchTeamDetails function will clear localStorage and try to find another team
  // This check is just a fallback - the component should naturally transition to empty state
  if (selectedTeamId && !hasCaptainAccess && !accessLoading && !teamLoading) {
    // Access was denied - localStorage should already be cleared by fetchTeamDetails
    // The component will re-render and show empty state or team selector
    return null; // Let the component re-render naturally
  }

  // Show loading for other operations
  if (loading || accessLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    );
  }

  // Show empty state only if there are no teams at all
  // If teams exist, show team selector (backend will enforce permissions)
  // Don't block on captain check - if teams exist, let user try to access them
  if (!teamLoading && teams.length === 0 && (!selectedTeamId || !selectedTeam)) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="max-w-[1920px] mx-auto py-6 px-4 lg:px-6">
          <div className="text-center py-12">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
              <Users className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No teams to manage</h3>
            <p className="text-sm text-gray-500 mb-6">
              You're not currently a captain of any teams. Create a new team to get started!
            </p>
            <Button
              variant="primary"
              onClick={handleCreateTeam}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Team
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedTeamId || !selectedTeam) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="max-w-[1920px] mx-auto py-6 px-4 lg:px-6">
          <div className="text-center py-12">
            <div className="mx-auto max-w-md">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">No team selected</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Please select a team to manage it.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowTeamSelector(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Select a Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reusable match form component
  const renderMatchForm = (formId?: string) => (
    <div id={formId} className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 md:p-6">
      <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
        {editingMatchId ? 'Edit Match' : 'Create New Match'}
      </h3>
      <form onSubmit={handleSubmitMatch} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="opponent_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Opponent Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="opponent_name"
              name="opponent_name"
              value={matchForm.opponent_name}
              onChange={handleMatchFormChange}
              required
              className="mt-1 block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Match Date & Time <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="match_date" className="sr-only">Match Date</label>
                <input
                  type="date"
                  id="match_date"
                  name="match_date"
                  value={matchForm.match_date}
                  onChange={handleMatchFormChange}
                  required
                  className="mt-1 block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                />
              </div>
              <div>
                <label htmlFor="match_time" className="sr-only">Match Time</label>
                <input
                  type="time"
                  id="match_time"
                  name="match_time"
                  value={matchForm.match_time}
                  onChange={handleMatchFormChange}
                  required
                  className="mt-1 block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Location
            </label>
            <LocationAutocomplete
              id="location"
              name="location"
              value={matchForm.location}
              onChange={handleLocationChange}
              placeholder="Search for a location..."
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Home/Away
          </label>
          <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4">
            <label className="inline-flex items-center min-h-[44px]">
              <input
                type="radio"
                name="home_away"
                value="home"
                checked={matchForm.home_away === 'home'}
                onChange={handleMatchFormChange}
                className="form-radio h-5 w-5 text-primary-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Home</span>
            </label>
            <label className="inline-flex items-center min-h-[44px]">
              <input
                type="radio"
                name="home_away"
                value="away"
                checked={matchForm.home_away === 'away'}
                onChange={handleMatchFormChange}
                className="form-radio h-5 w-5 text-primary-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Away</span>
            </label>
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={matchForm.notes}
            onChange={handleMatchFormChange}
            className="mt-1 block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[80px]"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            type="button"
            onClick={() => {
              if (editingMatchId) {
                // Cancel inline edit - only reset editingMatchId
                setEditingMatchId(null);
              } else {
                // Cancel new match form - reset both
                setShowMatchForm(false);
                setEditingMatchId(null);
              }
            }}
            variant="outline"
            size="lg"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="primary"
            size="lg"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {editingMatchId ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                {editingMatchId ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Match
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Match
                  </>
                )}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Mobile Warning Modal */}
      {isMobileDevice && !mobileWarningDismissed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 space-y-6">
            {/* Close Button */}
            <button
              onClick={handleDismissMobileWarning}
              className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Icon with Background */}
            <div className="flex justify-center pt-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-200 to-blue-300 dark:from-blue-800 dark:to-blue-900 rounded-full blur-xl opacity-50"></div>
                <div className="relative bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-full p-6 border-2 border-blue-200 dark:border-blue-700">
                  <AlertTriangle className="h-16 w-16 text-primary-600 dark:text-primary-400" strokeWidth={1.5} />
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="text-center space-y-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Desktop Recommended
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Manage Team interface is not optimized for mobile devices. For optimal performance and the best experience, please access this page on a desktop or tablet.
              </p>
            </div>

            {/* Action Button */}
            <div className="pt-2">
              <Button
                onClick={handleDismissMobileWarning}
                variant="primary"
                className="w-full"
                size="lg"
              >
                Okay
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-[1920px] mx-auto py-6 px-4 lg:px-6">
        <div className="py-6">
          {/* Team Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {selectedTeam ? selectedTeam.name : 'No team selected'}
              </h1>
              <div className="flex items-center gap-2 group">
                <button
                  onClick={() => setShowTeamSelector(true)}
                  className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  <span>Switch</span>
                  {userPendingInvitationsCount > 0 && (
                    <span className="text-purple-600 dark:text-purple-400 font-semibold">
                      ({userPendingInvitationsCount} pending)
                    </span>
                  )}
                </button>
                <span className="hidden lg:inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
                    {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'}
                  </kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
                    K
                  </kbd>
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your team roster, schedule, and lineups</p>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-100 dark:border-gray-700/50 mb-4 md:mb-6 overflow-x-auto overflow-y-hidden">
            <nav className="-mb-px flex space-x-4 md:space-x-8 min-w-max" aria-label="Team management sections">
              {([
                { id: 'strategize' as const, icon: <WandSparkles className="w-4 h-4 mr-2" />, label: 'Lineups', shortLabel: undefined },
                { id: 'availability' as const, icon: <CalendarCheck className="w-4 h-4 mr-2" />, label: 'Team Availability', shortLabel: 'Availability' },
                { id: 'teamSchedule' as const, icon: <Calendar className="w-4 h-4 mr-2" />, label: 'Schedule', shortLabel: undefined },
                { id: 'members' as const, icon: <Users className="w-4 h-4 mr-2" />, label: 'Team Members', shortLabel: undefined },
                { id: 'analytics' as const, icon: <BarChart3 className="w-4 h-4 mr-2" />, label: 'Analytics', shortLabel: undefined },
                { id: 'configuration' as const, icon: <Settings className="w-4 h-4 mr-2" />, label: 'Configuration', shortLabel: undefined },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (canSwitchTab(tab.id)) {
                      setActiveTab(tab.id);
                    }
                  }}
                  className={`${
                    activeTab === tab.id
                      ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  } whitespace-nowrap py-2 md:py-4 px-1 border-b-2 font-medium text-sm flex items-center min-h-[44px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800`}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  {tab.icon}
                  {tab.shortLabel ? (
                    <>
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.shortLabel}</span>
                    </>
                  ) : (
                    tab.label
                  )}
                  {tab.id === 'strategize' && hasUnsavedChanges && activeTab === 'strategize' && (
                    <span className="ml-1 inline-block w-2 h-2 bg-yellow-500 rounded-full"></span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Team Members Tab */}
          {activeTab === 'members' && selectedTeam && (
            <div className="space-y-4 md:space-y-6">
              {/* Header Section */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">Team Members</h2>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {(memberFilters.nameFilter || memberFilters.emailFilter || memberFilters.captainFilter !== 'all' || memberFilters.genderFilter !== 'all' || memberFilters.ntrpFilter.length > 0) && filteredTeamMembers.length !== selectedTeam.members.length
                      ? `${filteredTeamMembers.length} of ${selectedTeam.members.length}`
                      : selectedTeam.members.length
                    } member{selectedTeam.members.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Add Member Button */}
                  {canManageTeam && !showAddMemberForm && (
                    <Button
                      onClick={() => {
                        setShowAddMemberForm(true);
                      }}
                      variant="primary"
                      size="md"
                      className="w-full sm:w-auto min-h-[44px]"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Member
                    </Button>
                  )}
                </div>
              </div>

              {/* Add Member Form */}
              {canManageTeam && showAddMemberForm && selectedTeam && (
                <AddTeamMember
                  teamId={selectedTeam.id}
                  onMemberAdded={handleMemberAdded}
                  onClose={() => setShowAddMemberForm(false)}
                  allowCaptainSelection={user?.role === 'ADMIN'}
                />
              )}

              {/* Results Count */}
              {(memberFilters.nameFilter || memberFilters.emailFilter || memberFilters.captainFilter !== 'all' || memberFilters.genderFilter !== 'all' || memberFilters.ntrpFilter.length > 0) && (
                <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Showing {filteredTeamMembers.length} of {selectedTeam.members.length} members
                </div>
              )}

              {/* Members Table */}
              <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50/80 dark:bg-gray-800/50">
                      <tr>
                        {/* Name Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleMemberSort('name')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Name
                              {memberSortColumn === 'name' && (
                                memberSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={memberFilters.nameFilter}
                              onChange={(e) => setMemberFilters({ ...memberFilters, nameFilter: e.target.value })}
                              placeholder="Filter name..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Email Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleMemberSort('email')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Email
                              {memberSortColumn === 'email' && (
                                memberSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={memberFilters.emailFilter}
                              onChange={(e) => setMemberFilters({ ...memberFilters, emailFilter: e.target.value })}
                              placeholder="Filter email..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Captain Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleMemberSort('captain')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Captain
                              {memberSortColumn === 'captain' && (
                                memberSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={memberFilters.captainFilter}
                              onChange={(e) => setMemberFilters({ ...memberFilters, captainFilter: e.target.value as 'all' | 'captain' | 'not_captain' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="captain">Captain</option>
                              <option value="not_captain">Not Captain</option>
                            </select>
                          </div>
                        </th>

                        {/* Gender Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleMemberSort('gender')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Gender
                              {memberSortColumn === 'gender' && (
                                memberSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={memberFilters.genderFilter}
                              onChange={(e) => setMemberFilters({ ...memberFilters, genderFilter: e.target.value as 'all' | 'male' | 'female' | 'not_set' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="not_set">Not Set</option>
                            </select>
                          </div>
                        </th>

                        {/* NTRP Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleMemberSort('ntrp_rating')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              NTRP
                              {memberSortColumn === 'ntrp_rating' && (
                                memberSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <div className="relative" ref={ntrpDropdownRef}>
                              <button
                                ref={ntrpButtonRef}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!ntrpDropdownOpen && ntrpButtonRef.current) {
                                    const rect = ntrpButtonRef.current.getBoundingClientRect();
                                    setNtrpDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      left: rect.left + window.scrollX,
                                      width: rect.width
                                    });
                                  }
                                  setNtrpDropdownOpen(!ntrpDropdownOpen);
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 flex items-center justify-between"
                              >
                                <span className="truncate">
                                  {memberFilters.ntrpFilter.length === 0
                                    ? 'All'
                                    : memberFilters.ntrpFilter.length === 1
                                    ? memberFilters.ntrpFilter[0] === 'not_set'
                                      ? 'Not Set'
                                      : formatNtrpRating(memberFilters.ntrpFilter[0] as number)
                                    : `${memberFilters.ntrpFilter.length} selected`}
                                </span>
                                <ChevronDown className={`w-3 h-3 transition-transform flex-shrink-0 ${ntrpDropdownOpen ? 'transform rotate-180' : ''}`} />
                              </button>
                              {ntrpDropdownOpen && ntrpDropdownPosition && typeof document !== 'undefined' && createPortal(
                                <div 
                                  data-ntrp-dropdown
                                  className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                  style={{
                                    top: `${ntrpDropdownPosition.top}px`,
                                    left: `${ntrpDropdownPosition.left}px`,
                                    width: `${Math.max(ntrpDropdownPosition.width, 200)}px`
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-2 space-y-1">
                                    {availableNtrpRatings.hasUnset && (
                                      <label className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={memberFilters.ntrpFilter.includes('not_set')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setMemberFilters({
                                                ...memberFilters,
                                                ntrpFilter: [...memberFilters.ntrpFilter, 'not_set']
                                              });
                                            } else {
                                              setMemberFilters({
                                                ...memberFilters,
                                                ntrpFilter: memberFilters.ntrpFilter.filter(r => r !== 'not_set')
                                              });
                                            }
                                          }}
                                          className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 bg-white dark:bg-gray-700"
                                        />
                                        <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Not Set</span>
                                        {memberFilters.ntrpFilter.includes('not_set') && (
                                          <Check className="ml-auto w-4 h-4 text-primary-600" />
                                        )}
                                      </label>
                                    )}
                                    {availableNtrpRatings.ratings.map((rating) => {
                                      const isSelected = memberFilters.ntrpFilter.includes(rating);
                                      return (
                                        <label
                                          key={rating}
                                          className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setMemberFilters({
                                                  ...memberFilters,
                                                  ntrpFilter: [...memberFilters.ntrpFilter, rating]
                                                });
                                              } else {
                                                setMemberFilters({
                                                  ...memberFilters,
                                                  ntrpFilter: memberFilters.ntrpFilter.filter(r => r !== rating)
                                                });
                                              }
                                            }}
                                            className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 bg-white dark:bg-gray-700"
                                          />
                                          <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                                            {formatNtrpRating(rating)}
                                          </span>
                                          {isSelected && (
                                            <Check className="ml-auto w-4 h-4 text-primary-600" />
                                          )}
                                        </label>
                                      );
                                    })}
                                  </div>
                                  {memberFilters.ntrpFilter.length > 0 && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                                      <button
                                        onClick={() => {
                                          setMemberFilters({ ...memberFilters, ntrpFilter: [] });
                                          setNtrpDropdownOpen(false);
                                          setNtrpDropdownPosition(null);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                      >
                                        Clear selection
                                      </button>
                                    </div>
                                  )}
                                </div>,
                                document.body
                              )}
                            </div>
                          </div>
                        </th>

                        {/* Lineup Preference Column */}
                        {selectedTeam?.lineup_format?.positions && new Set(selectedTeam.lineup_format.positions.map(p => p.match_type)).size > 1 && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col gap-2">
                              <span>Lineup Preferences</span>
                              <div className="h-[26px]"></div>
                            </div>
                          </th>
                        )}

                        {/* Actions Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col gap-2">
                            <span>Actions</span>
                            <div className="h-[26px]"></div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedTeam.members.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center">
                            <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No team members yet.</p>
                            <p className="text-xs md:text-sm text-gray-400 dark:text-gray-500 mt-1">Click "Add New Member" to add your first team member.</p>
                          </td>
                        </tr>
                      ) : filteredTeamMembers.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center">
                            <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                              No members found matching the current filters
                            </p>
                            <button
                              onClick={() => {
                                setMemberFilters({
                                  nameFilter: '',
                                  emailFilter: '',
                                  captainFilter: 'all',
                                  genderFilter: 'all',
                                  ntrpFilter: []
                                });
                              }}
                              className="mt-4 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium min-h-[44px] px-4"
                            >
                              Clear filters
                            </button>
                          </td>
                        </tr>
                      ) : (
                        filteredTeamMembers.map((member) => (
                          <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            {/* Name Column */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{getDisplayName(member.user)}</span>
                                {member.user.tennisrecord_name &&
                                 member.user.tennisrecord_name.trim().toLowerCase() !== member.user.name.trim().toLowerCase() && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400" title={`Full name: ${member.user.name}`}>
                                    ({member.user.name})
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Email Column */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span 
                                className={`text-sm text-gray-900 dark:text-gray-100 ${
                                  (member.team_memberships_count && member.team_memberships_count > 1) || 
                                  (member.user.google_id && member.user.google_id !== null)
                                    ? 'cursor-help' 
                                    : ''
                                }`}
                                title={
                                  (member.team_memberships_count && member.team_memberships_count > 1) || 
                                  (member.user.google_id && member.user.google_id !== null)
                                    ? "Email cannot be changed if user belongs to multiple teams or has already authenticated"
                                    : undefined
                                }
                              >
                                {member.user.email || <span className="text-gray-400 dark:text-gray-500 italic">No email</span>}
                              </span>
                            </td>

                            {/* Captain Column */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {member.is_captain ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                    Captain
                                  </span>
                                ) : pendingInvitations.some(inv => inv.invitee_id === member.user_id && inv.status === 'pending') ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                    Invite Sent
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-500 dark:text-gray-400">—</span>
                                )}
                              </div>
                            </td>

                            {/* Gender Column */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {member.user.gender ? (member.user.gender === 'male' ? 'Male' : 'Female') : 'Not set'}
                              </span>
                            </td>

                            {/* NTRP Column */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div>
                                <span className="text-sm text-gray-900 dark:text-gray-100">
                                  {member.user.ntrp_rating ? formatNtrpRating(member.user.ntrp_rating) : 'Not set'}
                                </span>
                                {isTennisRecordEnabled && member.dynamic_rating !== null && member.dynamic_rating !== undefined && (
                                  <div className="mt-1 text-xs text-purple-600 dark:text-purple-400">
                                    Dynamic: {member.dynamic_rating.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Lineup Preference Column */}
                            {selectedTeam?.lineup_format?.positions && new Set(selectedTeam.lineup_format.positions.map(p => p.match_type)).size > 1 && (
                              <td className="px-4 py-3">
                                <span className="text-sm text-gray-900 dark:text-gray-100">
                                  {!member.lineup_type_preferences || member.lineup_type_preferences.length === 0
                                    ? <span className="text-gray-400 dark:text-gray-500">No Preference</span>
                                    : member.lineup_type_preferences.length === 1
                                      ? <span className="inline-flex items-center gap-1.5"><MatchTypeIcon matchType={member.lineup_type_preferences[0]} iconSize="w-4 h-4" />{formatMatchType(member.lineup_type_preferences[0])}</span>
                                      : <ol className="list-decimal list-inside space-y-0.5 whitespace-nowrap">
                                          {member.lineup_type_preferences.map((pref, i) => (
                                            <li key={i} className="inline-flex items-center gap-1.5"><MatchTypeIcon matchType={pref} iconSize="w-4 h-4" />{formatMatchType(pref)}</li>
                                          ))}
                                        </ol>
                                  }
                                </span>
                              </td>
                            )}

                            {/* Actions Column */}
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              {canManageTeam && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  {(user?.role === 'ADMIN' || member.is_captain) ? (
                                    <Button
                                      onClick={() => toggleCaptainStatus(member.id, member.user_id, member.is_captain)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-sm px-2 py-1 whitespace-nowrap"
                                    >
                                      {member.is_captain ? (
                                        <>
                                          <UserX className="w-3.5 h-3.5 mr-1" />
                                          Remove Captain
                                        </>
                                      ) : (
                                        <>
                                          <UserCheck className="w-3.5 h-3.5 mr-1" />
                                          Invite Captain
                                        </>
                                      )}
                                    </Button>
                                  ) : (
                                    <>
                                      {!member.is_captain && (() => {
                                        const pendingInv = pendingInvitations.find(inv => inv.invitee_id === member.user_id && inv.status === 'pending');
                                        const hasEmail = member.user.email && member.user.email.trim();
                                        
                                        return pendingInv ? (
                                          <Button
                                            onClick={() => revokeInvitation(pendingInv.id, getDisplayName(member.user))}
                                            variant="ghost"
                                            size="sm"
                                            className="text-sm px-2 py-1 whitespace-nowrap text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                          >
                                            <XCircle className="w-3.5 h-3.5 mr-1" />
                                            Revoke Invitation
                                          </Button>
                                        ) : !hasEmail ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled
                                            className="text-sm px-2 py-1 whitespace-nowrap opacity-60"
                                            title="This user does not have an email address. They must have an email to receive captain invitations."
                                          >
                                            <UserCheck className="w-3.5 h-3.5 mr-1" />
                                            Invite Captain
                                          </Button>
                                        ) : (
                                          <Button
                                            onClick={() => sendCaptainInvitation(member.user_id, getDisplayName(member.user))}
                                            variant="ghost"
                                            size="sm"
                                            className="text-sm px-2 py-1 whitespace-nowrap"
                                          >
                                            <UserCheck className="w-3.5 h-3.5 mr-1" />
                                            Invite Captain
                                          </Button>
                                        );
                                      })()}
                                    </>
                                  )}
                                  <Button
                                    onClick={() => handleStartEditMember(member)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-sm px-2 py-1 whitespace-nowrap"
                                  >
                                    <Edit className="w-3.5 h-3.5 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => removeMember(member.id, member.user_id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-sm px-2 py-1 whitespace-nowrap text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                    Remove
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'teamSchedule' && selectedTeam && (
            <div className="space-y-4 md:space-y-6">
              {/* Header Section */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">Schedule</h2>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {(scheduleSearchQuery.trim() || scheduleMatchFilter !== 'all') && filteredScheduleMatches.length !== matches.length
                      ? `${filteredScheduleMatches.length} of ${matches.length}`
                      : matches.length
                    } match{matches.length !== 1 ? 'es' : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Search - First */}
                  {matches && matches.length > 0 && (
                    <div className="relative flex items-center w-full sm:flex-initial sm:w-64">
                      <div className="relative w-full flex items-center">
                        <input
                          ref={scheduleSearchInputRef}
                          type="text"
                          value={scheduleSearchQuery}
                          onChange={(e) => setScheduleSearchQuery(e.target.value)}
                          placeholder="Search matches..."
                          className="w-full min-h-[44px] pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
                          <Search className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Match Filter - Second */}
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                      value={scheduleMatchFilter}
                      onChange={(e) => setScheduleMatchFilter(e.target.value as 'all' | 'upcoming' | 'past')}
                      className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] min-w-[140px]"
                    >
                      <option value="all">All Matches</option>
                      <option value="upcoming">Upcoming Only</option>
                      <option value="past">Past Only</option>
                    </select>
                  </div>
                  
                  {/* Add Match Button - Third */}
                  {canManageTeam && (
                    <Button
                      onClick={() => {
                        if (!showMatchForm) {
                          // Opening form for new match - clear form first
                          setMatchForm({
                            opponent_name: '',
                            match_date: '',
                            match_time: '12:00',
                            location: '',
                            formatted_address: undefined,
                            latitude: undefined,
                            longitude: undefined,
                            home_away: 'home',
                            notes: '',
                          });
                          setEditingMatchId(null);
                        }
                        setShowMatchForm(!showMatchForm);
                        setScheduleSearchQuery('');
                      }}
                      variant="primary"
                      size="md"
                      className="w-full sm:w-auto min-h-[44px]"
                    >
                      {showMatchForm ? (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Match
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Match Creation Form - Only for new matches */}
              {canManageTeam && showMatchForm && !editingMatchId && (
                renderMatchForm('match-form')
              )}

              {/* Matches List */}
              {(() => {
                const totalMatches = matches.length;
                
                if (filteredScheduleMatches.length === 0) {
                  return (
                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 text-center py-8 md:py-12">
                      <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                        {totalMatches === 0 
                          ? 'No matches scheduled yet.'
                          : scheduleSearchQuery.trim()
                            ? `No matches found matching "${scheduleSearchQuery}".`
                            : `No ${scheduleMatchFilter === 'upcoming' ? 'upcoming' : scheduleMatchFilter === 'past' ? 'past' : ''} matches found.`
                        }
                      </p>
                      {totalMatches === 0 && (
                        <p className="text-xs md:text-sm text-gray-400 dark:text-gray-500 mt-1">Click "Add Match" to create your first match.</p>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {filteredScheduleMatches.map((match) => (
                      editingMatchId === match.id ? (
                        // Render edit form in place of match card
                        <div key={match.id}>
                          {renderMatchForm()}
                        </div>
                      ) : (
                        // Render normal match card
                        <div 
                          key={match.id} 
                          className="relative block bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 md:p-6"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0 pr-3">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                                <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">
                                  {match.home_away === 'away' ? 'at' : 'vs'} {match.opponent_name}
                                </h3>
                                {match.match_completed && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                    ✓ Results Entered
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1">
                                {(() => {
                                  const formatted = formatMatchDateFull(match.match_date);
                                  return (
                                    <>
                                      <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 flex-shrink-0" />
                                        {formatted.date}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                                        <Clock className="w-4 h-4 flex-shrink-0" />
                                        {formatted.time}
                                      </p>
                                    </>
                                  );
                                })()}
                                {(match.location || match.formatted_address) && (
                                  <div className="space-y-1">
                                    {match.location && (
                                      <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 flex-shrink-0" />
                                        {match.location}
                                      </p>
                                    )}
                                    {match.formatted_address && (
                                      <p className={`flex items-center gap-2 ${match.location ? 'text-sm text-gray-600 dark:text-gray-400 ml-6' : 'text-sm text-gray-600 dark:text-gray-300'}`}>
                                        {!match.location && <MapPin className="w-4 h-4 flex-shrink-0" />}
                                        {match.formatted_address}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              {match.notes && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 italic">
                                  {match.notes}
                                </p>
                              )}
                            </div>
                            {canManageTeam && (
                              <div 
                                className="flex flex-col min-[900px]:flex-row gap-2 flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  onClick={() => navigate(`/matches/${match.id}`)}
                                  variant="ghost"
                                  size="sm"
                                  className="whitespace-nowrap"
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                                  View Details
                                </Button>
                                <Button
                                  onClick={() => handleStartEditMatch(match)}
                                  variant="ghost"
                                  size="sm"
                                  className="whitespace-nowrap"
                                >
                                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                                  Edit Match
                                </Button>
                                {isMatchInPast(match.match_date) && (
                                  <Button
                                    onClick={() => handleOpenResultsModal(match)}
                                    variant="ghost"
                                    size="sm"
                                    className="whitespace-nowrap"
                                  >
                                    <Trophy className="w-3.5 h-3.5 mr-1.5" />
                                    {match.match_completed ? 'Edit Results' : 'Enter Results'}
                                  </Button>
                                )}
                                <Button
                                  onClick={() => handleDeleteMatch(match.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="whitespace-nowrap text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                  Delete
                                </Button>
                              </div>
                            )}
                            {!canManageTeam && (
                              <div 
                                className="flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  onClick={() => navigate(`/matches/${match.id}`)}
                                  variant="ghost"
                                  size="sm"
                                  className="whitespace-nowrap"
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                                  View Details
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                );
              })()}
              
            </div>
          )}

          {/* Availability Tab */}
          {activeTab === 'availability' && selectedTeam && (
            <div className="space-y-4 md:space-y-6">
              {/* Header Section */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <CalendarCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">Team Availability</h2>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Mobile Toggle Button - Only visible on mobile */}
                  <button
                    onClick={() => setMobileAvailabilityFiltersExpanded(!mobileAvailabilityFiltersExpanded)}
                    className="sm:hidden flex items-center gap-2 px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                  >
                    <Filter className="w-4 h-4" />
                    <span>{mobileAvailabilityFiltersExpanded ? 'Hide' : 'Show'} Filters</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileAvailabilityFiltersExpanded ? 'transform rotate-180' : ''}`} />
                  </button>
                  
                  {/* Filters Container - Collapsible on mobile */}
                  <div className={`flex-wrap items-center gap-3 w-full sm:w-auto ${mobileAvailabilityFiltersExpanded ? 'flex' : 'hidden sm:flex'}`}>
                    {/* Match Filter - First */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <select
                        value={availabilityMatchFilter}
                        onChange={(e) => setAvailabilityMatchFilter(e.target.value as 'all' | 'upcoming' | 'past')}
                        className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] min-w-[140px]"
                      >
                        <option value="all">All Matches</option>
                        <option value="upcoming">Upcoming Only</option>
                        <option value="past">Past Only</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Import Actions Menu - Second */}
                  {(user?.role === 'ADMIN' || selectedTeam.members.some(m => m.user_id === user?.id && m.is_captain)) && (
                    <div className="relative import-menu-container w-full sm:w-auto">
                      <Button
                        onClick={() => setIsImportMenuOpen(!isImportMenuOpen)}
                        disabled={availabilityLoading}
                        variant="primary"
                        size="md"
                        className="flex items-center gap-2 min-h-[44px] w-full sm:w-auto"
                      >
                        <Upload className="w-4 h-4" />
                        Import
                        <ChevronDown className={`w-4 h-4 transition-transform ${isImportMenuOpen ? 'rotate-180' : ''}`} />
                      </Button>
                      
                      {/* Dropdown Menu */}
                      {isImportMenuOpen && (
                        <div className="absolute right-0 mt-2 w-full sm:w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                          <div className="py-1">
                            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-start gap-3">
                                <Upload className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    Import from TennisLink
                                  </h3>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Upload a Team Availability Report from tennislink.usta.com
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="px-4 py-3">
                              <input
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={handleFileSelect}
                                ref={fileInputRef}
                                className="hidden"
                              />
                              
                              {!selectedFile ? (
                                <Button
                                  onClick={() => fileInputRef.current?.click()}
                                  variant="primary"
                                  fullWidth
                                  size="lg"
                                >
                                  <FileUp className="w-4 h-4 mr-2" />
                                  Choose File
                                </Button>
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                    <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                    <span className="flex-1 truncate">{selectedFile.name}</span>
                                    {!importing && (
                                      <button
                                        onClick={() => {
                                          setSelectedFile(null);
                                          if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                  
                                  <Button
                                    onClick={handleImportAvailability}
                                    disabled={importing}
                                    variant="primary"
                                    fullWidth
                                    size="lg"
                                    className="bg-green-600 hover:bg-green-700 focus:ring-green-500"
                                  >
                                    {importing ? (
                                      <span className="flex items-center justify-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Importing...
                                      </span>
                                    ) : (
                                      'Import Availability'
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Compute filtered matches outside conditional rendering so it can be used for both table and legend */}
              {(() => {
                // Compute filtered matches before conditional rendering so it can be used for both table and legend
                const filteredMatches = availabilityData && availabilityData.matches.length > 0
                  ? availabilityData.matches.filter((match) => {
                      if (availabilityMatchFilter === 'all') return true;
                      if (availabilityMatchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                      if (availabilityMatchFilter === 'past') return isMatchInPast(match.match_date);
                      return true;
                    })
                  : [];
                
                if (availabilityLoading) {
                  return (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
                    </div>
                  );
                }
                
                if (!availabilityData || availabilityData.matches.length === 0) {
                  return (
                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-8 md:p-12 text-center">
                      <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No matches scheduled yet.</p>
                      <p className="text-xs md:text-sm text-gray-400 dark:text-gray-500 mt-1">Add matches to track player availability.</p>
                    </div>
                  );
                }
                
                if (filteredMatches.length === 0) {
                  return (
                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-8 md:p-12 text-center">
                      <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No {availabilityMatchFilter === 'upcoming' ? 'upcoming' : 'past'} matches found.</p>
                    </div>
                  );
                }
                
                return (
                  <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 -mx-4 md:mx-0">
                    <div className="px-4 md:px-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 md:hidden">Swipe horizontally to see all matches</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse min-w-max">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-700">
                            <th className="sticky left-0 top-0 z-30 bg-gray-50 dark:bg-gray-700 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-r dark:border-gray-600 min-w-[120px]">
                              Match Details
                            </th>
                            {filteredMatches.map((match) => {
                            const isPast = new Date(match.match_date) < new Date();
                            return (
                              <th
                                key={match.id}
                                className={`sticky top-0 z-20 px-3 py-3 text-left border-b border-l border-gray-200 dark:border-gray-600 relative ${
                                  isPast ? 'bg-gray-100 dark:bg-gray-700/50 opacity-60' : 'bg-gray-50 dark:bg-gray-700'
                                }`}
                            >
                              <div className="flex flex-col gap-2">
                                {/* Opponent Name */}
                                <h4 className={`text-sm font-medium truncate ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                  {match.opponent_name}
                                </h4>
                                
                                {/* Date/Time */}
                                {(() => {
                                  const formatted = formatMatchDateCompact(match.match_date, false);
                                  const textClass = isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300';
                                  return (
                                    <div className="space-y-1">
                                      <div className={`flex items-center gap-1.5 text-xs ${textClass}`}>
                                        <Calendar className="w-3 h-3 flex-shrink-0" />
                                        <span>{formatted.date}</span>
                                      </div>
                                      <div className={`flex items-center gap-1.5 text-xs ${textClass}`}>
                                        <Clock className="w-3 h-3 flex-shrink-0" />
                                        <span>{formatted.time}</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                
                                {/* Location */}
                                {match.location && (
                                  <div className={`flex items-center gap-1.5 text-xs ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">
                                      {match.location}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Bottom row: Home/Away and Past indicators */}
                                <div className="flex items-center gap-2 flex-wrap mt-auto">
                                  {match.home_away && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                                      match.home_away === 'home' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {match.home_away === 'home' ? 'H' : 'A'}
                                    </span>
                                  )}
                                  {isPast && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 flex-shrink-0">
                                      Past
                                    </span>
                                  )}
                                </div>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {/* Availability Summary Header Row */}
                      <tr className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-10">
                        <td className="sticky left-0 z-20 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-t-2 border-r dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                          <div className="flex items-center justify-between gap-2">
                            <span>Availability Summary</span>
                            <button
                              onClick={() => setIsAvailabilitySummaryCollapsed(!isAvailabilitySummaryCollapsed)}
                              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 px-3 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 flex-shrink-0"
                            >
                              {isAvailabilitySummaryCollapsed ? 'Show' : 'Hide'}
                            </button>
                          </div>
                        </td>
                        {isAvailabilitySummaryCollapsed ? (
                          // Show summary when collapsed
                          filteredMatches.map(match => {
                            if (!availabilityData) return null;
                            
                            // Count available players by gender for this match
                            const availableMen = availabilityData.members.filter(m => 
                              m.gender === 'male' && 
                              (availabilityData.availability[m.id]?.[match.id]?.status === 'available')
                            ).length;
                            
                            const availableWomen = availabilityData.members.filter(m => 
                              m.gender === 'female' && 
                              (availabilityData.availability[m.id]?.[match.id]?.status === 'available')
                            ).length;
                            
                            const availableUnknown = availabilityData.members.filter(m => 
                              (!m.gender || m.gender === null) && 
                              (availabilityData.availability[m.id]?.[match.id]?.status === 'available')
                            ).length;
                            
                            // Format gender breakdown for display (matching match details page format)
                            const formatGenderBreakdown = () => {
                              const parts: string[] = [];
                              if (availableMen > 0) {
                                parts.push(`${availableMen} ${availableMen === 1 ? 'man' : 'men'}`);
                              }
                              if (availableWomen > 0) {
                                parts.push(`${availableWomen} ${availableWomen === 1 ? 'woman' : 'women'}`);
                              }
                              if (availableUnknown > 0) {
                                parts.push(`${availableUnknown} unspecified`);
                              }
                              return parts.length > 0 ? `(${parts.join(', ')})` : null;
                            };
                            
                            return (
                              <td key={match.id} className="border-t-2 border-l dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-3 py-2 text-center">
                                {formatGenderBreakdown() ? (
                                  <div className="text-xs text-gray-600 dark:text-gray-300">
                                    {formatGenderBreakdown()}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400 dark:text-gray-500">-</div>
                                )}
                              </td>
                            );
                          })
                        ) : (
                          // Show empty cells when expanded (detailed view will be shown below)
                          filteredMatches.map(match => (
                            <td key={match.id} className="border-t-2 border-l dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-3 py-2"></td>
                          ))
                        )}
                      </tr>

                      {/* Summary Section */}
                      {!isAvailabilitySummaryCollapsed && (
                        <>
                      {['available', 'maybe', 'unavailable', 'not_set'].map((status) => {
                        const statusLabel = status === 'not_set' ? 'Not Set' : status === 'maybe' ? 'If Needed' : status.charAt(0).toUpperCase() + status.slice(1);
                        const statusIcon = status === 'available' ? CheckCircle : status === 'unavailable' ? XCircle : status === 'maybe' ? AlertCircle : HelpCircle;
                        const StatusIcon = statusIcon;
                        const statusColor = status === 'available' ? 'text-green-500' : status === 'unavailable' ? 'text-red-500' : status === 'maybe' ? 'text-yellow-500' : 'text-gray-400';
                        
                        return (
                          <tr key={status} className="bg-gray-50 dark:bg-gray-700">
                            <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-b border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2">
                                <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                                <span>{statusLabel}</span>
                              </div>
                            </td>
                            {filteredMatches.map((match) => {
                              if (!availabilityData) return null;
                              const summary = getMatchAvailabilitySummary(match.id, availabilityData.members, availabilityData.availability);
                              const matchSummary = summary[status] || { male: {}, female: {}, unspecified: {} };
                              
                              // Build summary with structured data for better rendering
                              const genderSummaries: Array<{
                                gender: string;
                                genderLabel: string;
                                total: number;
                                ntrpBreakdown: Array<{ ntrp: string; count: number }>;
                                colorClass: string;
                              }> = [];
                              
                              // Process each gender
                              ['male', 'female', 'unspecified'].forEach((gender) => {
                                const genderLabel = gender === 'male' ? 'men' : gender === 'female' ? 'women' : 'unspecified';
                                const genderData = matchSummary[gender] || {};
                                
                                // Group by NTRP rating
                                const ntrpGroups: { [key: string]: number } = {};
                                Object.entries(genderData).forEach(([ntrp, count]) => {
                                  const countNum = count as number;
                                  if (!ntrpGroups[ntrp]) {
                                    ntrpGroups[ntrp] = 0;
                                  }
                                  ntrpGroups[ntrp] += countNum;
                                });
                                
                                // Build gender summary with NTRP breakdown showing count at each level
                                const totalForGender = Object.values(ntrpGroups).reduce((sum, val) => sum + (val as number), 0);
                                if (totalForGender > 0) {
                                  const ntrpBreakdown: Array<{ ntrp: string; count: number }> = [];
                                  Object.entries(ntrpGroups)
                                    .sort(([a], [b]) => {
                                      if (a === 'no_rating') return 1;
                                      if (b === 'no_rating') return -1;
                                      return parseFloat(a) - parseFloat(b);
                                    })
                                    .forEach(([ntrp, count]) => {
                                      const countNum = count as number;
                                      if (countNum > 0) {
                                        ntrpBreakdown.push({
                                          ntrp: ntrp === 'no_rating' ? 'no rating' : ntrp,
                                          count: countNum
                                        });
                                      }
                                    });
                                  
                                  const colorClass = gender === 'male' 
                                    ? 'text-primary-700 dark:text-primary-300' 
                                    : gender === 'female' 
                                    ? 'text-pink-700 dark:text-pink-300' 
                                    : 'text-gray-600 dark:text-gray-400';
                                  
                                  genderSummaries.push({
                                    gender,
                                    genderLabel,
                                    total: totalForGender,
                                    ntrpBreakdown,
                                    colorClass
                                  });
                                }
                              });
                              
                              const isPast = new Date(match.match_date) < new Date();
                              
                              return (
                                <td
                                  key={match.id}
                                  className={`px-3 py-2.5 text-xs border-l border-b border-gray-200 dark:border-gray-600 align-top ${isPast ? 'opacity-60' : ''}`}
                                >
                                  {genderSummaries.length > 0 ? (
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-1.5 mb-0.5">
                                        <StatusIcon className={`w-3.5 h-3.5 ${statusColor} flex-shrink-0`} />
                                        <span className="text-gray-500 dark:text-gray-400 text-[10px] font-medium uppercase">{statusLabel}</span>
                                      </div>
                                      {genderSummaries.map((summary, idx) => (
                                        <div key={idx} className="flex flex-col gap-1">
                                          <div className={`font-semibold ${summary.colorClass}`}>
                                            {summary.total} {summary.total === 1 
                                              ? (summary.genderLabel === 'men' ? 'man' : summary.genderLabel === 'women' ? 'woman' : 'unspecified')
                                              : summary.genderLabel}
                                          </div>
                                          <div className="flex flex-wrap gap-1">
                                            {summary.ntrpBreakdown.map((item, itemIdx) => (
                                              <span
                                                key={itemIdx}
                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                              >
                                                {item.count} × {item.ntrp}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-1.5">
                                        <StatusIcon className={`w-3.5 h-3.5 ${statusColor} flex-shrink-0`} />
                                        <span className="text-gray-500 dark:text-gray-400 text-[10px] font-medium uppercase">{statusLabel}</span>
                                      </div>
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                        </>
                      )}
                      
                      {/* Men Section */}
                      {availabilityData.members.filter(m => m.gender === 'male').length > 0 && (
                        <>
                          <tr className="bg-primary-50 dark:bg-primary-900 sticky top-0 z-[9]">
                            <td className="sticky left-0 z-20 px-4 py-1.5 text-xs font-semibold text-primary-800 dark:text-primary-300 uppercase tracking-wider border-r dark:border-gray-600 bg-primary-50 dark:bg-primary-900">
                              Men
                            </td>
                            <td colSpan={filteredMatches.length} className="bg-primary-50 dark:bg-primary-900/20"></td>
                          </tr>
                          {availabilityData.members
                            .filter(m => m.gender === 'male')
                            .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
                            .map((member) => (
                              <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 border-t dark:border-gray-700">
                                <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r dark:border-gray-600 min-w-[120px]">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="truncate">{getDisplayName(member)}</span>
                                    {member.ntrp_rating != null && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {member.ntrp_rating.toFixed(1)}
                                      </span>
                                    )}
                                    {isTennisRecordEnabled && member.dynamic_rating != null && (
                                      <span className="text-xs text-purple-700 dark:text-purple-300">
                                        {member.dynamic_rating.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {filteredMatches.map((match: { id: number; match_date: string; opponent_name: string; home_away?: string }) => {
                                  const status = availabilityData.availability[member.id]?.[match.id]?.status;
                                  const isCurrentUserAdmin = user?.role === 'ADMIN';
                                  const isCurrentUserCaptain = selectedTeam?.members.some(m => m.user_id === user?.id && m.is_captain);
                                  const isOwnAvailability = member.id === user?.id;
                                  const isPast = isMatchInPast(match.match_date);
                                  
                                  // Players can only edit their own availability, unless they're captain or admin
                                  const canEdit = (isCurrentUserAdmin || isCurrentUserCaptain || isOwnAvailability) && !isPast;
                                  const cellKey = `${match.id}-${member.id}`;
                                  
                                  return (
                                    <td
                                      key={match.id}
                                      ref={(el) => {
                                        if (el) {
                                          cellRefs.current.set(cellKey, el);
                                        } else {
                                          cellRefs.current.delete(cellKey);
                                        }
                                      }}
                                      className={`px-3 py-1.5 text-center border-l dark:border-gray-600 availability-cell relative ${isPast ? 'bg-gray-50 dark:bg-gray-700/50' : ''} ${canEdit ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                      onClick={() => canEdit && handleAvailabilityCellClick(match.id, member.id)}
                                      title={canEdit ? 'Click to set availability' : ''}
                                    >
                                      <div className="flex justify-center">
                                        {status === 'available' ? (
                                          <CheckCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-green-500'}`} />
                                        ) : status === 'unavailable' ? (
                                          <XCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-red-500'}`} />
                                        ) : status === 'maybe' ? (
                                          <AlertCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-yellow-500'}`} />
                                        ) : (
                                          <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                                        )}
                                      </div>

                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </>
                      )}

                      {/* Women Section */}
                      {availabilityData.members.filter(m => m.gender === 'female').length > 0 && (
                        <>
                          <tr className="bg-pink-50 dark:bg-pink-900 sticky top-0 z-[9]">
                            <td className="sticky left-0 z-20 px-4 py-1.5 text-xs font-semibold text-pink-800 dark:text-pink-300 uppercase tracking-wider border-r dark:border-gray-600 bg-pink-50 dark:bg-pink-900">
                              Women
                            </td>
                            <td colSpan={filteredMatches.length} className="bg-pink-50 dark:bg-pink-900/20"></td>
                          </tr>
                          {availabilityData.members
                            .filter(m => m.gender === 'female')
                            .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
                            .map((member) => (
                              <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 border-t dark:border-gray-700">
                                <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r dark:border-gray-600 min-w-[120px]">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="truncate">{getDisplayName(member)}</span>
                                    {member.ntrp_rating != null && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {member.ntrp_rating.toFixed(1)}
                                      </span>
                                    )}
                                    {isTennisRecordEnabled && member.dynamic_rating != null && (
                                      <span className="text-xs text-purple-700 dark:text-purple-300">
                                        {member.dynamic_rating.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {filteredMatches.map((match: { id: number; match_date: string; opponent_name: string; home_away?: string }) => {
                                  const status = availabilityData.availability[member.id]?.[match.id]?.status;
                                  const isCurrentUserAdmin = user?.role === 'ADMIN';
                                  const isCurrentUserCaptain = selectedTeam?.members.some(m => m.user_id === user?.id && m.is_captain);
                                  const isOwnAvailability = member.id === user?.id;
                                  const isPast = isMatchInPast(match.match_date);
                                  
                                  // Players can only edit their own availability, unless they're captain or admin
                                  const canEdit = (isCurrentUserAdmin || isCurrentUserCaptain || isOwnAvailability) && !isPast;
                                  const cellKey = `${match.id}-${member.id}`;
                                  
                                  return (
                                    <td
                                      key={match.id}
                                      ref={(el) => {
                                        if (el) {
                                          cellRefs.current.set(cellKey, el);
                                        } else {
                                          cellRefs.current.delete(cellKey);
                                        }
                                      }}
                                      className={`px-3 py-1.5 text-center border-l dark:border-gray-600 availability-cell relative ${isPast ? 'bg-gray-50 dark:bg-gray-700/50' : ''} ${canEdit ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                      onClick={() => canEdit && handleAvailabilityCellClick(match.id, member.id)}
                                      title={canEdit ? 'Click to set availability' : ''}
                                    >
                                      <div className="flex justify-center">
                                        {status === 'available' ? (
                                          <CheckCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-green-500'}`} />
                                        ) : status === 'unavailable' ? (
                                          <XCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-red-500'}`} />
                                        ) : status === 'maybe' ? (
                                          <AlertCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-yellow-500'}`} />
                                        ) : (
                                          <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                                        )}
                                      </div>

                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </>
                      )}

                      {/* Not Specified Section */}
                      {availabilityData.members.filter(m => m.gender !== 'male' && m.gender !== 'female').length > 0 && (
                        <>
                          <tr className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-[9]">
                            <td className="sticky left-0 z-20 px-4 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                              Not Specified
                            </td>
                            <td colSpan={filteredMatches.length} className="bg-gray-50 dark:bg-gray-800/50"></td>
                          </tr>
                          {availabilityData.members
                            .filter(m => m.gender !== 'male' && m.gender !== 'female')
                            .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
                            .map((member) => (
                              <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 border-t dark:border-gray-700">
                                <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r dark:border-gray-600 min-w-[120px]">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="truncate">{getDisplayName(member)}</span>
                                    {member.ntrp_rating != null && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {member.ntrp_rating.toFixed(1)}
                                      </span>
                                    )}
                                    {isTennisRecordEnabled && member.dynamic_rating != null && (
                                      <span className="text-xs text-purple-700 dark:text-purple-300">
                                        {member.dynamic_rating.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {filteredMatches.map((match: { id: number; match_date: string; opponent_name: string; home_away?: string }) => {
                                  const status = availabilityData.availability[member.id]?.[match.id]?.status;
                                  const isCurrentUserAdmin = user?.role === 'ADMIN';
                                  const isCurrentUserCaptain = selectedTeam?.members.some(m => m.user_id === user?.id && m.is_captain);
                                  const isOwnAvailability = member.id === user?.id;
                                  const isPast = isMatchInPast(match.match_date);
                                  
                                  // Players can only edit their own availability, unless they're captain or admin
                                  const canEdit = (isCurrentUserAdmin || isCurrentUserCaptain || isOwnAvailability) && !isPast;
                                  const isDropdownOpen = activeCell?.matchId === match.id && activeCell?.playerId === member.id;
                                  const cellKey = `${match.id}-${member.id}`;
                                  
                                  return (
                                    <td
                                      key={match.id}
                                      ref={(el) => {
                                        if (el) {
                                          cellRefs.current.set(cellKey, el);
                                        } else {
                                          cellRefs.current.delete(cellKey);
                                        }
                                      }}
                                      className={`px-3 py-1.5 text-center border-l dark:border-gray-600 availability-cell relative ${isPast ? 'bg-gray-50 dark:bg-gray-700/50' : ''} ${canEdit ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                      onClick={() => canEdit && handleAvailabilityCellClick(match.id, member.id)}
                                      title={canEdit ? 'Click to set availability' : ''}
                                    >
                                      <div className="flex justify-center">
                                        {status === 'available' ? (
                                          <CheckCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-green-500'}`} />
                                        ) : status === 'unavailable' ? (
                                          <XCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-red-500'}`} />
                                        ) : status === 'maybe' ? (
                                          <AlertCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-yellow-500'}`} />
                                        ) : (
                                          <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                                        )}
                                      </div>

                                      {/* Dropdown Menu */}
                                      {isDropdownOpen && canEdit && dropdownPosition && (
                                        <div 
                                          className="absolute z-20 w-48 md:w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
                                          style={dropdownPosition}
                                        >
                                          <div className="py-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAvailabilityChange(match.id, member.id, 'available', e);
                                              }}
                                              className="w-full px-4 py-3 md:py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 min-h-[44px] text-gray-900 dark:text-gray-100"
                                            >
                                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                              <span>Available</span>
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAvailabilityChange(match.id, member.id, 'maybe', e);
                                              }}
                                              className="w-full px-4 py-3 md:py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 min-h-[44px] text-gray-900 dark:text-gray-100"
                                            >
                                              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                                              <span>If Needed</span>
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAvailabilityChange(match.id, member.id, 'unavailable', e);
                                              }}
                                              className="w-full px-4 py-3 md:py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 min-h-[44px] text-gray-900 dark:text-gray-100"
                                            >
                                              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                              <span>Not Available</span>
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </>
                      )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
                })()}

              {/* Legend */}
              {availabilityData && availabilityData.matches.length > 0 && (() => {
                // Compute filtered matches to check if legend should be shown
                const filteredMatches = availabilityData.matches.filter((match) => {
                  if (availabilityMatchFilter === 'all') return true;
                  if (availabilityMatchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                  if (availabilityMatchFilter === 'past') return isMatchInPast(match.match_date);
                  return true;
                });
                return filteredMatches.length > 0;
              })() && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 md:p-6">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Legend</h3>
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-6 h-6 text-yellow-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">If Needed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-6 h-6 text-red-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Not Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">No Response</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Availability Dropdown Portal */}
              {activeCell && dropdownPosition && typeof document !== 'undefined' && createPortal(
                <div 
                  data-availability-dropdown
                  className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeCell) {
                          handleAvailabilityChange(activeCell.matchId, activeCell.playerId, 'available', e);
                        }
                      }}
                      className="w-full px-4 py-3 md:py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 min-h-[44px] text-gray-900 dark:text-gray-100"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>Available</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeCell) {
                          handleAvailabilityChange(activeCell.matchId, activeCell.playerId, 'maybe', e);
                        }
                      }}
                      className="w-full px-4 py-3 md:py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 min-h-[44px] text-gray-900 dark:text-gray-100"
                    >
                      <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                      <span>If Needed</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeCell) {
                          handleAvailabilityChange(activeCell.matchId, activeCell.playerId, 'unavailable', e);
                        }
                      }}
                      className="w-full px-4 py-3 md:py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 min-h-[44px] text-gray-900 dark:text-gray-100"
                    >
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span>Not Available</span>
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}

          {/* Strategize Tab */}
          {activeTab === 'strategize' && selectedTeam && (
            <div className="space-y-4 md:space-y-6">
              {/* Header Section */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <WandSparkles className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">Lineups</h2>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Match Filter - First */}
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                      value={matchFilter}
                      onChange={(e) => setMatchFilter(e.target.value as 'all' | 'upcoming' | 'past')}
                      className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] min-w-[140px]"
                    >
                      <option value="all">All Matches</option>
                      <option value="upcoming">Upcoming Only</option>
                      <option value="past">Past Only</option>
                    </select>
                  </div>
                  
                  {/* Stats Panel Toggle - Second */}
                  <Button
                    onClick={() => setIsStatsPanelOpen(!isStatsPanelOpen)}
                    variant="outline"
                    size="md"
                    className="hidden md:flex min-h-[44px] w-full sm:w-auto"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {isStatsPanelOpen ? 'Hide Stats' : 'Show Stats'}
                  </Button>
                  
                  {/* Stats Button - Mobile */}
                  <Button
                    onClick={() => setShowMobileStatsModal(true)}
                    variant="outline"
                    size="md"
                    className="md:hidden min-h-[44px] w-full sm:w-auto"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Stats
                  </Button>
                  
                  {/* Actions Dropdown Menu - Third */}
                  {canManageTeam && (
                    <>
                      {/* Desktop Actions Menu */}
                      <div className="relative actions-menu-container hidden md:block">
                        <Button
                          onClick={() => setIsActionsMenuOpen(!isActionsMenuOpen)}
                          disabled={strategizeLoading}
                          variant="primary"
                          size="md"
                          className="flex items-center gap-2 min-h-[44px] w-full sm:w-auto"
                        >
                          Actions
                          <ChevronDown className={`w-4 h-4 transition-transform ${isActionsMenuOpen ? 'rotate-180' : ''}`} />
                        </Button>
                        
                        {/* Desktop Dropdown Menu */}
                        {isActionsMenuOpen && (
                          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                            <div className="py-1">
                              {/* Suggest Lineups */}
                              <button
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  setShowSuggestLineupsModal(true);
                                }}
                                disabled={suggestingLineups || strategizeLoading}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                              >
                                {suggestingLineups ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 dark:border-green-400"></div>
                                ) : (
                                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                  </svg>
                                )}
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {suggestingLineups ? 'Suggesting...' : 'Suggest Lineups'}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Auto-fill empty positions based on availability</div>
                                </div>
                              </button>

                              {/* Fetch Competitive Intelligence - only show if URL is set and feature is enabled */}
                              {isTennisRecordEnabled && tennisRecordUrl && (
                                <>
                                  {/* Divider */}
                                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                  
                                  <button
                                    onClick={() => {
                                      setIsActionsMenuOpen(false);
                                      handleFetchIntelligence();
                                    }}
                                    disabled={fetchingIntelligence || strategizeLoading}
                                    className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                                  >
                                    {fetchingIntelligence ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 dark:border-purple-400"></div>
                                    ) : (
                                      <Brain className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                    )}
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {fetchingIntelligence ? 'Fetching Intelligence...' : 'Fetch Competitive Intelligence'}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">Update player ratings from TennisRecord.com</div>
                                    </div>
                                  </button>
                                </>
                              )}
                              
                              {/* Divider */}
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                              
                              {/* Clear All Lineups */}
                              <button
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  setShowClearLineupsModal(true);
                                }}
                                disabled={strategizeLoading}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                              >
                                <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">Clear All Lineups</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Remove all players from upcoming matches</div>
                                </div>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Mobile Actions Menu Button */}
                      <div className="md:hidden relative w-full sm:w-auto">
                        <Button
                          onClick={() => setIsActionsMenuOpen(!isActionsMenuOpen)}
                          disabled={strategizeLoading}
                          variant="primary"
                          size="md"
                          className="flex items-center gap-2 min-h-[44px] w-full sm:w-auto"
                        >
                          Actions
                          <ChevronDown className={`w-4 h-4 transition-transform ${isActionsMenuOpen ? 'rotate-180' : ''}`} />
                        </Button>
                      
                        {/* Mobile Actions Dropdown */}
                        {isActionsMenuOpen && (
                          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                            <div className="py-1">
                              {/* Save Button in Mobile Menu */}
                              <button
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  handleSaveAllChanges();
                                }}
                                disabled={!hasUnsavedChanges || saving}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                              >
                                {saving ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400"></div>
                                ) : (
                                  <Save className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                )}
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {saving ? 'Saving...' : 'Save All Changes'}
                                  </div>
                                  {hasUnsavedChanges && !saving && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">You have unsaved changes</div>
                                  )}
                                </div>
                              </button>
                              
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                              
                              {/* Suggest Lineups */}
                              <button
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  setShowSuggestLineupsModal(true);
                                }}
                                disabled={suggestingLineups || strategizeLoading}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                              >
                                {suggestingLineups ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 dark:border-green-400"></div>
                                ) : (
                                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                  </svg>
                                )}
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {suggestingLineups ? 'Suggesting...' : 'Suggest Lineups'}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Auto-fill empty positions</div>
                                </div>
                              </button>

                              {/* Fetch Competitive Intelligence */}
                              {isTennisRecordEnabled && tennisRecordUrl && (
                                <>
                                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                  <button
                                    onClick={() => {
                                      setIsActionsMenuOpen(false);
                                      handleFetchIntelligence();
                                    }}
                                    disabled={fetchingIntelligence || strategizeLoading}
                                    className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                                  >
                                    {fetchingIntelligence ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 dark:border-purple-400"></div>
                                    ) : (
                                      <Brain className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                    )}
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {fetchingIntelligence ? 'Fetching...' : 'Fetch Intelligence'}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">Update player ratings</div>
                                    </div>
                                  </button>
                                </>
                              )}
                              
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                              
                              {/* Clear All Lineups */}
                              <button
                                onClick={() => {
                                  setIsActionsMenuOpen(false);
                                  setShowClearLineupsModal(true);
                                }}
                                disabled={strategizeLoading}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 min-h-[44px]"
                              >
                                <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">Clear All Lineups</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Remove all players</div>
                                </div>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* Save Button - Desktop - Fourth */}
                  {canManageTeam && (
                    <Button
                      onClick={handleSaveAllChanges}
                      disabled={!hasUnsavedChanges || saving}
                      variant="primary"
                      size="md"
                      className="hidden md:flex min-h-[44px] w-full sm:w-auto"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {hasUnsavedChanges && (
                            <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                          )}
                          Save All Changes
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Main Content Area - Table and Stats Panel */}
              <div className="flex gap-4">
                {/* Main Content */}
                <div className="flex-1 min-w-0">

              {strategizeLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
                </div>
              ) : !strategizeData ? (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400">Failed to load strategize data.</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Check the browser console for errors.</p>
                </div>
              ) : strategizeData.matches.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400">No matches scheduled yet.</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Add matches to use the strategize matrix.</p>
                </div>
              ) : (
                <>
                  <style>{`
                    .strategize-pattern-bg {
                      background: repeating-linear-gradient(45deg, #f9fafb, #f9fafb 10px, #ffffff 10px, #ffffff 20px);
                    }
                    .dark .strategize-pattern-bg {
                      background: repeating-linear-gradient(45deg, #1f2937, #1f2937 10px, #111827 10px, #111827 20px);
                    }
                  `}</style>
                  {/* Desktop Table View */}
                  <div 
                    className="hidden md:block shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden strategize-pattern-bg"
                  >
                    <div 
                      className="overflow-x-auto overflow-y-visible min-h-[400px] strategize-pattern-bg"
                    >
                        <table style={{ width: 'max-content', tableLayout: 'fixed', background: 'transparent' }} className="bg-transparent">
                          <colgroup>
                            <col style={{ width: '280px' }} />
                            {strategizeData.matches
                              .filter(match => {
                                if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                return true;
                              })
                              .map(match => (
                                <col key={match.id} style={{ width: '225px' }} />
                              ))}
                          </colgroup>

                          {/* Section 1: Match Schedule Header */}
                          <thead className="bg-gray-50/80 dark:bg-gray-800/50">
                            <tr>
                              <th className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-700 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-r dark:border-gray-600">
                                Match Details
                              </th>
                              {strategizeData.matches
                                .filter(match => {
                                  if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                  if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                  return true;
                                })
                                .map(match => {
                                  const isPast = isMatchInPast(match.match_date);
                                  return (
                                    <th
                                      key={match.id}
                                      className={`px-3 py-3 text-left border-b border-l border-gray-200 dark:border-gray-600 relative ${
                                        isPast ? 'bg-gray-100 dark:bg-gray-700/50 opacity-60' : 'bg-gray-50 dark:bg-gray-700'
                                      }`}
                                      onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setMatchTooltipPosition({
                                          top: rect.bottom + 8,
                                          left: rect.left + rect.width / 2,
                                          match: match
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMatchTooltipPosition(null);
                                      }}
                                    >
                                      <div className="flex flex-col gap-2">
                                        {/* Opponent Name */}
                                        <h4 className={`text-sm font-medium break-words ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                          {match.opponent_name}
                                        </h4>
                                        
                                        {/* Date/Time */}
                                        {(() => {
                                          const formatted = formatMatchDateCompact(match.match_date, false);
                                          const textClass = isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300';
                                          return (
                                            <div className="space-y-1">
                                              <div className={`flex items-center gap-1.5 text-xs ${textClass}`}>
                                                <Calendar className="w-3 h-3 flex-shrink-0" />
                                                <span>{formatted.date}</span>
                                              </div>
                                              <div className={`flex items-center gap-1.5 text-xs ${textClass}`}>
                                                <Clock className="w-3 h-3 flex-shrink-0" />
                                                <span>{formatted.time}</span>
                                              </div>
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Location */}
                                        {match.location && (
                                          <div className={`flex items-center gap-1.5 text-xs ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                                            <MapPin className="w-3 h-3 flex-shrink-0" />
                                            <span className="break-words">
                                              {match.location}
                                            </span>
                                          </div>
                                        )}
                                        
                                        {/* Bottom row: Home/Away and Past indicators */}
                                        <div className="flex items-center gap-2 flex-wrap mt-auto">
                                          {match.home_away && (
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                                              match.home_away === 'home' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                            }`}>
                                              {match.home_away === 'home' ? 'H' : 'A'}
                                            </span>
                                          )}
                                          {isPast && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 flex-shrink-0">
                                              Past
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </th>
                                  );
                                })}
                            </tr>
                          </thead>

                          {/* Section 2: Competitive Intelligence */}
                          {isTennisRecordEnabled && competitiveIntel && (() => {
                            const teamAvg = getTeamAverageDynamicRating();
                            const teamRange = getTeamDynamicRatingRange();
                            return (
                            <tbody className="bg-white dark:bg-gray-800">
                              <tr className="bg-gray-50 dark:bg-purple-950 border-t-2 dark:border-gray-700">
                                <td className="sticky left-0 z-10 bg-gray-50 dark:bg-purple-950 px-4 py-3 text-sm font-semibold text-purple-900 dark:text-purple-200 border-r dark:border-gray-600">
                                  <div className="flex flex-col">
                                    <span>Competitive Intelligence</span>
                                    {teamAvg !== null ? (
                                      <>
                                        <span className="text-xs font-normal text-purple-700 dark:text-purple-300 mt-0.5">
                                          Team Avg: {teamAvg.toFixed(2)}
                                        </span>
                                        {teamRange.min !== null && teamRange.max !== null ? (
                                          <span className="text-xs font-normal text-purple-600 dark:text-purple-400 mt-0.5">
                                            Range: {teamRange.min.toFixed(2)} - {teamRange.max.toFixed(2)}
                                          </span>
                                        ) : (
                                          <span className="text-xs font-normal text-purple-600 dark:text-purple-400 mt-0.5">
                                            Range: N/A
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-xs font-normal text-purple-600 dark:text-purple-400 mt-0.5">
                                        Team Avg: N/A
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {strategizeData.matches
                                  .filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  })
                                  .map(match => {
                                    const oppStats = getOpponentStats(match.opponent_name);
                                    const isHovered = hoveredOpponentCell === match.id;
                                    const isPast = isMatchInPast(match.match_date);

                                    // Forecast (future): compare team avg vs opp team avg.
                                    // Actual (past, when available): use averaged per-lineup difficulty from
                                    // the actual lineup that was played.
                                    const oppAvg = oppStats?.avg_dynamic_rating ? parseFloat(oppStats.avg_dynamic_rating) : null;
                                    const actual = match.actual_difficulty ?? null;
                                    const useActual = isPast && actual !== null;
                                    const gaugeTeamAvg = useActual ? actual.team_avg_rating : teamAvg;
                                    const gaugeOppAvg = useActual ? actual.opponent_avg_rating : oppAvg;
                                    const ratingDiff = useActual
                                      ? actual.rating_diff
                                      : (teamAvg !== null && oppAvg !== null ? oppAvg - teamAvg : null);
                                    const diffColorClass = useActual
                                      ? getRatingDifferenceColor(actual.team_avg_rating, actual.opponent_avg_rating)
                                      : getRatingDifferenceColor(teamAvg, oppAvg);

                                    // Forecasted = future match; Actual = past with computed difficulty;
                                    // Past matches without enough opponent rating data fall back to "Forecasted"
                                    // (using team-avg vs opp-team-avg).
                                    const difficultyKind: 'forecasted' | 'actual' = useActual ? 'actual' : 'forecasted';

                                    // Format difference indicator
                                    let diffIndicator = '';
                                    if (ratingDiff !== null) {
                                      if (Math.abs(ratingDiff) < 0.01) {
                                        diffIndicator = '±0.00';
                                      } else if (ratingDiff > 0) {
                                        diffIndicator = `↑${ratingDiff.toFixed(2)}`;
                                      } else {
                                        diffIndicator = `↓${Math.abs(ratingDiff).toFixed(2)}`;
                                      }
                                    }

                                    // Lineup strength: average dynamic rating of the planned lineup for this
                                    // match, shown on forecasted cells (it replaces the old "Lineup Strength" row).
                                    const lineupAvg = getLineupAverageDynamicRating(match.id);
                                    const lineupVsOpp = lineupAvg !== null && oppAvg !== null ? lineupAvg - oppAvg : null;
                                    let lineupVsOppIndicator = '';
                                    let lineupVsOppColor = 'text-gray-500 dark:text-gray-400';
                                    if (lineupVsOpp !== null) {
                                      if (Math.abs(lineupVsOpp) < 0.01) {
                                        lineupVsOppIndicator = '±0.00';
                                      } else if (lineupVsOpp > 0) {
                                        lineupVsOppIndicator = `↑${lineupVsOpp.toFixed(2)}`;
                                        lineupVsOppColor = 'text-green-600 dark:text-green-400';
                                      } else {
                                        lineupVsOppIndicator = `↓${Math.abs(lineupVsOpp).toFixed(2)}`;
                                        lineupVsOppColor = 'text-orange-600 dark:text-orange-400';
                                      }
                                    }

                                    // Sort players by dynamic rating (highest first), then players without ratings
                                    const sortedPlayers = oppStats?.players ? [...oppStats.players].sort((a: any, b: any) => {
                                      const aRating = a.dynamic_rating ? parseFloat(a.dynamic_rating) : null;
                                      const bRating = b.dynamic_rating ? parseFloat(b.dynamic_rating) : null;

                                      // If both have ratings, sort descending
                                      if (aRating !== null && bRating !== null) {
                                        return bRating - aRating;
                                      }
                                      // Players with ratings come before players without
                                      if (aRating !== null) return -1;
                                      if (bRating !== null) return 1;
                                      // Both don't have ratings, maintain order
                                      return 0;
                                    }) : [];

                                    return (
                                      <td
                                        key={match.id}
                                        className="px-3 py-3 text-center border-l dark:border-gray-600 relative"
                                        onMouseEnter={() => setHoveredOpponentCell(match.id)}
                                        onMouseLeave={() => setHoveredOpponentCell(null)}
                                      >
                                        {oppStats ? (
                                          <>
                                            <div className="flex items-center justify-center gap-2 cursor-pointer leading-tight">
                                              <div className="flex flex-col text-left">
                                                <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                                  Avg: {oppStats.avg_dynamic_rating?.toFixed(2) || 'N/A'}
                                                  {diffIndicator && (
                                                    <span className={`ml-1 text-[10px] font-normal ${diffColorClass}`}>
                                                      ({diffIndicator})
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="text-[10px] text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                                  Range: {oppStats.min_dynamic_rating?.toFixed(2) || 'N/A'} - {oppStats.max_dynamic_rating?.toFixed(2) || 'N/A'}
                                                </div>
                                                {formatRecord(oppStats) && (
                                                  <div className="text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap flex items-center gap-1">
                                                    <span>Record: <span className="font-semibold">{formatRecord(oppStats)}</span></span>
                                                    {renderRecentForm(oppStats)}
                                                  </div>
                                                )}
                                                {difficultyKind === 'forecasted' && lineupAvg !== null && (
                                                  <div
                                                    className="text-[10px] font-medium text-primary-700 dark:text-primary-300 whitespace-nowrap"
                                                    title="Lineup strength — average dynamic rating of the planned lineup for this match"
                                                  >
                                                    Lineup: {lineupAvg.toFixed(2)}
                                                    {lineupVsOppIndicator && (
                                                      <span className={`ml-1 font-normal ${lineupVsOppColor}`}>
                                                        ({lineupVsOppIndicator})
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              <div className="flex flex-col items-center gap-0.5">
                                                <DifficultyGauge teamAvg={gaugeTeamAvg} opponentAvg={gaugeOppAvg} />
                                                <span
                                                  className={`text-[9px] uppercase tracking-wide font-semibold ${
                                                    difficultyKind === 'actual'
                                                      ? 'text-purple-700 dark:text-purple-300'
                                                      : 'text-gray-500 dark:text-gray-400'
                                                  }`}
                                                  title={
                                                    difficultyKind === 'actual'
                                                      ? 'Actual difficulty — computed from the lineup that was played'
                                                      : 'Forecasted difficulty — based on team and opponent averages'
                                                  }
                                                >
                                                  {difficultyKind === 'actual' ? 'Actual' : 'Forecasted'}
                                                </span>
                                              </div>
                                            </div>
                                            
                                            {/* Popup with player details */}
                                            {isHovered && sortedPlayers.length > 0 && (
                                              <div 
                                                className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 z-50 min-w-[320px] max-w-[400px]"
                                                style={{ 
                                                  animation: 'fadeIn 0.2s ease-in-out',
                                                }}
                                              >
                                                <div className="p-4">
                                                  <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{match.opponent_name}</h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{sortedPlayers.length} player{sortedPlayers.length !== 1 ? 's' : ''}</p>
                                                  </div>
                                                  <div className="max-h-[300px] overflow-y-auto">
                                                    <table className="w-full text-xs">
                                                      <thead className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                                        <tr>
                                                          <th className="text-left py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Player</th>
                                                          <th className="text-center py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">NTRP</th>
                                                          <th className="text-center py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Dynamic</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {sortedPlayers.map((player: any, idx: number) => {
                                                          const dynamicRating = player.dynamic_rating ? parseFloat(player.dynamic_rating) : null;
                                                          const ntrpRating = player.ntrp_rating ? parseFloat(player.ntrp_rating) : null;
                                                          
                                                          return (
                                                            <tr 
                                                              key={player.id || idx} 
                                                              className="border-b border-gray-100 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                                            >
                                                              <td className="py-2 px-2 text-left text-gray-900 dark:text-gray-100">{player.name}</td>
                                                              <td className="py-2 px-2 text-center text-gray-700 dark:text-gray-300">
                                                                {ntrpRating !== null ? ntrpRating.toFixed(1) : 'N/A'}
                                                              </td>
                                                              <td className="py-2 px-2 text-center font-medium text-purple-700 dark:text-purple-300">
                                                                {dynamicRating !== null ? dynamicRating.toFixed(2) : 'N/A'}
                                                              </td>
                                                            </tr>
                                                          );
                                                        })}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                                {/* Pointer arrow */}
                                                <div 
                                                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0"
                                                  style={{
                                                    borderLeft: '8px solid transparent',
                                                    borderRight: '8px solid transparent',
                                                    borderBottom: '8px solid rgb(31 41 55)',
                                                    filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))'
                                                  }}
                                                />
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <span className="text-xs text-gray-400 dark:text-gray-500">No data</span>
                                        )}
                                      </td>
                                    );
                                  })}
                              </tr>
                            </tbody>
                            );
                          })()}

                          {/* Section 3: Match Lineups */}
                          {strategizeData.lineup_format && strategizeData.lineup_format.positions.length > 0 && (
                            <tbody className="bg-white dark:bg-gray-800">
                              <tr className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-10">
                                <td className="sticky left-0 z-20 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-t-2 border-r dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                                  <div className="flex items-center justify-between gap-2">
                                    <span>Match Lineups</span>
                                    <button
                                      onClick={() => setIsLineupsSectionCollapsed(!isLineupsSectionCollapsed)}
                                      className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 px-3 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 flex-shrink-0"
                                    >
                                      {isLineupsSectionCollapsed ? 'Show' : 'Hide'}
                                    </button>
                                  </div>
                                </td>
                                {strategizeData.matches
                                  .filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  })
                                  .map(match => {
                                    const isPast = isMatchInPast(match.match_date);
                                    return (
                                      <td key={match.id} className="border-t-2 border-l dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-3 py-2">
                                        {/* Conditional button: Enter Results for past, Publish/Unpublish for future */}
                                        {canManageTeam && (() => {
                                          const matchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                          const hasLineup = matchLineup.length > 0;
                                          const isPublished = match.lineup_published === true;
                                          
                                          if (isPast) {
                                            // Past matches: show Enter Results button
                                            return (
                                              <div className="flex justify-center">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Convert StrategizeMatch to Match format
                                                    const matchForModal: Match = {
                                                      ...match,
                                                      team_id: selectedTeam?.id || 0,
                                                      created_at: new Date().toISOString()
                                                    };
                                                    handleOpenResultsModal(matchForModal);
                                                  }}
                                                  className="px-1.5 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded transition-colors"
                                                  title={match.match_completed ? "Edit match results" : "Enter match results"}
                                                >
                                                  Enter Results
                                                </button>
                                              </div>
                                            );
                                          } else if (hasLineup && !isPublished) {
                                            // Future matches with lineup but not published: show Publish button
                                            return (
                                              <div className="flex justify-center">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPublishDialogMatchId(match.id);
                                                    setPublishDialogIsResend(false);
                                                  }}
                                                  disabled={hasUnsavedChanges}
                                                  className="px-1.5 py-0.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-primary-600 dark:disabled:hover:text-primary-400"
                                                  title={hasUnsavedChanges ? "Please save all changes before publishing the lineup" : "Publish lineup"}
                                                >
                                                  Publish Lineup
                                                </button>
                                              </div>
                                            );
                                          } else if (hasLineup && isPublished) {
                                            // Future matches with lineup and published: show Unpublish, Resend Email, and Copy Text buttons
                                            return (
                                              <div className="flex gap-1 items-center justify-center flex-wrap">
                                                <button
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                      await axios.put(`/lineups/matches/${match.id}/unpublish`);
                                                      // Refresh strategize data
                                                      if (selectedTeam) {
                                                        await fetchStrategizeData(selectedTeam.id);
                                                      }
                                                    } catch (error: any) {
                                                      console.error('Failed to unpublish lineup:', error);
                                                      alert(error.response?.data?.detail || 'Failed to unpublish lineup. Please try again.');
                                                    }
                                                  }}
                                                  className="px-1.5 py-0.5 text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 border border-orange-200 dark:border-orange-600 rounded transition-colors"
                                                  title="Unpublish lineup"
                                                >
                                                  Unpublish
                                                </button>
                                                {isLineupEmailEnabled && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPublishDialogMatchId(match.id);
                                                        setPublishDialogIsResend(true);
                                                      }}
                                                      className="px-1.5 py-0.5 text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 border border-green-200 dark:border-green-600 rounded transition-colors"
                                                      title="Resend email"
                                                    >
                                                      <Mail className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                      onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                          await handleCopyLineupText(match.id);
                                                        } catch (error) {
                                                          // Error already handled in function
                                                        }
                                                      }}
                                                      className="px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/20 border border-gray-200 dark:border-gray-600 rounded transition-colors"
                                                      title="Copy lineup text"
                                                    >
                                                      <Copy className="w-3 h-3" />
                                                    </button>
                                                  </>
                                                )}
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`/matches/${match.id}/scorecard`, '_blank');
                                                  }}
                                                  className="px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/20 border border-gray-200 dark:border-gray-600 rounded transition-colors"
                                                  title="Print score card"
                                                >
                                                  <Printer className="w-3 h-3" />
                                                </button>
                                              </div>
                                            );
                                          }
                                          // No lineup: don't show button
                                          return null;
                                        })()}
                                      </td>
                                    );
                                  })}
                              </tr>
                              {!isLineupsSectionCollapsed && (
                                <>
                              {strategizeData.lineup_format.positions.map((formatPos, positionIndex) => {
                                const isSingles = isSinglesPosition(formatPos.match_type);
                                
                                // Count how many positions have the same match type
                                const allPositions = strategizeData.lineup_format?.positions || [];
                                const matchTypeCount = allPositions.filter(
                                  p => p.match_type === formatPos.match_type
                                ).length;
                                
                                // Determine the index within positions of the same match type
                                const sameTypePositions = allPositions.filter(
                                  p => p.match_type === formatPos.match_type
                                );
                                const positionIndexWithinType = sameTypePositions.findIndex(
                                  p => p.position_number === formatPos.position_number
                                ) + 1;
                                
                                // Create the display label
                                const formattedType = formatMatchType(formatPos.match_type);
                                const displayLabel = matchTypeCount > 1 
                                  ? `${formattedType} ${positionIndexWithinType}`
                                  : formattedType;
                                
                                // Zebra striping: even positions get subtle background (using solid colors to prevent transparency issues)
                                const zebraClass = positionIndex % 2 === 0 ? 'bg-gray-50 dark:bg-[#2b3442]' : '';
                                
                                return (
                                  <React.Fragment key={formatPos.position_number}>
                                    {/* Player 1 Row */}
                                    <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700 transition-colors ${zebraClass}`}>
                                      <td className={`sticky left-0 z-10 px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 whitespace-nowrap ${zebraClass || 'bg-white dark:bg-gray-800'}`}>
                                        <span className="inline-flex items-center gap-1.5">
                                          <MatchTypeIcon matchType={formatPos.match_type} iconSize="w-4 h-4" />
                                          {displayLabel}
                                        </span>
                                      </td>
                                      {strategizeData.matches
                                        .filter(match => {
                                          if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                          if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                          return true;
                                        })
                                        .map(match => {
                                          const matchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                          const position = matchLineup.find(p => p.position_number === formatPos.position_number);
                                          const player1Id = position?.player1_id || 0;
                                          // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                          const player2Id = position?.player2_id || 0;
                                          const isPast = isMatchInPast(match.match_date);
                                          
                                          // Get original position for confirmations (confirmations don't change when editing)
                                          const originalPosition = strategizeData.lineups[match.id]?.find(
                                            p => p.position_number === formatPos.position_number
                                          );
                                          
                                          // Get player1 confirmation details
                                          const player1Confirmation = originalPosition?.confirmations?.find(
                                            (conf: LineupConfirmation) => conf.player_id === player1Id
                                          );
                                          const player1Confirmed = !!player1Confirmation;
                                          
                                          // Get confirmer name
                                          const player1ConfirmerName = player1Confirmation 
                                            ? getMemberName(player1Confirmation.confirmed_by) || 'Unknown'
                                            : '';
                                          
                                          // Format confirmation time using team timezone
                                          // Backend stores confirmed_at in UTC, so we need to parse it as UTC and convert to team timezone
                                          const teamTimezone = selectedTeam?.timezone || 'America/Los_Angeles';
                                          const player1ConfirmedTime = player1Confirmation?.confirmed_at
                                            ? (() => {
                                                // Parse the ISO string - if it doesn't have timezone, assume UTC
                                                let dateStr = player1Confirmation.confirmed_at;
                                                // If the string doesn't end with Z or timezone offset, append Z to indicate UTC
                                                if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
                                                  dateStr = dateStr + 'Z';
                                                }
                                                const date = new Date(dateStr);
                                                return date.toLocaleString('en-US', {
                                                  month: 'short',
                                                  day: 'numeric',
                                                  hour: 'numeric',
                                                  minute: '2-digit',
                                                  timeZone: teamTimezone
                                                });
                                              })()
                                            : '';
                                          
                                          // Check if lineup is published (to show confirmation status)
                                          const isLineupPublished = match.lineup_published === true;
                                          
                                          // Get eligible players using new filtering utilities
                                          const currentMatchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                          const validationLineup: ValidationLineupPosition[] = currentMatchLineup.map(pos => ({
                                            position_number: pos.position_number,
                                            player1_id: pos.player1_id || 0,
                                            player2_id: pos.player2_id || null
                                          }));
                                          
                                          const players = strategizeData.members.map(m => ({ 
                                            id: m.id, 
                                            name: m.name, 
                                            gender: m.gender || null,
                                            ntrp_rating: m.ntrp_rating || null,
                                            dynamic_rating: m.dynamic_rating || null
                                          }));
                                          const lineupFormatForValidation: ValidationLineupFormat = {
                                            total_lines: strategizeData.lineup_format?.total_lines || 0,
                                            max_sets_per_line: strategizeData.lineup_format?.max_sets_per_line || 0,
                                            allow_multi_line_selection: strategizeData.lineup_format?.allow_multi_line_selection || false,
                                            positions: strategizeData.lineup_format?.positions.map(p => ({
                                              position_number: p.position_number,
                                              match_type: p.match_type
                                            })) || []
                                          };
                                          
                                          const { eligible, ineligible } = filterPlayer1Options(
                                            players,
                                            formatPos.match_type,
                                            validationLineup,
                                            lineupFormatForValidation,
                                            formatPos.position_number,
                                            player2Id && player2Id > 0 ? player2Id : undefined,
                                            selectedTeam?.max_combined_ntrp_rating || null
                                          );
                                          
                                          // Filter out gender-ineligible players (they should remain hidden)
                                          const ineligibleNonGender = ineligible.filter(
                                            ({ reason }) => {
                                              const lowerReason = reason.toLowerCase();
                                              return !lowerReason.includes('must be male') && 
                                                     !lowerReason.includes('must be female') && 
                                                     !lowerReason.includes('different gender') &&
                                                     !lowerReason.includes('for mens_') &&
                                                     !lowerReason.includes('for womens_') &&
                                                     !lowerReason.includes('for mixed_doubles');
                                            }
                                          );
                                          
                                          // Convert eligible players back to StrategizeMember format
                                          const eligibleMembers = eligible.map(eligiblePlayer => 
                                            strategizeData.members.find(m => m.id === eligiblePlayer.id)!
                                          ).filter(Boolean);
                                          
                                          // Sort ineligible players alphabetically
                                          const sortedIneligiblePlayers = ineligibleNonGender
                                            .map(({ player, reason }) => ({
                                              player: strategizeData.members.find(m => m.id === player.id)!,
                                              reason
                                            }))
                                            .filter(({ player }) => player)
                                            .sort((a, b) => getDisplayName(a.player).localeCompare(getDisplayName(b.player)));
                                          
                                          // Sort eligible players by availability
                                          const availablePlayers = sortPlayersByAvailabilityForMatch(
                                            eligibleMembers,
                                            match.id
                                          );
                                          
                                          const isChanged = isLineupPositionChanged(match.id, formatPos.position_number, 'player1');
                                          const isUnfilled = !isPast && isPositionUnfilled(match.id, formatPos.position_number, 'player1') && player1Id === 0;
                                          const fieldErrors = getErrorsForPosition(match.id, formatPos.position_number, 'player1_id');
                                          const hasError = fieldErrors.length > 0;
                                          
                                          // Determine background color: past (gray) > changed (yellow) > unfilled (pink) > error (red) > normal (white)
                                          const bgColor = isPast ? 'bg-gray-50 dark:bg-gray-700/50' : isChanged ? 'bg-yellow-100 dark:bg-yellow-900/30' : isUnfilled ? 'bg-red-50 dark:bg-red-900/30' : hasError ? 'bg-red-50 dark:bg-red-900/30' : '';
                                          
                                          return (
                                            <td key={match.id} className={`px-2 py-2 border-l border-gray-200 dark:border-gray-600 transition-colors ${bgColor}`}>
                                              {canManageTeam && !isPast ? (
                                                <div>
                                                  {isLineupPublished ? (
                                                    <div className="flex items-center gap-1.5">
                                                      <div className="flex-1 min-w-0">
                                                        <PlayerDropdown
                                                          value={player1Id || null}
                                                          onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player1_id', playerId || 0)}
                                                          eligiblePlayers={availablePlayers}
                                                          ineligiblePlayers={sortedIneligiblePlayers}
                                                          matchId={match.id}
                                                          getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                          getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                          hasError={hasError}
                                                          isChanged={isChanged}
                                                          isUnfilled={isUnfilled}
                                                          matchType={formatPos.match_type}
                                                        />
                                                      </div>
                                                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                                        {player1Id > 0 && originalPosition && (
                                                          player1Confirmed ? (
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const playerName = getMemberName(player1Id) || 'Player';
                                                                handleConfirmationToggle(
                                                                  match.id,
                                                                  originalPosition.id,
                                                                  player1Id,
                                                                  playerName,
                                                                  true
                                                                );
                                                              }}
                                                              title={`Confirmed by ${player1ConfirmerName} on ${player1ConfirmedTime}. Click to unconfirm.`}
                                                              className="inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                            >
                                                              <Check className="w-4 h-4 text-green-500" />
                                                            </button>
                                                          ) : (
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const playerName = getMemberName(player1Id) || 'Player';
                                                                handleConfirmationToggle(
                                                                  match.id,
                                                                  originalPosition.id,
                                                                  player1Id,
                                                                  playerName,
                                                                  false
                                                                );
                                                              }}
                                                              title="Not confirmed. Click to confirm."
                                                              className="inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                            >
                                                              <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                            </button>
                                                          )
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <PlayerDropdown
                                                      value={player1Id || null}
                                                      onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player1_id', playerId || 0)}
                                                      eligiblePlayers={availablePlayers}
                                                      ineligiblePlayers={sortedIneligiblePlayers}
                                                      matchId={match.id}
                                                      getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                      getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                      hasError={hasError}
                                                      isChanged={isChanged}
                                                      isUnfilled={isUnfilled}
                                                      matchType={formatPos.match_type}
                                                    />
                                                  )}
                                                  {hasError && (
                                                    <div className="mt-0.5">
                                                      {fieldErrors.map((err, idx) => (
                                                        <div key={idx} className="text-xs text-red-600 dark:text-red-400">
                                                          {err.message}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 truncate" title={getMemberName(player1Id)}>
                                                  {player1Id > 0 ? (
                                                    <>
                                                      <span className="truncate">{getMemberName(player1Id)}</span>
                                                      {isLineupPublished && player1Id > 0 && originalPosition && canManageTeam ? (
                                                        player1Confirmed ? (
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              const playerName = getMemberName(player1Id) || 'Player';
                                                              handleConfirmationToggle(
                                                                match.id,
                                                                originalPosition.id,
                                                                player1Id,
                                                                playerName,
                                                                true
                                                              );
                                                            }}
                                                            title={`Confirmed by ${player1ConfirmerName} on ${player1ConfirmedTime}. Click to unconfirm.`}
                                                            className="inline-flex items-center ml-1.5 cursor-pointer hover:opacity-70 transition-opacity"
                                                          >
                                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                          </button>
                                                        ) : (
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              const playerName = getMemberName(player1Id) || 'Player';
                                                              handleConfirmationToggle(
                                                                match.id,
                                                                originalPosition.id,
                                                                player1Id,
                                                                playerName,
                                                                false
                                                              );
                                                            }}
                                                            title="Not confirmed. Click to confirm."
                                                            className="inline-flex items-center ml-1.5 cursor-pointer hover:opacity-70 transition-opacity"
                                                          >
                                                            <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0"></div>
                                                          </button>
                                                        )
                                                      ) : isLineupPublished && player1Id > 0 ? (
                                                        player1Confirmed ? (
                                                          <span 
                                                            title={`Confirmed by ${player1ConfirmerName} on ${player1ConfirmedTime}`}
                                                            className="inline-flex items-center ml-1.5"
                                                          >
                                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                          </span>
                                                        ) : (
                                                          <span title="Not confirmed" className="inline-flex items-center ml-1.5">
                                                            <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0"></div>
                                                          </span>
                                                        )
                                                      ) : null}
                                                    </>
                                                  ) : (
                                                    '-'
                                                  )}
                                                </div>
                                              )}
                                            </td>
                                          );
                                        })}
                                    </tr>
                                    
                                    {/* Player 2 Row (only for doubles) */}
                                    {!isSingles && (
                                      <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${zebraClass}`}>
                                        <td className={`sticky left-0 z-10 px-4 py-3 text-sm border-r border-gray-200 dark:border-gray-600 whitespace-nowrap ${zebraClass || 'bg-white dark:bg-gray-800'}`}>
                                          
                                        </td>
                                        {strategizeData.matches
                                          .filter(match => {
                                            if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                            if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                            return true;
                                          })
                                          .map(match => {
                                            const matchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                            const position = matchLineup.find(p => p.position_number === formatPos.position_number);
                                            const player1Id = position?.player1_id || 0;
                                            const player2Id = position?.player2_id || 0;
                                            const isPast = isMatchInPast(match.match_date);
                                            
                                            // Get original position for confirmations (confirmations don't change when editing)
                                            const originalPosition = strategizeData.lineups[match.id]?.find(
                                              p => p.position_number === formatPos.position_number
                                            );
                                            
                                            // Get player2 confirmation details
                                            const player2Confirmation = originalPosition?.confirmations?.find(
                                              (conf: LineupConfirmation) => conf.player_id === player2Id
                                            );
                                            const player2Confirmed = !!player2Confirmation;
                                            
                                            // Get confirmer name
                                            const player2ConfirmerName = player2Confirmation 
                                              ? getMemberName(player2Confirmation.confirmed_by) || 'Unknown'
                                              : '';
                                            
                                            // Format confirmation time using team timezone
                                            // Backend stores confirmed_at in UTC, so we need to parse it as UTC and convert to team timezone
                                            const teamTimezone = selectedTeam?.timezone || 'America/Los_Angeles';
                                            const player2ConfirmedTime = player2Confirmation?.confirmed_at
                                              ? (() => {
                                                  // Parse the ISO string - if it doesn't have timezone, assume UTC
                                                  let dateStr = player2Confirmation.confirmed_at;
                                                  // If the string doesn't end with Z or timezone offset, append Z to indicate UTC
                                                  if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
                                                    dateStr = dateStr + 'Z';
                                                  }
                                                  const date = new Date(dateStr);
                                                  return date.toLocaleString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit',
                                                    timeZone: teamTimezone
                                                  });
                                                })()
                                              : '';
                                            
                                            // Check if lineup is published (to show confirmation status)
                                            const isLineupPublished = match.lineup_published === true;
                                            
                                            // Get eligible players using new filtering utilities
                                            const currentMatchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                            const validationLineup: ValidationLineupPosition[] = currentMatchLineup.map(pos => ({
                                              position_number: pos.position_number,
                                              player1_id: pos.player1_id || 0,
                                              player2_id: pos.player2_id || null
                                            }));
                                            
                                            const players = strategizeData.members.map(m => ({ 
                                              id: m.id, 
                                              name: m.name, 
                                              gender: m.gender || null,
                                              ntrp_rating: m.ntrp_rating || null,
                                              dynamic_rating: m.dynamic_rating || null
                                            }));
                                            const lineupFormatForValidation: ValidationLineupFormat = {
                                              total_lines: strategizeData.lineup_format?.total_lines || 0,
                                              max_sets_per_line: strategizeData.lineup_format?.max_sets_per_line || 0,
                                              allow_multi_line_selection: strategizeData.lineup_format?.allow_multi_line_selection || false,
                                              positions: strategizeData.lineup_format?.positions.map(p => ({
                                                position_number: p.position_number,
                                                match_type: p.match_type
                                              })) || []
                                            };
                                            
                                            const { eligible, ineligible } = filterPlayer2Options(
                                              players,
                                              formatPos.match_type,
                                              validationLineup,
                                              lineupFormatForValidation,
                                              formatPos.position_number,
                                              player1Id || 0,
                                              selectedTeam?.max_combined_ntrp_rating || null
                                            );
                                            
                                            // Filter out gender-ineligible players (they should remain hidden)
                                            const ineligibleNonGender = ineligible.filter(
                                              ({ reason }) => {
                                                const lowerReason = reason.toLowerCase();
                                                return !lowerReason.includes('must be male') && 
                                                       !lowerReason.includes('must be female') && 
                                                       !lowerReason.includes('different gender') &&
                                                       !lowerReason.includes('for mens_') &&
                                                       !lowerReason.includes('for womens_') &&
                                                       !lowerReason.includes('for mixed_doubles');
                                              }
                                            );
                                            
                                            // Convert eligible players back to StrategizeMember format
                                            const eligibleMembers = eligible.map(eligiblePlayer => 
                                              strategizeData.members.find(m => m.id === eligiblePlayer.id)!
                                            ).filter(Boolean);
                                            
                                            // Sort ineligible players alphabetically
                                            const sortedIneligiblePlayers = ineligibleNonGender
                                              .map(({ player, reason }) => ({
                                                player: strategizeData.members.find(m => m.id === player.id)!,
                                                reason
                                              }))
                                              .filter(({ player }) => player)
                                              .sort((a, b) => getDisplayName(a.player).localeCompare(getDisplayName(b.player)));
                                            
                                            // Sort eligible players by availability
                                            const availablePlayers = sortPlayersByAvailabilityForMatch(
                                              eligibleMembers,
                                              match.id
                                            );
                                            
                                            const isChanged = isLineupPositionChanged(match.id, formatPos.position_number, 'player2');
                                            const isUnfilled = !isPast && isPositionUnfilled(match.id, formatPos.position_number, 'player2') && (!player2Id || player2Id === 0);
                                            const fieldErrors = getErrorsForPosition(match.id, formatPos.position_number, 'player2_id');
                                            const hasError = fieldErrors.length > 0;
                                            
                                            // Determine background color: past (gray) > changed (yellow) > unfilled (pink) > error (red) > normal (white)
                                            const bgColor = isPast ? 'bg-gray-50 dark:bg-gray-700/50' : isChanged ? 'bg-yellow-100 dark:bg-yellow-900/30' : isUnfilled ? 'bg-red-50 dark:bg-red-900/30' : hasError ? 'bg-red-50 dark:bg-red-900/30' : '';
                                            
                                            return (
                                              <td key={match.id} className={`px-2 py-2 border-l border-gray-200 dark:border-gray-600 transition-colors ${bgColor}`}>
                                                {canManageTeam && !isPast ? (
                                                  <div>
                                                    {isLineupPublished ? (
                                                      <div className="flex items-center gap-1.5">
                                                        <div className="flex-1 min-w-0">
                                                          <PlayerDropdown
                                                            value={player2Id || null}
                                                            onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player2_id', playerId || 0)}
                                                            eligiblePlayers={availablePlayers}
                                                            ineligiblePlayers={sortedIneligiblePlayers}
                                                            matchId={match.id}
                                                            getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                            getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                            hasError={hasError}
                                                            isChanged={isChanged}
                                                            isUnfilled={isUnfilled}
                                                            matchType={formatPos.match_type}
                                                          />
                                                        </div>
                                                        <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                                          {player2Id > 0 && originalPosition && (
                                                            player2Confirmed ? (
                                                              <button
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const playerName = getMemberName(player2Id) || 'Player';
                                                                  handleConfirmationToggle(
                                                                    match.id,
                                                                    originalPosition.id,
                                                                    player2Id,
                                                                    playerName,
                                                                    true
                                                                  );
                                                                }}
                                                                title={`Confirmed by ${player2ConfirmerName} on ${player2ConfirmedTime}. Click to unconfirm.`}
                                                                className="inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                              >
                                                                <Check className="w-4 h-4 text-green-500" />
                                                              </button>
                                                            ) : (
                                                              <button
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const playerName = getMemberName(player2Id) || 'Player';
                                                                  handleConfirmationToggle(
                                                                    match.id,
                                                                    originalPosition.id,
                                                                    player2Id,
                                                                    playerName,
                                                                    false
                                                                  );
                                                                }}
                                                                title="Not confirmed. Click to confirm."
                                                                className="inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                              >
                                                                <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                              </button>
                                                            )
                                                          )}
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <PlayerDropdown
                                                        value={player2Id || null}
                                                        onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player2_id', playerId || 0)}
                                                        eligiblePlayers={availablePlayers}
                                                        ineligiblePlayers={sortedIneligiblePlayers}
                                                        matchId={match.id}
                                                        getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                        getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                        hasError={hasError}
                                                        isChanged={isChanged}
                                                        isUnfilled={isUnfilled}
                                                        matchType={formatPos.match_type}
                                                      />
                                                    )}
                                                    {hasError && (
                                                      <div className="mt-0.5">
                                                        {fieldErrors.map((err, idx) => (
                                                          <div key={idx} className="text-xs text-red-600 dark:text-red-400">
                                                            {err.message}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 truncate" title={getMemberName(player2Id)}>
                                                    {player2Id && player2Id > 0 ? (
                                                      <>
                                                        <span className="truncate">{getMemberName(player2Id)}</span>
                                                        {isLineupPublished && player2Id > 0 && originalPosition && canManageTeam ? (
                                                          player2Confirmed ? (
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const playerName = getMemberName(player2Id) || 'Player';
                                                                handleConfirmationToggle(
                                                                  match.id,
                                                                  originalPosition.id,
                                                                  player2Id,
                                                                  playerName,
                                                                  true
                                                                );
                                                              }}
                                                              title={`Confirmed by ${player2ConfirmerName} on ${player2ConfirmedTime}. Click to unconfirm.`}
                                                              className="inline-flex items-center ml-1.5 cursor-pointer hover:opacity-70 transition-opacity"
                                                            >
                                                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                            </button>
                                                          ) : (
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const playerName = getMemberName(player2Id) || 'Player';
                                                                handleConfirmationToggle(
                                                                  match.id,
                                                                  originalPosition.id,
                                                                  player2Id,
                                                                  playerName,
                                                                  false
                                                                );
                                                              }}
                                                              title="Not confirmed. Click to confirm."
                                                              className="inline-flex items-center ml-1.5 cursor-pointer hover:opacity-70 transition-opacity"
                                                            >
                                                              <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0"></div>
                                                            </button>
                                                          )
                                                        ) : isLineupPublished && player2Id > 0 ? (
                                                          player2Confirmed ? (
                                                            <span 
                                                              title={`Confirmed by ${player2ConfirmerName} on ${player2ConfirmedTime}`}
                                                              className="inline-flex items-center ml-1.5"
                                                            >
                                                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                            </span>
                                                          ) : (
                                                            <span title="Not confirmed" className="inline-flex items-center ml-1.5">
                                                              <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0"></div>
                                                            </span>
                                                          )
                                                        ) : null}
                                                      </>
                                                    ) : (
                                                      '-'
                                                    )}
                                                  </div>
                                                )}
                                              </td>
                                            );
                                          })}
                                      </tr>
                                    )}
                                    
                                    {/* Match Result Row - Shows score if available */}
                                    {strategizeData.matches.some(match => {
                                      const matchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                      const position = matchLineup.find(p => p.position_number === formatPos.position_number);
                                      return position?.is_actual_result && position?.score;
                                    }) && (
                                      <tr className={zebraClass}>
                                        <td className={`sticky left-0 z-10 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-600 italic ${zebraClass || 'bg-white dark:bg-gray-800'}`}>
                                          Result
                                        </td>
                                        {strategizeData.matches
                                          .filter(match => {
                                            if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                            if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                            return true;
                                          })
                                          .map(match => {
                                            const matchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                            const position = matchLineup.find(p => p.position_number === formatPos.position_number);
                                            const isPast = isMatchInPast(match.match_date);
                                            const bgColor = isPast ? 'bg-gray-50 dark:bg-gray-700/50' : '';
                                            
                                            return (
                                              <td key={match.id} className={`relative px-2 py-2 pr-6 border-l dark:border-gray-600 text-left ${bgColor}`}>
                                                {position?.is_actual_result && position?.score ? (
                                                  (() => {
                                                    const isWon = position.won === true;
                                                    const isLost = position.won === false;
                                                    const scoreText = position.score;
                                                    const resultIcon = isWon ? '✓' : isLost ? '✗' : '';
                                                    const colorClass = isWon
                                                      ? 'text-green-600 dark:text-green-400'
                                                      : isLost
                                                      ? 'text-red-600 dark:text-red-400'
                                                      : 'text-gray-700 dark:text-gray-300';

                                                    const scoreEl = match.match_result_url ? (
                                                      <a
                                                        href={match.match_result_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`text-xs font-semibold hover:opacity-70 transition-opacity cursor-pointer ${colorClass}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                      >
                                                        {scoreText} {resultIcon}
                                                      </a>
                                                    ) : (
                                                      <div className={`text-xs font-semibold ${colorClass}`}>
                                                        {scoreText} {resultIcon}
                                                      </div>
                                                    );

                                                    return (
                                                      <>
                                                        {scoreEl}
                                                        {position.difficulty && (
                                                          <div className="absolute bottom-1.5 right-1.5 pointer-events-none">
                                                            <DifficultyGauge
                                                              teamAvg={position.difficulty.team_avg_rating}
                                                              opponentAvg={position.difficulty.opponent_avg_rating}
                                                              size="sm"
                                                              showLabel={false}
                                                              className="pointer-events-auto"
                                                            />
                                                          </div>
                                                        )}
                                                      </>
                                                    );
                                                  })()
                                                ) : (
                                                  <div className="text-xs text-gray-400 dark:text-gray-500">-</div>
                                                )}
                                              </td>
                                            );
                                          })}
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                                </>
                              )}
                            </tbody>
                          )}

                          {/* Section 4: Player Availability */}
                          <tbody className="bg-white dark:bg-gray-800">
                            <tr className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-10">
                              <td className="sticky left-0 z-20 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-t-2 border-r dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                                <div className="flex items-center justify-between gap-2">
                                  <span>Player Availability</span>
                                  <button
                                    onClick={() => setIsAvailabilitySectionCollapsed(!isAvailabilitySectionCollapsed)}
                                    className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 px-3 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 flex-shrink-0"
                                  >
                                    {isAvailabilitySectionCollapsed ? 'Show' : 'Hide'}
                                  </button>
                                </div>
                              </td>
                              {isAvailabilitySectionCollapsed ? (
                                // Show summary when collapsed
                                strategizeData.matches
                                  .filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  })
                                  .map(match => {
                                    // Count available players by gender for this match
                                    const availableMen = strategizeData.members.filter(m => 
                                      m.gender === 'male' && 
                                      (getPlayerAvailabilityForMatch(m.id, match.id) === 'available')
                                    ).length;
                                    
                                    const availableWomen = strategizeData.members.filter(m => 
                                      m.gender === 'female' && 
                                      (getPlayerAvailabilityForMatch(m.id, match.id) === 'available')
                                    ).length;
                                    
                                    const availableUnknown = strategizeData.members.filter(m => 
                                      (!m.gender || m.gender === null) && 
                                      (getPlayerAvailabilityForMatch(m.id, match.id) === 'available')
                                    ).length;
                                    
                                    // Format gender breakdown for display (matching match details page format)
                                    const formatGenderBreakdown = () => {
                                      const parts: string[] = [];
                                      if (availableMen > 0) {
                                        parts.push(`${availableMen} ${availableMen === 1 ? 'man' : 'men'}`);
                                      }
                                      if (availableWomen > 0) {
                                        parts.push(`${availableWomen} ${availableWomen === 1 ? 'woman' : 'women'}`);
                                      }
                                      if (availableUnknown > 0) {
                                        parts.push(`${availableUnknown} unspecified`);
                                      }
                                      return parts.length > 0 ? `(${parts.join(', ')})` : null;
                                    };
                                    
                                    return (
                                      <td key={match.id} className="border-t-2 border-l dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-3 py-2 text-center">
                                        {formatGenderBreakdown() ? (
                                          <div className="text-xs text-gray-600 dark:text-gray-300">
                                            {formatGenderBreakdown()}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-gray-400 dark:text-gray-500">-</div>
                                        )}
                                      </td>
                                    );
                                  })
                              ) : (
                                // Show empty cells when expanded (detailed view will be shown below)
                                strategizeData.matches
                                  .filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  })
                                  .map(match => (
                                    <td key={match.id} className="border-t-2 border-l dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-3 py-2"></td>
                                  ))
                              )}
                            </tr>

                            {!isAvailabilitySectionCollapsed && (
                              <>
                            {availabilityDisplayVariant === 'summary' ? (
                              // Summary View
                              <>
                                {['available', 'maybe', 'unavailable', 'not_set'].map((status) => {
                                  const statusLabel = status === 'not_set' ? 'Not Set' : status === 'maybe' ? 'If Needed' : status.charAt(0).toUpperCase() + status.slice(1);
                                  const statusIcon = status === 'available' ? CheckCircle : status === 'unavailable' ? XCircle : status === 'maybe' ? AlertCircle : HelpCircle;
                                  const StatusIcon = statusIcon;
                                  const statusColor = status === 'available' ? 'text-green-500' : status === 'unavailable' ? 'text-red-500' : status === 'maybe' ? 'text-yellow-500' : 'text-gray-400';
                                  
                                  const filteredMatches = strategizeData.matches.filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  });
                                  
                                  return (
                                    <tr key={status} className="bg-gray-50 dark:bg-gray-700">
                                      <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-b border-gray-200 dark:border-gray-600">
                                        <div className="flex items-center gap-2">
                                          <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                                          <span>{statusLabel}</span>
                                        </div>
                                      </td>
                                      {filteredMatches.map((match) => {
                                        const summary = getMatchAvailabilitySummary(match.id, strategizeData.members, strategizeData.availability);
                                        const matchSummary = summary[status] || { male: {}, female: {}, unspecified: {} };
                                        
                                        // Build summary with structured data for better rendering
                                        const genderSummaries: Array<{
                                          gender: string;
                                          genderLabel: string;
                                          total: number;
                                          ntrpBreakdown: Array<{ ntrp: string; count: number }>;
                                          colorClass: string;
                                        }> = [];
                                        
                                        // Process each gender
                                        ['male', 'female', 'unspecified'].forEach((gender) => {
                                          const genderLabel = gender === 'male' ? 'men' : gender === 'female' ? 'women' : 'unspecified';
                                          const genderData = matchSummary[gender] || {};
                                          
                                          // Group by NTRP rating
                                          const ntrpGroups: { [key: string]: number } = {};
                                          Object.entries(genderData).forEach(([ntrp, count]) => {
                                            const countNum = count as number;
                                            if (!ntrpGroups[ntrp]) {
                                              ntrpGroups[ntrp] = 0;
                                            }
                                            ntrpGroups[ntrp] += countNum;
                                          });
                                          
                                          // Build gender summary with NTRP breakdown showing count at each level
                                          const totalForGender = Object.values(ntrpGroups).reduce((sum, val) => sum + (val as number), 0);
                                          if (totalForGender > 0) {
                                            const ntrpBreakdown: Array<{ ntrp: string; count: number }> = [];
                                            Object.entries(ntrpGroups)
                                              .sort(([a], [b]) => {
                                                if (a === 'no_rating') return 1;
                                                if (b === 'no_rating') return -1;
                                                return parseFloat(a) - parseFloat(b);
                                              })
                                              .forEach(([ntrp, count]) => {
                                                const countNum = count as number;
                                                if (countNum > 0) {
                                                  ntrpBreakdown.push({
                                                    ntrp: ntrp === 'no_rating' ? 'no rating' : ntrp,
                                                    count: countNum
                                                  });
                                                }
                                              });
                                            
                                            const colorClass = gender === 'male' 
                                              ? 'text-primary-700 dark:text-primary-300' 
                                              : gender === 'female' 
                                              ? 'text-pink-700 dark:text-pink-300' 
                                              : 'text-gray-600 dark:text-gray-400';
                                            
                                            genderSummaries.push({
                                              gender,
                                              genderLabel,
                                              total: totalForGender,
                                              ntrpBreakdown,
                                              colorClass
                                            });
                                          }
                                        });
                                        
                                        const isPast = isMatchInPast(match.match_date);
                                        
                                        return (
                                          <td
                                            key={match.id}
                                            className={`px-3 py-2.5 text-xs border-l border-b border-gray-200 dark:border-gray-600 align-top ${isPast ? 'opacity-60' : ''}`}
                                          >
                                            {genderSummaries.length > 0 ? (
                                              <div className="flex flex-col gap-1.5">
                                                <div className="flex items-center gap-1.5 mb-0.5">
                                                  <StatusIcon className={`w-3.5 h-3.5 ${statusColor} flex-shrink-0`} />
                                                  <span className="text-gray-500 dark:text-gray-400 text-[10px] font-medium uppercase">{statusLabel}</span>
                                                </div>
                                                {genderSummaries.map((summary, idx) => (
                                                  <div key={idx} className="flex flex-col gap-1">
                                                    <div className={`font-semibold ${summary.colorClass}`}>
                                                      {summary.total} {summary.total === 1 
                                                        ? (summary.genderLabel === 'men' ? 'man' : summary.genderLabel === 'women' ? 'woman' : 'unspecified')
                                                        : summary.genderLabel}
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                      {summary.ntrpBreakdown.map((item, itemIdx) => (
                                                        <span
                                                          key={itemIdx}
                                                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                        >
                                                          {item.count} × {item.ntrp}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div className="flex flex-col gap-1.5">
                                                <div className="flex items-center gap-1.5">
                                                  <StatusIcon className={`w-3.5 h-3.5 ${statusColor} flex-shrink-0`} />
                                                  <span className="text-gray-500 dark:text-gray-400 text-[10px] font-medium uppercase">{statusLabel}</span>
                                                </div>
                                              </div>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </>
                            ) : (
                              // Detailed List View (Control)
                              <>
                            {/* Men Section */}
                            {strategizeData.members.filter(m => m.gender === 'male').length > 0 && (
                              <>
                                <tr className="bg-primary-50 dark:bg-primary-900/20 sticky top-0 z-[9]">
                                  <td className="sticky left-0 z-20 px-4 py-1.5 text-xs font-semibold text-primary-800 dark:text-primary-300 uppercase tracking-wider border-r dark:border-gray-600 bg-primary-50 dark:bg-primary-900/20">
                                    Men
                                  </td>
                                  <td colSpan={strategizeData.matches.filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  }).length} className="bg-primary-50 dark:bg-primary-900/20"></td>
                                </tr>
                                {strategizeData.members
                                  .filter(m => m.gender === 'male')
                                  .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
                                  .map(member => {
                              const canEditThisMember = canManageTeam || member.id === user?.id;
                              const playerRating = getPlayerRatings(member.id);
                              return (
                                <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 border-t dark:border-gray-700">
                                  <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r dark:border-gray-600">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span>{getDisplayName(member)}</span>
                                      {playerRating?.ntrp_rating && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {playerRating.ntrp_rating.toFixed(1)}
                                        </span>
                                      )}
                                      {isTennisRecordEnabled && playerRating?.dynamic_rating && (
                                        <span className="text-xs text-purple-700 dark:text-purple-300">
                                          {playerRating.dynamic_rating.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {strategizeData.matches
                                    .filter(match => {
                                      if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                      if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                      return true;
                                    })
                                    .map(match => {
                                      const key = `${match.id}_${member.id}`;
                                      const currentStatus = editedAvailability[key] || 
                                        strategizeData.availability[member.id]?.[match.id]?.status || 
                                        '';
                                      const isPast = isMatchInPast(match.match_date);
                                      
                                      const isDropdownOpen = activeStrategizeCell?.matchId === match.id && activeStrategizeCell?.playerId === member.id;
                                      
                                      return (
                                        <td 
                                          key={match.id} 
                                          className={`px-3 py-1.5 text-center border-l border-gray-200 dark:border-gray-600 scheduling-availability-cell relative transition-colors ${isPast ? 'bg-gray-50 dark:bg-gray-700/50' : ''} ${canEditThisMember && !isPast ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                          onClick={() => canEditThisMember && !isPast && setActiveStrategizeCell({ matchId: match.id, playerId: member.id })}
                                          title={canEditThisMember && !isPast ? 'Click to set availability' : ''}
                                        >
                                          <div className="flex justify-center">
                                            {currentStatus === 'available' ? (
                                              <CheckCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-green-500'}`} />
                                            ) : currentStatus === 'unavailable' ? (
                                              <XCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-red-500'}`} />
                                            ) : currentStatus === 'maybe' ? (
                                              <AlertCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-yellow-500'}`} />
                                            ) : (
                                              <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                                            )}
                                          </div>

                                          {/* Dropdown Menu */}
                                          {isDropdownOpen && canEditThisMember && !isPast && (
                                            <div className="absolute z-20 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 left-1/2 transform -translate-x-1/2">
                                              <div className="py-1">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'available', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                                >
                                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                                  <span>Available</span>
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'maybe', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                                >
                                                  <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                                                  <span>If Needed</span>
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'unavailable', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                                >
                                                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                                  <span>Not Available</span>
                                                </button>
                                                {currentStatus && (
                                                  <>
                                                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStrategizeAvailabilityChange(match.id, member.id, '', e);
                                                        setActiveStrategizeCell(null);
                                                      }}
                                                      className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-600 dark:text-gray-400"
                                                    >
                                                      <div className="w-5 h-5 rounded-full border-2 border-gray-400 dark:border-gray-500 flex items-center justify-center flex-shrink-0">
                                                        <svg className="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                      </div>
                                                      <span>Clear</span>
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </td>
                                      );
                                    })}
                                </tr>
                              );
                            })}
                              </>
                            )}

                            {/* Women Section */}
                            {strategizeData.members.filter(m => m.gender === 'female').length > 0 && (
                              <>
                                <tr className="bg-pink-50 dark:bg-pink-900/20 sticky top-0 z-[9]">
                                  <td className="sticky left-0 z-20 px-4 py-1.5 text-xs font-semibold text-pink-800 dark:text-pink-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 bg-pink-50 dark:bg-pink-900/20">
                                    Women
                                  </td>
                                  <td colSpan={strategizeData.matches.filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  }).length} className="bg-pink-50 dark:bg-pink-900/20"></td>
                                </tr>
                                {strategizeData.members
                                  .filter(m => m.gender === 'female')
                                  .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
                                  .map(member => {
                              const canEditThisMember = canManageTeam || member.id === user?.id;
                              const playerRating = getPlayerRatings(member.id);
                              return (
                                <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700 transition-colors">
                                  <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span>{getDisplayName(member)}</span>
                                      {playerRating?.ntrp_rating && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {playerRating.ntrp_rating.toFixed(1)}
                                        </span>
                                      )}
                                      {isTennisRecordEnabled && playerRating?.dynamic_rating && (
                                        <span className="text-xs text-purple-700 dark:text-purple-300">
                                          {playerRating.dynamic_rating.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {strategizeData.matches
                                    .filter(match => {
                                      if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                      if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                      return true;
                                    })
                                    .map(match => {
                                      const key = `${match.id}_${member.id}`;
                                      const currentStatus = editedAvailability[key] || 
                                        strategizeData.availability[member.id]?.[match.id]?.status || 
                                        '';
                                      const isPast = isMatchInPast(match.match_date);
                                      
                                      const isDropdownOpen = activeStrategizeCell?.matchId === match.id && activeStrategizeCell?.playerId === member.id;
                                      
                                      return (
                                        <td 
                                          key={match.id} 
                                          className={`px-3 py-1.5 text-center border-l border-gray-200 dark:border-gray-600 scheduling-availability-cell relative transition-colors ${isPast ? 'bg-gray-50 dark:bg-gray-700/50' : ''} ${canEditThisMember && !isPast ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                          onClick={() => canEditThisMember && !isPast && setActiveStrategizeCell({ matchId: match.id, playerId: member.id })}
                                          title={canEditThisMember && !isPast ? 'Click to set availability' : ''}
                                        >
                                          <div className="flex justify-center">
                                            {currentStatus === 'available' ? (
                                              <CheckCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-green-500'}`} />
                                            ) : currentStatus === 'unavailable' ? (
                                              <XCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-red-500'}`} />
                                            ) : currentStatus === 'maybe' ? (
                                              <AlertCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-yellow-500'}`} />
                                            ) : (
                                              <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                                            )}
                                          </div>

                                          {/* Dropdown Menu */}
                                          {isDropdownOpen && canEditThisMember && !isPast && (
                                            <div className="absolute z-20 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 left-1/2 transform -translate-x-1/2">
                                              <div className="py-1">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'available', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                                >
                                                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                  </div>
                                                  <span>Available</span>
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'maybe', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                                >
                                                  <div className="w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-white font-bold text-xs">?</span>
                                                  </div>
                                                  <span>If Needed</span>
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'unavailable', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                                >
                                                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                  </div>
                                                  <span>Not Available</span>
                                                </button>
                                                {currentStatus && (
                                                  <>
                                                    <div className="border-t border-gray-200 my-1"></div>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStrategizeAvailabilityChange(match.id, member.id, '', e);
                                                        setActiveStrategizeCell(null);
                                                      }}
                                                      className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex items-center gap-2 text-gray-600 transition-colors"
                                                    >
                                                      <div className="w-5 h-5 rounded-full border-2 border-gray-400 flex items-center justify-center flex-shrink-0">
                                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                      </div>
                                                      <span>Clear</span>
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </td>
                                      );
                                    })}
                                </tr>
                              );
                            })}
                              </>
                            )}

                            {/* Gender Unset Section */}
                            {strategizeData.members.filter(m => !m.gender).length > 0 && (
                              <>
                                <tr className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-[9]">
                                  <td className="sticky left-0 z-20 px-4 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                                    Gender Not Set
                                  </td>
                                  <td colSpan={strategizeData.matches.filter(match => {
                                    if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                    if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                    return true;
                                  }).length} className="bg-gray-100 dark:bg-gray-700"></td>
                                </tr>
                                {strategizeData.members
                                  .filter(m => !m.gender)
                                  .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
                                  .map(member => {
                              const canEditThisMember = canManageTeam || member.id === user?.id;
                              const playerRating = getPlayerRatings(member.id);
                              return (
                                <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 transition-colors">
                                  <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span>{getDisplayName(member)}</span>
                                      {member.is_captain && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                          Captain
                                        </span>
                                      )}
                                      {playerRating?.ntrp_rating && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {playerRating.ntrp_rating.toFixed(1)}
                                        </span>
                                      )}
                                      {isTennisRecordEnabled && playerRating?.dynamic_rating && (
                                        <span className="text-xs text-purple-700 dark:text-purple-300">
                                          {playerRating.dynamic_rating.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {strategizeData.matches
                                    .filter(match => {
                                      if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                                      if (matchFilter === 'past') return isMatchInPast(match.match_date);
                                      return true;
                                    })
                                    .map(match => {
                                      const key = `${match.id}_${member.id}`;
                                      const currentStatus = editedAvailability[key] || 
                                        strategizeData.availability[member.id]?.[match.id]?.status || 
                                        '';
                                      const isPast = isMatchInPast(match.match_date);
                                      
                                      const isDropdownOpen = activeStrategizeCell?.matchId === match.id && activeStrategizeCell?.playerId === member.id;
                                      
                                      return (
                                        <td 
                                          key={match.id} 
                                          className={`px-3 py-3 text-center border-l border-gray-200 dark:border-gray-700 scheduling-availability-cell relative transition-colors ${isPast ? 'bg-gray-50 dark:bg-gray-800/50' : ''} ${canEditThisMember && !isPast ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                          onClick={() => canEditThisMember && !isPast && setActiveStrategizeCell({ matchId: match.id, playerId: member.id })}
                                          title={canEditThisMember && !isPast ? 'Click to set availability' : ''}
                                        >
                                          <div className="flex justify-center">
                                            {currentStatus === 'available' ? (
                                              <CheckCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-green-500'}`} />
                                            ) : currentStatus === 'unavailable' ? (
                                              <XCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-red-500'}`} />
                                            ) : currentStatus === 'maybe' ? (
                                              <AlertCircle className={`w-8 h-8 ${isPast ? 'text-gray-400' : 'text-yellow-500'}`} />
                                            ) : (
                                              <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                                            )}
                                          </div>

                                          {/* Dropdown Menu */}
                                          {isDropdownOpen && canEditThisMember && !isPast && (
                                            <div className="absolute z-20 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 left-1/2 transform -translate-x-1/2">
                                              <div className="py-1">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'available', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                                >
                                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                                  <span>Available</span>
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'maybe', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                                >
                                                  <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                                                  <span>If Needed</span>
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStrategizeAvailabilityChange(match.id, member.id, 'unavailable', e);
                                                    setActiveStrategizeCell(null);
                                                  }}
                                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                                >
                                                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                                  <span>Not Available</span>
                                                </button>
                                                {currentStatus && (
                                                  <>
                                                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStrategizeAvailabilityChange(match.id, member.id, '', e);
                                                        setActiveStrategizeCell(null);
                                                      }}
                                                      className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-600 dark:text-gray-400"
                                                    >
                                                      <div className="w-5 h-5 rounded-full border-2 border-gray-400 dark:border-gray-500 flex items-center justify-center flex-shrink-0">
                                                        <svg className="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                      </div>
                                                      <span>Clear</span>
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </td>
                                      );
                                    })}
                                </tr>
                              );
                            })}
                              </>
                            )}
                              </>
                            )}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                  {/* Mobile Cards View */}
                  <div className="md:hidden space-y-4">
                    {strategizeData.matches
                      .filter(match => {
                        if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
                        if (matchFilter === 'past') return isMatchInPast(match.match_date);
                        return true;
                      })
                      .map(match => {
                        const isPast = isMatchInPast(match.match_date);
                        const matchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                        const hasLineup = matchLineup.length > 0;
                        const isPublished = match.lineup_published === true;
                        const oppStats = getOpponentStats(match.opponent_name);
                        const teamAvg = getTeamAverageDynamicRating();
                        const teamRange = getTeamDynamicRatingRange();
                        const isCompetitiveIntelCollapsed = mobileMatchCollapsed[match.id]?.competitiveIntel ?? true;
                        const isCardCollapsed = mobileMatchCollapsed[match.id]?.card ?? true; // Default to collapsed
                        
                        return (
                          <div key={match.id} className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
                            {/* Match Header - Clickable to toggle collapse */}
                            <button
                              onClick={() => {
                                setMobileMatchCollapsed({
                                  ...mobileMatchCollapsed,
                                  [match.id]: {
                                    ...mobileMatchCollapsed[match.id],
                                    card: !isCardCollapsed
                                  }
                                });
                              }}
                              className="w-full p-4 border-b border-gray-200 dark:border-gray-700 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  <ChevronDown className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 mt-0.5 ${isCardCollapsed ? '' : 'rotate-180'}`} />
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                      {match.opponent_name}
                                    </h3>
                                    <div className="mt-2 space-y-1">
                                      {(() => {
                                        const formatted = formatMatchDateCompact(match.match_date, true);
                                        return (
                                          <>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                              <Calendar className="w-4 h-4 flex-shrink-0" />
                                              <span>{formatted.date}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                              <Clock className="w-4 h-4 flex-shrink-0" />
                                              <span>{formatted.time}</span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                      {match.location && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                          <MapPin className="w-4 h-4 flex-shrink-0" />
                                          <span className="truncate">{match.location}</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      {match.home_away && (
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          match.home_away === 'home' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                        }`}>
                                          {match.home_away === 'home' ? 'Home' : 'Away'}
                                        </span>
                                      )}
                                      {isPast && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                                          Past
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>
                            
                            {/* Match Content - Collapsible */}
                            {!isCardCollapsed && (
                              <div>
                            {/* Match Actions */}
                            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                              {canManageTeam && !isPast && (
                                <div className="flex flex-col gap-2">
                                  {hasLineup && !isPublished && (
                                    <button
                                      onClick={() => {
                                        setPublishDialogMatchId(match.id);
                                        setPublishDialogIsResend(false);
                                      }}
                                      disabled={hasUnsavedChanges}
                                      className="w-full px-4 py-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-600 rounded transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-primary-600 dark:disabled:hover:text-primary-400"
                                      title={hasUnsavedChanges ? "Please save all changes before publishing the lineup" : "Publish lineup"}
                                    >
                                      Publish Lineup
                                    </button>
                                  )}
                                  {hasLineup && isPublished && isLineupEmailEnabled && (
                                    <button
                                      onClick={() => {
                                        setPublishDialogMatchId(match.id);
                                        setPublishDialogIsResend(true);
                                      }}
                                      className="w-full px-4 py-3 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 border border-green-200 dark:border-green-600 rounded transition-colors min-h-[44px]"
                                    >
                                      Resend Email
                                    </button>
                                  )}
                                  {hasLineup && isLineupEmailEnabled && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          await handleCopyLineupText(match.id);
                                        } catch (error) {
                                          // Error already handled in function
                                        }
                                      }}
                                      className="w-full px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/20 border border-gray-200 dark:border-gray-600 rounded transition-colors min-h-[44px]"
                                    >
                                      Copy Lineup Text
                                    </button>
                                  )}
                                  {hasLineup && isPublished && (
                                    <button
                                      onClick={() => window.open(`/matches/${match.id}/scorecard`, '_blank')}
                                      className="w-full px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/20 border border-gray-200 dark:border-gray-600 rounded transition-colors min-h-[44px]"
                                    >
                                      Print Score Card
                                    </button>
                                  )}
                                  {hasLineup && isPublished && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          await axios.put(`/lineups/matches/${match.id}/unpublish`);
                                          if (selectedTeam) {
                                            await fetchStrategizeData(selectedTeam.id);
                                          }
                                        } catch (error: any) {
                                          console.error('Failed to unpublish lineup:', error);
                                          alert(error.response?.data?.detail || 'Failed to unpublish lineup. Please try again.');
                                        }
                                      }}
                                      className="w-full px-4 py-3 text-sm font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 border border-orange-200 dark:border-orange-600 rounded transition-colors min-h-[44px]"
                                    >
                                      Unpublish Lineup
                                    </button>
                                  )}
                                </div>
                              )}
                              {canManageTeam && isPast && (
                                <button
                                  onClick={() => {
                                    const matchForModal: Match = {
                                      ...match,
                                      team_id: selectedTeam?.id || 0,
                                      created_at: new Date().toISOString()
                                    };
                                    handleOpenResultsModal(matchForModal);
                                  }}
                                  className="w-full px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded transition-colors min-h-[44px]"
                                >
                                  Enter Results
                                </button>
                              )}
                            </div>

                            {/* Competitive Intelligence Section */}
                            {isTennisRecordEnabled && competitiveIntel && oppStats && (
                              <div className="border-b border-gray-200 dark:border-gray-700">
                                <button
                                  onClick={() => {
                                    setMobileMatchCollapsed({
                                      ...mobileMatchCollapsed,
                                      [match.id]: {
                                        ...mobileMatchCollapsed[match.id],
                                        competitiveIntel: !isCompetitiveIntelCollapsed
                                      }
                                    });
                                  }}
                                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors min-h-[44px]"
                                >
                                  <div className="flex items-center gap-2">
                                    <Brain className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Competitive Intelligence</span>
                                  </div>
                                  <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isCompetitiveIntelCollapsed ? '' : 'rotate-180'}`} />
                                </button>
                                {!isCompetitiveIntelCollapsed && (
                                  <div className="px-4 pb-4 bg-purple-50 dark:bg-purple-900/20">
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-700 dark:text-gray-300">Opponent Avg:</span>
                                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                                          {oppStats.avg_dynamic_rating?.toFixed(2) || 'N/A'}
                                        </span>
                                      </div>
                                      {teamAvg !== null && oppStats.avg_dynamic_rating && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-700 dark:text-gray-300">Difference:</span>
                                          <span className={`font-semibold ${getRatingDifferenceColor(teamAvg, parseFloat(oppStats.avg_dynamic_rating))}`}>
                                            {(() => {
                                              const diff = parseFloat(oppStats.avg_dynamic_rating) - teamAvg;
                                              if (Math.abs(diff) < 0.01) return '±0.00';
                                              return diff > 0 ? `↑${diff.toFixed(2)}` : `↓${Math.abs(diff).toFixed(2)}`;
                                            })()}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex justify-between">
                                        <span className="text-gray-700 dark:text-gray-300">Range:</span>
                                        <span className="text-gray-900 dark:text-gray-100">
                                          {oppStats.min_dynamic_rating?.toFixed(2) || 'N/A'} - {oppStats.max_dynamic_rating?.toFixed(2) || 'N/A'}
                                        </span>
                                      </div>
                                      {formatRecord(oppStats) && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-gray-700 dark:text-gray-300">Season Record:</span>
                                          <span className="flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
                                            <span className="font-semibold">{formatRecord(oppStats)}</span>
                                            {renderRecentForm(oppStats)}
                                          </span>
                                        </div>
                                      )}
                                      {teamAvg !== null && (
                                        <div className="pt-2 border-t border-purple-200 dark:border-purple-700">
                                          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                                            <span>Team Avg: {teamAvg.toFixed(2)}</span>
                                            {teamRange.min !== null && teamRange.max !== null && (
                                              <span>Range: {teamRange.min.toFixed(2)} - {teamRange.max.toFixed(2)}</span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {/* Lineup Strength */}
                                      {(() => {
                                        const lineupAvg = getLineupAverageDynamicRating(match.id);
                                        const oppAvg = oppStats.avg_dynamic_rating ? parseFloat(oppStats.avg_dynamic_rating) : null;
                                        const diff = lineupAvg !== null && oppAvg !== null ? lineupAvg - oppAvg : null;
                                        return lineupAvg !== null ? (
                                          <div className="pt-2 border-t border-purple-200 dark:border-purple-700">
                                            <div className="flex justify-between">
                                              <span className="text-gray-700 dark:text-gray-300">Lineup Avg:</span>
                                              <div className="flex items-center gap-2">
                                                <span className="font-semibold text-primary-800 dark:text-primary-200">{lineupAvg.toFixed(2)}</span>
                                                {diff !== null && (
                                                  <span className={`text-xs font-medium ${
                                                    Math.abs(diff) < 0.01
                                                      ? 'text-gray-500'
                                                      : diff > 0
                                                        ? 'text-green-600 dark:text-green-400'
                                                        : 'text-orange-600 dark:text-orange-400'
                                                  }`}>
                                                    {Math.abs(diff) < 0.01 ? '±0.00' : diff > 0 ? `↑${diff.toFixed(2)}` : `↓${Math.abs(diff).toFixed(2)}`}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ) : null;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Lineup Positions */}
                            {strategizeData.lineup_format && strategizeData.lineup_format.positions.length > 0 && (
                              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Lineup</h4>
                                <div className="space-y-3">
                                  {(() => {
                                    // Group positions by match_type
                                    const positionsByType = (strategizeData.lineup_format?.positions || []).reduce((acc, pos) => {
                                      const matchType = pos.match_type || 'unknown';
                                      if (!acc[matchType]) {
                                        acc[matchType] = [];
                                      }
                                      acc[matchType].push(pos);
                                      return acc;
                                    }, {} as Record<string, Array<{ position_number: number; match_type: string }>>);
                                    
                                    // Get unique match types in order
                                    const matchTypes = Object.keys(positionsByType);
                                    
                                    return (
                                      <>
                                        {matchTypes.map((matchType, typeIndex) => {
                                          const positions = positionsByType[matchType];
                                          
                                          return (
                                            <React.Fragment key={matchType}>
                                              {typeIndex > 0 && (
                                                <div className="border-t border-gray-300 dark:border-gray-600 my-4"></div>
                                              )}
                                              {positions.map(formatPos => {
                                    const isSingles = isSinglesPosition(formatPos.match_type);
                                    const position = matchLineup.find(p => p.position_number === formatPos.position_number);
                                    const player1Id = position?.player1_id || 0;
                                    const player2Id = position?.player2_id || 0;
                                    
                                    // Get original position for confirmations (confirmations don't change when editing)
                                    const originalPosition = strategizeData.lineups[match.id]?.find(
                                      p => p.position_number === formatPos.position_number
                                    );
                                    
                                    // Get player1 confirmation details
                                    const player1Confirmation = originalPosition?.confirmations?.find(
                                      (conf: LineupConfirmation) => conf.player_id === player1Id
                                    );
                                    const player1Confirmed = !!player1Confirmation;
                                    
                                    // Get confirmer name
                                    const player1ConfirmerName = player1Confirmation 
                                      ? getMemberName(player1Confirmation.confirmed_by) || 'Unknown'
                                      : '';
                                    
                                    // Format confirmation time using team timezone
                                    const teamTimezone = selectedTeam?.timezone || 'America/Los_Angeles';
                                    const player1ConfirmedTime = player1Confirmation?.confirmed_at
                                      ? (() => {
                                          let dateStr = player1Confirmation.confirmed_at;
                                          if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
                                            dateStr = dateStr + 'Z';
                                          }
                                          const date = new Date(dateStr);
                                          return date.toLocaleString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            timeZone: teamTimezone
                                          });
                                        })()
                                      : '';
                                    
                                    // Check if lineup is published (to show confirmation status)
                                    const isLineupPublished = match.lineup_published === true;
                                    
                                    // Get player2 confirmation details
                                    const player2Confirmation = originalPosition?.confirmations?.find(
                                      (conf: LineupConfirmation) => conf.player_id === player2Id
                                    );
                                    const player2Confirmed = !!player2Confirmation;
                                    
                                    // Get confirmer name
                                    const player2ConfirmerName = player2Confirmation 
                                      ? getMemberName(player2Confirmation.confirmed_by) || 'Unknown'
                                      : '';
                                    
                                    // Format confirmation time using team timezone
                                    const player2ConfirmedTime = player2Confirmation?.confirmed_at
                                      ? (() => {
                                          let dateStr = player2Confirmation.confirmed_at;
                                          if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
                                            dateStr = dateStr + 'Z';
                                          }
                                          const date = new Date(dateStr);
                                          return date.toLocaleString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            timeZone: teamTimezone
                                          });
                                        })()
                                      : '';
                                    
                                    const allPositions = strategizeData.lineup_format?.positions || [];
                                    const matchTypeCount = allPositions.filter(p => p.match_type === formatPos.match_type).length;
                                    const sameTypePositions = allPositions.filter(p => p.match_type === formatPos.match_type);
                                    const positionIndexWithinType = sameTypePositions.findIndex(p => p.position_number === formatPos.position_number) + 1;
                                    const formattedTypeLabel = formatMatchType(formatPos.match_type);
                                    const displayLabel = matchTypeCount > 1 ? `${formattedTypeLabel} ${positionIndexWithinType}` : formattedTypeLabel;
                                    
                                    const currentMatchLineup = editedLineups[match.id] || strategizeData.lineups[match.id] || [];
                                    const validationLineup: ValidationLineupPosition[] = currentMatchLineup.map(pos => ({
                                      position_number: pos.position_number,
                                      player1_id: pos.player1_id || 0,
                                      player2_id: pos.player2_id || null
                                    }));
                                    
                                    const players = strategizeData.members.map(m => ({ 
                                      id: m.id, 
                                      name: m.name, 
                                      gender: m.gender || null,
                                      ntrp_rating: m.ntrp_rating || null,
                                      dynamic_rating: m.dynamic_rating || null
                                    }));
                                    const lineupFormatForValidation: ValidationLineupFormat = {
                                      total_lines: strategizeData.lineup_format?.total_lines || 0,
                                      max_sets_per_line: strategizeData.lineup_format?.max_sets_per_line || 0,
                                      allow_multi_line_selection: strategizeData.lineup_format?.allow_multi_line_selection || false,
                                      positions: strategizeData.lineup_format?.positions.map(p => ({
                                        position_number: p.position_number,
                                        match_type: p.match_type
                                      })) || []
                                    };
                                    
                                    const { eligible, ineligible } = filterPlayer1Options(
                                      players,
                                      formatPos.match_type,
                                      validationLineup,
                                      lineupFormatForValidation,
                                      formatPos.position_number,
                                      player2Id && player2Id > 0 ? player2Id : undefined,
                                      selectedTeam?.max_combined_ntrp_rating || null
                                    );
                                    
                                    // Filter out gender-ineligible players (they should remain hidden)
                                    const ineligibleNonGender1 = ineligible.filter(
                                      ({ reason }) => {
                                        const lowerReason = reason.toLowerCase();
                                        return !lowerReason.includes('must be male') && 
                                               !lowerReason.includes('must be female') && 
                                               !lowerReason.includes('different gender') &&
                                               !lowerReason.includes('for mens_') &&
                                               !lowerReason.includes('for womens_') &&
                                               !lowerReason.includes('for mixed_doubles');
                                      }
                                    );
                                    
                                    // Convert eligible players back to StrategizeMember format
                                    const eligibleMembers1 = eligible.map(eligiblePlayer => 
                                      strategizeData.members.find(m => m.id === eligiblePlayer.id)!
                                    ).filter(Boolean);
                                    
                                    // Sort ineligible players alphabetically
                                    const sortedIneligiblePlayers1 = ineligibleNonGender1
                                      .map(({ player, reason }) => ({
                                        player: strategizeData.members.find(m => m.id === player.id)!,
                                        reason
                                      }))
                                      .filter(({ player }) => player)
                                      .sort((a, b) => getDisplayName(a.player).localeCompare(getDisplayName(b.player)));
                                    
                                    const availablePlayers1 = sortPlayersByAvailabilityForMatch(eligibleMembers1, match.id);
                                    
                                    const isChanged1 = isLineupPositionChanged(match.id, formatPos.position_number, 'player1');
                                    const isUnfilled1 = !isPast && isPositionUnfilled(match.id, formatPos.position_number, 'player1') && player1Id === 0;
                                    const fieldErrors1 = getErrorsForPosition(match.id, formatPos.position_number, 'player1_id');
                                    const hasError1 = fieldErrors1.length > 0;
                                    
                                    return (
                                      <div key={formatPos.position_number} className="space-y-2">
                                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5"><MatchTypeIcon matchType={formatPos.match_type} iconSize="w-4 h-4" />{displayLabel}</div>
                                        <div className="space-y-2">
                                          {/* Player 1 */}
                                          <div>
                                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Player 1</label>
                                            {canManageTeam && !isPast ? (
                                              isLineupPublished ? (
                                                <div className="flex items-center gap-2">
                                                  <div className="flex-1 min-w-0">
                                                    <PlayerDropdown
                                                      value={player1Id || null}
                                                      onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player1_id', playerId || 0)}
                                                      eligiblePlayers={availablePlayers1}
                                                      ineligiblePlayers={sortedIneligiblePlayers1}
                                                      matchId={match.id}
                                                      getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                      getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                      className="text-sm"
                                                      hasError={hasError1}
                                                      isChanged={isChanged1}
                                                      isUnfilled={isUnfilled1}
                                                      matchType={formatPos.match_type}
                                                    />
                                                  </div>
                                                  {player1Id > 0 && originalPosition && (
                                                    player1Confirmed ? (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const playerName = getMemberName(player1Id) || 'Player';
                                                          handleConfirmationToggle(
                                                            match.id,
                                                            originalPosition.id,
                                                            player1Id,
                                                            playerName,
                                                            true
                                                          );
                                                        }}
                                                        title={`Confirmed by ${player1ConfirmerName} on ${player1ConfirmedTime}. Tap to unconfirm.`}
                                                        className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity min-h-[44px] px-2"
                                                      >
                                                        <Check className="w-4 h-4 text-green-500" />
                                                      </button>
                                                    ) : (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const playerName = getMemberName(player1Id) || 'Player';
                                                          handleConfirmationToggle(
                                                            match.id,
                                                            originalPosition.id,
                                                            player1Id,
                                                            playerName,
                                                            false
                                                          );
                                                        }}
                                                        title="Not confirmed. Tap to confirm."
                                                        className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity min-h-[44px] px-2"
                                                      >
                                                        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                      </button>
                                                    )
                                                  )}
                                                </div>
                                              ) : (
                                                <PlayerDropdown
                                                  value={player1Id || null}
                                                  onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player1_id', playerId || 0)}
                                                  eligiblePlayers={availablePlayers1}
                                                  ineligiblePlayers={sortedIneligiblePlayers1}
                                                  matchId={match.id}
                                                  getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                  getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                  className="text-sm"
                                                  hasError={hasError1}
                                                  isChanged={isChanged1}
                                                  isUnfilled={isUnfilled1}
                                                  matchType={formatPos.match_type}
                                                />
                                              )
                                            ) : (
                                              <div className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md min-h-[44px] flex items-center gap-2">
                                                <span className="flex-1">
                                                  {player1Id > 0 ? getMemberName(player1Id) : '-'}
                                                </span>
                                                {isLineupPublished && player1Id > 0 && originalPosition && (
                                                  canManageTeam ? (
                                                    player1Confirmed ? (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const playerName = getMemberName(player1Id) || 'Player';
                                                          handleConfirmationToggle(
                                                            match.id,
                                                            originalPosition.id,
                                                            player1Id,
                                                            playerName,
                                                            true
                                                          );
                                                        }}
                                                        title={`Confirmed by ${player1ConfirmerName} on ${player1ConfirmedTime}. Tap to unconfirm.`}
                                                        className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                      >
                                                        <Check className="w-4 h-4 text-green-500" />
                                                      </button>
                                                    ) : (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const playerName = getMemberName(player1Id) || 'Player';
                                                          handleConfirmationToggle(
                                                            match.id,
                                                            originalPosition.id,
                                                            player1Id,
                                                            playerName,
                                                            false
                                                          );
                                                        }}
                                                        title="Not confirmed. Tap to confirm."
                                                        className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                      >
                                                        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                      </button>
                                                    )
                                                  ) : (
                                                    player1Confirmed ? (
                                                      <span title={`Confirmed by ${player1ConfirmerName} on ${player1ConfirmedTime}`}>
                                                        <Check className="w-4 h-4 text-green-500" />
                                                      </span>
                                                    ) : (
                                                      <span title="Not confirmed">
                                                        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                      </span>
                                                    )
                                                  )
                                                )}
                                              </div>
                                            )}
                                            {hasError1 && (
                                              <div className="mt-1">
                                                {fieldErrors1.map((err, idx) => (
                                                  <div key={idx} className="text-xs text-red-600 dark:text-red-400">
                                                    {err.message}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* Player 2 (doubles only) */}
                                          {!isSingles && (
                                            <div>
                                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Player 2</label>
                                              {canManageTeam && !isPast ? (
                                                (() => {
                                                  const { eligible, ineligible } = filterPlayer2Options(
                                                    players,
                                                    formatPos.match_type,
                                                    validationLineup,
                                                    lineupFormatForValidation,
                                                    formatPos.position_number,
                                                    player1Id || 0,
                                                    selectedTeam?.max_combined_ntrp_rating || null
                                                  );
                                                  
                                                  // Filter out gender-ineligible players (they should remain hidden)
                                                  const ineligibleNonGender2 = ineligible.filter(
                                                    ({ reason }) => {
                                                      const lowerReason = reason.toLowerCase();
                                                      return !lowerReason.includes('must be male') && 
                                                             !lowerReason.includes('must be female') && 
                                                             !lowerReason.includes('different gender') &&
                                                             !lowerReason.includes('for mens_') &&
                                                             !lowerReason.includes('for womens_') &&
                                                             !lowerReason.includes('for mixed_doubles');
                                                    }
                                                  );
                                                  
                                                  // Convert eligible players back to StrategizeMember format
                                                  const eligibleMembers2 = eligible.map(eligiblePlayer => 
                                                    strategizeData.members.find(m => m.id === eligiblePlayer.id)!
                                                  ).filter(Boolean);
                                                  
                                                  // Sort ineligible players alphabetically
                                                  const sortedIneligiblePlayers2 = ineligibleNonGender2
                                                    .map(({ player, reason }) => ({
                                                      player: strategizeData.members.find(m => m.id === player.id)!,
                                                      reason
                                                    }))
                                                    .filter(({ player }) => player)
                                                    .sort((a, b) => getDisplayName(a.player).localeCompare(getDisplayName(b.player)));
                                                  
                                                  const availablePlayers2 = sortPlayersByAvailabilityForMatch(eligibleMembers2, match.id);
                                                  
                                                  const isChanged2 = isLineupPositionChanged(match.id, formatPos.position_number, 'player2');
                                                  const isUnfilled2 = !isPast && isPositionUnfilled(match.id, formatPos.position_number, 'player2') && (!player2Id || player2Id === 0);
                                                  const fieldErrors2 = getErrorsForPosition(match.id, formatPos.position_number, 'player2_id');
                                                  const hasError2 = fieldErrors2.length > 0;
                                                  
                                                  return (
                                                    <>
                                                      {isLineupPublished ? (
                                                        <div className="flex items-center gap-2">
                                                          <div className="flex-1 min-w-0">
                                                            <PlayerDropdown
                                                              value={player2Id || null}
                                                              onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player2_id', playerId || 0)}
                                                              eligiblePlayers={availablePlayers2}
                                                              ineligiblePlayers={sortedIneligiblePlayers2}
                                                              matchId={match.id}
                                                              getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                              getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                              className="text-sm"
                                                              hasError={hasError2}
                                                              isChanged={isChanged2}
                                                              isUnfilled={isUnfilled2}
                                                              matchType={formatPos.match_type}
                                                            />
                                                          </div>
                                                          {player2Id > 0 && originalPosition && (
                                                            player2Confirmed ? (
                                                              <button
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const playerName = getMemberName(player2Id) || 'Player';
                                                                  handleConfirmationToggle(
                                                                    match.id,
                                                                    originalPosition.id,
                                                                    player2Id,
                                                                    playerName,
                                                                    true
                                                                  );
                                                                }}
                                                                title={`Confirmed by ${player2ConfirmerName} on ${player2ConfirmedTime}. Tap to unconfirm.`}
                                                                className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity min-h-[44px] px-2"
                                                              >
                                                                <Check className="w-4 h-4 text-green-500" />
                                                              </button>
                                                            ) : (
                                                              <button
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const playerName = getMemberName(player2Id) || 'Player';
                                                                  handleConfirmationToggle(
                                                                    match.id,
                                                                    originalPosition.id,
                                                                    player2Id,
                                                                    playerName,
                                                                    false
                                                                  );
                                                                }}
                                                                title="Not confirmed. Tap to confirm."
                                                                className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity min-h-[44px] px-2"
                                                              >
                                                                <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                              </button>
                                                            )
                                                          )}
                                                        </div>
                                                      ) : (
                                                        <PlayerDropdown
                                                          value={player2Id || null}
                                                          onChange={(playerId) => handleStrategizeLineupChange(match.id, formatPos.position_number, 'player2_id', playerId || 0)}
                                                          eligiblePlayers={availablePlayers2}
                                                          ineligiblePlayers={sortedIneligiblePlayers2}
                                                          matchId={match.id}
                                                          getPlayerAvailability={getPlayerAvailabilityForMatch}
                                                          getPlayerLabel={getPlayerLabelWithoutEmoji}
                                                          className="text-sm"
                                                          hasError={hasError2}
                                                          isChanged={isChanged2}
                                                          isUnfilled={isUnfilled2}
                                                          matchType={formatPos.match_type}
                                                        />
                                                      )}
                                                      {hasError2 && (
                                                        <div className="mt-1">
                                                          {fieldErrors2.map((err, idx) => (
                                                            <div key={idx} className="text-xs text-red-600 dark:text-red-400">
                                                              {err.message}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </>
                                                  );
                                                })()
                                              ) : (
                                                <div className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md min-h-[44px] flex items-center gap-2">
                                                  <span className="flex-1">
                                                    {player2Id && player2Id > 0 ? getMemberName(player2Id) : '-'}
                                                  </span>
                                                  {isLineupPublished && player2Id > 0 && originalPosition && (
                                                    canManageTeam ? (
                                                      player2Confirmed ? (
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            const playerName = getMemberName(player2Id) || 'Player';
                                                            handleConfirmationToggle(
                                                              match.id,
                                                              originalPosition.id,
                                                              player2Id,
                                                              playerName,
                                                              true
                                                            );
                                                          }}
                                                          title={`Confirmed by ${player2ConfirmerName} on ${player2ConfirmedTime}. Tap to unconfirm.`}
                                                          className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                        >
                                                          <Check className="w-4 h-4 text-green-500" />
                                                        </button>
                                                      ) : (
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            const playerName = getMemberName(player2Id) || 'Player';
                                                            handleConfirmationToggle(
                                                              match.id,
                                                              originalPosition.id,
                                                              player2Id,
                                                              playerName,
                                                              false
                                                            );
                                                          }}
                                                          title="Not confirmed. Tap to confirm."
                                                          className="flex-shrink-0 inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
                                                        >
                                                          <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                        </button>
                                                      )
                                                    ) : (
                                                      player2Confirmed ? (
                                                        <span title={`Confirmed by ${player2ConfirmerName} on ${player2ConfirmedTime}`}>
                                                          <Check className="w-4 h-4 text-green-500" />
                                                        </span>
                                                      ) : (
                                                        <span title="Not confirmed">
                                                          <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></div>
                                                        </span>
                                                      )
                                                    )
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Match Result */}
                                          {position?.is_actual_result && position?.score && (
                                            <div className="px-3 py-2 text-xs bg-gray-50 dark:bg-gray-700 rounded-md">
                                              <span className="text-gray-600 dark:text-gray-400">Result: </span>
                                              <span className={`font-semibold ${
                                                position.won === true ? 'text-green-600 dark:text-green-400' : 
                                                position.won === false ? 'text-red-600 dark:text-red-400' : 
                                                'text-gray-700 dark:text-gray-300'
                                              }`}>
                                                {position.score} {position.won === true ? '✓' : position.won === false ? '✗' : ''}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                              </React.Fragment>
                                            );
                                          })}
                                        </>
                                      );
                                    })()}
                                </div>
                              </div>
                            )}

                            {/* Availability Section - Hidden on mobile */}
                            </div>
                            )}
                          </div>
                        );
                      })}
                  </div>

                  {/* Validation Errors */}
                  {Object.entries(validationErrors).some(([_, errors]) => errors.length > 0) && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            Validation Errors
                          </h3>
                          <div className="mt-2 text-sm text-red-700">
                            {Object.entries(validationErrors).map(([matchId, errors]) => {
                              if (!errors || errors.length === 0) return null;
                              const match = strategizeData.matches.find(m => m.id === parseInt(matchId));
                              return (
                                <div key={matchId} className="mb-2">
                                  <div className="font-semibold">Match vs {match?.opponent_name}:</div>
                                  <ul className="list-disc list-inside ml-2">
                                    {errors.map((error, idx) => (
                                      <li key={idx}>
                                        {error.positionNumber ? `Position ${error.positionNumber}: ` : ''}
                                        {error.message}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
                </div>
                
                {/* Stats Panel */}
                {isStatsPanelOpen && (
                  <div className="hidden md:block w-80 flex-shrink-0">
                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 md:p-6 sticky top-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">Lineup Stats</h3>
                      <Button
                        onClick={() => setIsStatsPanelOpen(false)}
                        variant="ghost"
                        size="sm"
                        aria-label="Close stats panel"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-hidden pr-2">
                      {(() => {
                        const stats = calculatePlayerStats();
                        const maxCount = Math.max(...stats.map(s => s.totalCount), 1);
                        
                        return (
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                              <tr className="text-left text-gray-600 dark:text-gray-300">
                                <th className="pb-2 pt-1 font-medium pr-2">Player</th>
                                <th className="pb-2 pt-1 font-medium text-center w-12">Played</th>
                                <th className="pb-2 pt-1 font-medium text-center w-12">Sched</th>
                                <th className="pb-2 pt-1 font-medium text-center w-12">Total</th>
                                <th className="pb-2 pt-1" style={{ minWidth: '25px', width: '25px', paddingRight: '8px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {stats.map(stat => (
                                <React.Fragment key={stat.player.id}>
                                  <tr 
                                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                    onClick={() => setExpandedPlayerId(
                                      expandedPlayerId === stat.player.id ? null : stat.player.id
                                    )}
                                  >
                                    <td className="py-2.5 font-medium text-gray-900 dark:text-gray-100 truncate" title={getDisplayName(stat.player)}>
                                      <div className="flex items-center gap-2">
                                        <ChevronDown
                                          className={`w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform ${
                                            expandedPlayerId === stat.player.id ? 'rotate-0' : '-rotate-90'
                                          }`}
                                        />
                                        <span className="truncate">{getDisplayName(stat.player)}</span>
                                      </div>
                                    </td>
                                    <td className="py-2.5 text-center text-gray-700 dark:text-gray-300">
                                      {stat.playedCount}
                                    </td>
                                    <td className="py-2.5 text-center text-gray-700 dark:text-gray-300">
                                      {stat.scheduledCount}
                                    </td>
                                    <td className="py-2.5 text-center font-semibold text-gray-900 dark:text-gray-100">
                                      {stat.totalCount}
                                    </td>
                                    <td className="py-2.5" style={{ paddingRight: '8px' }}>
                                      <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2 relative overflow-hidden" style={{ width: '25px', minHeight: '8px' }}>
                                        {(() => {
                                          const totalWidth = 25;
                                          const playedWidth = maxCount > 0 && stat.playedCount > 0 
                                            ? Math.min((stat.playedCount / maxCount) * totalWidth, totalWidth) 
                                            : 0;
                                          const scheduledWidth = maxCount > 0 && stat.scheduledCount > 0 
                                            ? Math.min((stat.scheduledCount / maxCount) * totalWidth, totalWidth - playedWidth) 
                                            : 0;
                                          
                                          return (
                                            <>
                                              {/* Played segment - normal color based on total count (first in stack) */}
                                              {stat.playedCount > 0 && playedWidth > 0 && (
                                                <div
                                                  className={`absolute left-0 top-0 h-2 ${stat.scheduledCount === 0 || scheduledWidth === 0 ? 'rounded-full' : 'rounded-l-full'} ${getColorForCount(stat.totalCount, maxCount)}`}
                                                  style={{ 
                                                    width: `${Math.max(playedWidth, 2)}px`
                                                  }}
                                                  title={`${stat.playedCount} played`}
                                                ></div>
                                              )}
                                              {/* Scheduled segment - lighter color based on total count (second in stack) */}
                                              {stat.scheduledCount > 0 && scheduledWidth > 0 && (
                                                <div
                                                  className={`absolute top-0 h-2 ${stat.playedCount === 0 || playedWidth === 0 ? 'rounded-full' : 'rounded-r-full'} ${getLighterColorForCount(stat.totalCount, maxCount)}`}
                                                  style={{ 
                                                    left: `${playedWidth}px`,
                                                    width: `${Math.max(scheduledWidth, 2)}px`
                                                  }}
                                                  title={`${stat.scheduledCount} scheduled`}
                                                ></div>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                  </tr>
                                  
                                  {/* Expandable format breakdown */}
                                  {expandedPlayerId === stat.player.id && (
                                    <tr>
                                      <td colSpan={5} className="py-3 px-3 bg-gray-50 dark:bg-gray-700/50">
                                        <div className="text-xs space-y-3">
                                          {/* Win/Loss Record */}
                                          {stat.playedCount > 0 && (
                                            <div className="pb-2 border-b border-gray-200 dark:border-gray-600">
                                              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">Win/Loss Record:</div>
                                              <div className="flex items-center gap-4 text-gray-600 dark:text-gray-300">
                                                <div className="flex items-center gap-1">
                                                  <span className="text-green-600 dark:text-green-400 font-semibold">{stat.wins}</span>
                                                  <span>W</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <span className="text-red-600 dark:text-red-400 font-semibold">{stat.losses}</span>
                                                  <span>L</span>
                                                </div>
                                                {stat.wins + stat.losses > 0 && (
                                                  <div className="text-gray-500 dark:text-gray-400">
                                                    ({((stat.wins / (stat.wins + stat.losses)) * 100).toFixed(0)}%)
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          {/* Lineup preferences */}
                                          {stat.player.lineup_type_preferences && stat.player.lineup_type_preferences.length > 0 && (
                                            <div className="pb-2 border-b border-gray-200 dark:border-gray-600">
                                              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">Lineup Preference:</div>
                                              <div className="space-y-0.5">
                                                {stat.player.lineup_type_preferences.map((pref, i) => (
                                                  <div key={pref} className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                                                    <span className={`font-medium ${i === 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                                      {i + 1}.
                                                    </span>
                                                    <MatchTypeIcon matchType={pref} iconSize="w-4 h-4" />
                                                    <span>{formatMatchType(pref)}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {/* Format breakdown */}
                                          {Object.keys(stat.formatCounts).length > 0 && (
                                            <div>
                                              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">By Format:</div>
                                              {(() => {
                                                // Get format order from lineup format positions
                                                const formatOrder = new Map<string, number>();
                                                if (strategizeData?.lineup_format?.positions) {
                                                  strategizeData.lineup_format.positions
                                                    .sort((a, b) => a.position_number - b.position_number)
                                                    .forEach((pos, index) => {
                                                      const formattedType = formatMatchType(pos.match_type);
                                                      if (!formatOrder.has(formattedType)) {
                                                        formatOrder.set(formattedType, index);
                                                      }
                                                    });
                                                }
                                                
                                                // Sort format entries by lineup format order
                                                return Object.entries(stat.formatCounts)
                                                  .sort(([formatA], [formatB]) => {
                                                    const orderA = formatOrder.get(formatA) ?? 999;
                                                    const orderB = formatOrder.get(formatB) ?? 999;
                                                    return orderA - orderB;
                                                  })
                                                  .map(([format, count]) => (
                                                    <div key={format} className="flex justify-between text-gray-600 dark:text-gray-300 mb-1">
                                                      <span>{format}:</span>
                                                      <span className="font-medium">{count}</span>
                                                    </div>
                                                  ));
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mobile Stats Modal */}
          {showMobileStatsModal && (
            <div className="fixed inset-0 z-50 md:hidden" onClick={() => setShowMobileStatsModal(false)}>
              <div className="absolute inset-0 bg-black bg-opacity-50" />
              <div 
                className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-lg shadow-xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lineup Stats</h3>
                  <button
                    onClick={() => setShowMobileStatsModal(false)}
                    className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Close stats"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {(() => {
                      const stats = calculatePlayerStats();
                      const maxCount = Math.max(...stats.map(s => s.totalCount), 1);
                      
                      return (
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
                            <tr className="text-left text-gray-600 dark:text-gray-300">
                              <th className="pb-2 pt-1 font-medium pr-2">Player</th>
                              <th className="pb-2 pt-1 font-medium text-center w-12">Played</th>
                              <th className="pb-2 pt-1 font-medium text-center w-12">Sched</th>
                              <th className="pb-2 pt-1 font-medium text-center w-12">Total</th>
                              <th className="pb-2 pt-1" style={{ minWidth: '25px', width: '25px', paddingRight: '8px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.map(stat => (
                              <React.Fragment key={stat.player.id}>
                                <tr 
                                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                  onClick={() => setExpandedPlayerId(
                                    expandedPlayerId === stat.player.id ? null : stat.player.id
                                  )}
                                >
                                  <td className="py-2.5 font-medium text-gray-900 dark:text-gray-100 truncate" title={getDisplayName(stat.player)}>
                                    <div className="flex items-center gap-2">
                                      <ChevronDown
                                        className={`w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform ${
                                          expandedPlayerId === stat.player.id ? 'rotate-0' : '-rotate-90'
                                        }`}
                                      />
                                      <span className="truncate">{getDisplayName(stat.player)}</span>
                                    </div>
                                  </td>
                                  <td className="py-2.5 text-center text-gray-700 dark:text-gray-300">
                                    {stat.playedCount}
                                  </td>
                                  <td className="py-2.5 text-center text-gray-700 dark:text-gray-300">
                                    {stat.scheduledCount}
                                  </td>
                                  <td className="py-2.5 text-center font-semibold text-gray-900 dark:text-gray-100">
                                    {stat.totalCount}
                                  </td>
                                  <td className="py-2.5" style={{ paddingRight: '8px' }}>
                                    <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2 relative overflow-hidden" style={{ width: '25px', minHeight: '8px' }}>
                                      {(() => {
                                        const totalWidth = 25;
                                        const playedWidth = maxCount > 0 && stat.playedCount > 0 
                                          ? Math.min((stat.playedCount / maxCount) * totalWidth, totalWidth) 
                                          : 0;
                                        const scheduledWidth = maxCount > 0 && stat.scheduledCount > 0 
                                          ? Math.min((stat.scheduledCount / maxCount) * totalWidth, totalWidth - playedWidth) 
                                          : 0;
                                        
                                        return (
                                          <>
                                            {stat.playedCount > 0 && playedWidth > 0 && (
                                              <div
                                                className={`absolute left-0 top-0 h-2 ${stat.scheduledCount === 0 || scheduledWidth === 0 ? 'rounded-full' : 'rounded-l-full'} ${getColorForCount(stat.totalCount, maxCount)}`}
                                                style={{ 
                                                  width: `${Math.max(playedWidth, 2)}px`
                                                }}
                                                title={`${stat.playedCount} played`}
                                              ></div>
                                            )}
                                            {stat.scheduledCount > 0 && scheduledWidth > 0 && (
                                              <div
                                                className={`absolute top-0 h-2 ${stat.playedCount === 0 || playedWidth === 0 ? 'rounded-full' : 'rounded-r-full'} ${getLighterColorForCount(stat.totalCount, maxCount)}`}
                                                style={{ 
                                                  left: `${playedWidth}px`,
                                                  width: `${Math.max(scheduledWidth, 2)}px`
                                                }}
                                                title={`${stat.scheduledCount} scheduled`}
                                              ></div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                </tr>
                                
                                {expandedPlayerId === stat.player.id && (
                                  <tr>
                                    <td colSpan={5} className="py-3 px-3 bg-gray-50 dark:bg-gray-700/50">
                                      <div className="text-xs space-y-3">
                                        {stat.playedCount > 0 && (
                                          <div className="pb-2 border-b border-gray-200 dark:border-gray-600">
                                            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">Win/Loss Record:</div>
                                            <div className="flex items-center gap-4 text-gray-600 dark:text-gray-300">
                                              <div className="flex items-center gap-1">
                                                <span className="text-green-600 dark:text-green-400 font-semibold">{stat.wins}</span>
                                                <span>W</span>
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <span className="text-red-600 dark:text-red-400 font-semibold">{stat.losses}</span>
                                                <span>L</span>
                                              </div>
                                              {stat.wins + stat.losses > 0 && (
                                                <div className="text-gray-500 dark:text-gray-400">
                                                  ({((stat.wins / (stat.wins + stat.losses)) * 100).toFixed(0)}%)
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {/* Lineup preferences */}
                                        {stat.player.lineup_type_preferences && stat.player.lineup_type_preferences.length > 0 && (
                                          <div className="pb-2 border-b border-gray-200 dark:border-gray-600">
                                            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">Lineup Preference:</div>
                                            <div className="space-y-0.5">
                                              {stat.player.lineup_type_preferences.map((pref, i) => (
                                                <div key={pref} className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                                                  <span className={`font-medium ${i === 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                                    {i + 1}.
                                                  </span>
                                                  <MatchTypeIcon matchType={pref} iconSize="w-4 h-4" />
                                                  <span>{formatMatchType(pref)}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {Object.keys(stat.formatCounts).length > 0 && (
                                          <div>
                                            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">By Format:</div>
                                            {(() => {
                                              // Get format order from lineup format positions
                                              const formatOrder = new Map<string, number>();
                                              if (strategizeData?.lineup_format?.positions) {
                                                strategizeData.lineup_format.positions
                                                  .sort((a, b) => a.position_number - b.position_number)
                                                  .forEach((pos, index) => {
                                                    const formattedType = formatMatchType(pos.match_type);
                                                    if (!formatOrder.has(formattedType)) {
                                                      formatOrder.set(formattedType, index);
                                                    }
                                                  });
                                              }
                                              
                                              // Sort format entries by lineup format order
                                              return Object.entries(stat.formatCounts)
                                                .sort(([formatA], [formatB]) => {
                                                  const orderA = formatOrder.get(formatA) ?? 999;
                                                  const orderB = formatOrder.get(formatB) ?? 999;
                                                  return orderA - orderB;
                                                })
                                                .map(([format, count]) => (
                                                  <div key={format} className="flex justify-between text-gray-600 dark:text-gray-300 mb-1">
                                                    <span>{format}:</span>
                                                    <span className="font-medium">{count}</span>
                                                  </div>
                                                ));
                                            })()}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Floating Save Button */}
          {activeTab === 'strategize' && hasUnsavedChanges && (
            <div className="fixed bottom-6 right-6 z-40 md:hidden">
              <button
                onClick={handleSaveAllChanges}
                disabled={saving}
                className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-full px-5 py-4 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Save all changes"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span className="font-medium">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span className="font-medium">Save All Changes</span>
                    {/* Unsaved changes indicator */}
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white dark:border-gray-800"></span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Configuration Tab */}
          {activeTab === 'configuration' && selectedTeam && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <Settings className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Configuration</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Manage your team settings and preferences</p>
                </div>
              </div>

              {/* Cards Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Team Information Card */}
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                        <Settings className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Team Information</h3>
                    </div>
                    {canManageTeam && !isEditingConfig && (
                      <Button
                        onClick={handleEditConfig}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )}
                  </div>

                  {isEditingConfig ? (
                    // Edit Mode
                    <div className="space-y-6">
                    {/* Team Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Team Name *</label>
                      <input
                        type="text"
                        value={configFormData.name}
                        onChange={(e) => setConfigFormData({ ...configFormData, name: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                      />
                    </div>

                    {/* League & Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">League</label>
                        <input
                          type="text"
                          value={configFormData.league}
                          onChange={(e) => setConfigFormData({ ...configFormData, league: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                          placeholder="e.g., USTA League"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section</label>
                        <input
                          type="text"
                          value={configFormData.section}
                          onChange={(e) => setConfigFormData({ ...configFormData, section: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                          placeholder="e.g., Southern"
                        />
                      </div>
                    </div>

                    {/* District & Flight */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">District/Area</label>
                        <input
                          type="text"
                          value={configFormData.district_area}
                          onChange={(e) => setConfigFormData({ ...configFormData, district_area: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Flight/Sub-Flight</label>
                        <input
                          type="text"
                          value={configFormData.flight_subflight}
                          onChange={(e) => setConfigFormData({ ...configFormData, flight_subflight: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                        />
                      </div>
                    </div>

                    {/* USTA Team ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">USTA Team ID</label>
                      <input
                        type="text"
                        value={configFormData.usta_team_id}
                        onChange={(e) => setConfigFormData({ ...configFormData, usta_team_id: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                        placeholder="e.g., 12-345678"
                      />
                    </div>

                    {/* League Dates */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="mb-4">
                        <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">League Dates</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Dates are used to determine if your team is currently active.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id="config_use_first_match"
                            checked={configFormData.use_first_match_for_start_date}
                            onChange={(e) => {
                              const useCalculated = e.target.checked;
                              setConfigFormData({ 
                                ...configFormData, 
                                use_first_match_for_start_date: useCalculated,
                                // Clear manual date when using calculated date
                                league_start_date: useCalculated ? '' : configFormData.league_start_date
                              });
                              // Clear errors when toggling
                              if (configErrors.league_start_date) {
                                setConfigErrors({ ...configErrors, league_start_date: undefined });
                              }
                            }}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label htmlFor="config_use_first_match" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Use first match date
                          </label>
                        </div>
                        {!configFormData.use_first_match_for_start_date && (
                          <>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">League Start Date</label>
                            <input
                              type="date"
                              value={configFormData.league_start_date}
                              onChange={(e) => {
                                setConfigFormData({ ...configFormData, league_start_date: e.target.value });
                                // Clear error when user changes the date
                                if (configErrors.league_start_date) {
                                  setConfigErrors({ ...configErrors, league_start_date: undefined });
                                }
                              }}
                              className={`w-full px-3 py-2.5 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] ${
                                configErrors.league_start_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                            {configErrors.league_start_date && (
                              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{configErrors.league_start_date}</p>
                            )}
                          </>
                        )}
                        {configFormData.use_first_match_for_start_date && matches.length === 0 && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Date will be calculated when matches are added
                          </p>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id="config_use_last_match"
                            checked={configFormData.use_last_match_for_end_date}
                            onChange={(e) => {
                              const useCalculated = e.target.checked;
                              setConfigFormData({ 
                                ...configFormData, 
                                use_last_match_for_end_date: useCalculated,
                                // Clear manual date when using calculated date
                                league_end_date: useCalculated ? '' : configFormData.league_end_date
                              });
                              // Clear errors when toggling
                              if (configErrors.league_end_date) {
                                setConfigErrors({ ...configErrors, league_end_date: undefined });
                              }
                            }}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label htmlFor="config_use_last_match" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Use last match date
                          </label>
                        </div>
                        {!configFormData.use_last_match_for_end_date && (
                          <>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">League End Date</label>
                            <input
                              type="date"
                              value={configFormData.league_end_date}
                              onChange={(e) => {
                                setConfigFormData({ ...configFormData, league_end_date: e.target.value });
                                // Clear error when user changes the date
                                if (configErrors.league_end_date) {
                                  setConfigErrors({ ...configErrors, league_end_date: undefined });
                                }
                              }}
                              className={`w-full px-3 py-2.5 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] ${
                                configErrors.league_end_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                            {configErrors.league_end_date && (
                              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{configErrors.league_end_date}</p>
                            )}
                          </>
                        )}
                        {configFormData.use_last_match_for_end_date && matches.length === 0 && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Date will be calculated when matches are added
                          </p>
                        )}
                      </div>
                      </div>
                    </div>

                    {/* Timezone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
                      <TimezoneSelect
                        value={configFormData.timezone}
                        onChange={(tz) => setConfigFormData({ ...configFormData, timezone: tz.value })}
                        className="timezone-select"
                        classNames={{
                          control: (state) => `!bg-white !border-gray-300 dark:!bg-gray-700 dark:!border-gray-600 dark:!text-gray-100 ${state.isFocused ? '!border-primary-500 !ring-primary-500 dark:!border-primary-400 dark:!ring-primary-400' : ''}`,
                          singleValue: () => '!text-gray-900 dark:!text-gray-100',
                          input: () => '!text-gray-900 dark:!text-gray-100',
                          menu: () => '!bg-white dark:!bg-gray-700 !border-gray-300 dark:!border-gray-600',
                          option: (state) => `!text-gray-900 dark:!text-gray-100 ${state.isFocused ? '!bg-primary-100 dark:!bg-primary-900/20' : ''} ${state.isSelected ? '!bg-primary-500 dark:!bg-primary-600 !text-white' : ''}`,
                          placeholder: () => '!text-gray-400 dark:!text-gray-500',
                          menuList: () => 'dark:!bg-gray-700',
                          indicatorSeparator: () => '!bg-gray-300 dark:!bg-gray-600',
                          dropdownIndicator: () => '!text-gray-400 dark:!text-gray-400',
                          clearIndicator: () => '!text-gray-400 dark:!text-gray-400',
                        }}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Used for all matches and calendar events for this team
                      </p>
                    </div>

                    {/* Max Combined NTRP Rating */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Combined NTRP Rating (optional)
                      </label>
                      <select
                        value={configFormData.max_combined_ntrp_rating || ''}
                        onChange={(e) => setConfigFormData({ ...configFormData, max_combined_ntrp_rating: e.target.value ? parseFloat(e.target.value) : null })}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                      >
                        <option value="">Select a rating (optional)</option>
                        {Array.from({ length: 26 }, (_, i) => {
                          const value = 1.5 + (i * 0.5);
                          return (
                            <option key={value} value={value}>
                              {value.toFixed(1)}
                            </option>
                          );
                        })}
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Maximum combined NTRP rating for doubles lineups (e.g., 6.0 means two players' ratings must sum to 6.0 or less)
                      </p>
                    </div>

                    {/* Match Win Criteria */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Match Win Criteria (optional)
                      </label>
                      <select
                        value={configFormData.match_win_criteria || ''}
                        onChange={(e) => setConfigFormData({ ...configFormData, match_win_criteria: e.target.value || null })}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                      >
                        <option value="">Not set</option>
                        <option value="team_wins">Team Wins</option>
                        <option value="total_games">Total Games</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        How the match winner is determined. Team Wins: team winning the most individual lineups. Total Games: sum of individual games won across all lineups.
                      </p>
                    </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          onClick={handleCancelConfigEdit}
                          disabled={savingConfig}
                          variant="outline"
                          size="lg"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveConfig}
                          disabled={savingConfig || !configFormData.name.trim()}
                          variant="primary"
                          size="lg"
                        >
                          {savingConfig ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Team Name</dt>
                        <dd className="text-base font-medium text-gray-900 dark:text-gray-100">{selectedTeam.name}</dd>
                      </div>
                      {selectedTeam.league && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">League</dt>
                          <dd className="text-base text-gray-900 dark:text-gray-100">{selectedTeam.league}</dd>
                        </div>
                      )}
                      {selectedTeam.section && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Section</dt>
                          <dd className="text-base text-gray-900 dark:text-gray-100">{selectedTeam.section}</dd>
                        </div>
                      )}
                      {selectedTeam.district_area && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">District/Area</dt>
                          <dd className="text-base text-gray-900 dark:text-gray-100">{selectedTeam.district_area}</dd>
                        </div>
                      )}
                      {selectedTeam.flight_subflight && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Flight/Sub-Flight</dt>
                          <dd className="text-base text-gray-900 dark:text-gray-100">{selectedTeam.flight_subflight}</dd>
                        </div>
                      )}
                      {selectedTeam.usta_team_id && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">USTA Team ID</dt>
                          <dd className="text-base text-gray-900 dark:text-gray-100 font-mono">{selectedTeam.usta_team_id}</dd>
                        </div>
                      )}
                      {selectedTeam.timezone && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Timezone</dt>
                          <dd className="text-base text-gray-900 dark:text-gray-100">{selectedTeam.timezone}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Max Combined NTRP Rating</dt>
                        <dd className="text-base text-gray-900 dark:text-gray-100">
                          {selectedTeam.max_combined_ntrp_rating !== null && selectedTeam.max_combined_ntrp_rating !== undefined
                            ? selectedTeam.max_combined_ntrp_rating.toFixed(1)
                            : 'Not set'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Match Win Criteria</dt>
                        <dd className="text-base text-gray-900 dark:text-gray-100">
                          {selectedTeam.match_win_criteria === 'team_wins' ? 'Team Wins'
                            : selectedTeam.match_win_criteria === 'total_games' ? 'Total Games'
                            : 'Not set'}
                        </dd>
                      </div>
                      {(selectedTeam.league_start_date || selectedTeam.league_end_date) && (
                        <>
                          {selectedTeam.league_start_date && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                League Start Date
                                {selectedTeam.use_first_match_for_start_date && (
                                  <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">(from first match)</span>
                                )}
                              </dt>
                              <dd className="text-base text-gray-900 dark:text-gray-100">
                                {new Date(selectedTeam.league_start_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </dd>
                            </div>
                          )}
                          {selectedTeam.league_end_date && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                League End Date
                                {selectedTeam.use_last_match_for_end_date && (
                                  <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">(from last match)</span>
                                )}
                              </dt>
                              <dd className="text-base text-gray-900 dark:text-gray-100">
                                {new Date(selectedTeam.league_end_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </dd>
                            </div>
                          )}
                        </>
                      )}
                    </dl>
                  )}
                </div>

                {/* Reminders Card */}
                <TeamReminderSettingsCard
                  teamId={selectedTeam.id}
                  playerDaysAhead={selectedTeam.reminder_player_days_ahead ?? 3}
                  captainDaysAhead={selectedTeam.reminder_captain_days_ahead ?? 5}
                  playerEnabled={selectedTeam.reminder_player_enabled ?? true}
                  captainEnabled={selectedTeam.reminder_captain_enabled ?? true}
                  canManage={canManageTeam}
                  onSaved={({ playerDaysAhead, captainDaysAhead, playerEnabled, captainEnabled }) => {
                    setSelectedTeam((prev) =>
                      prev
                        ? {
                            ...prev,
                            reminder_player_days_ahead: playerDaysAhead,
                            reminder_captain_days_ahead: captainDaysAhead,
                            reminder_player_enabled: playerEnabled,
                            reminder_captain_enabled: captainEnabled,
                          }
                        : prev
                    );
                    refreshTeams();
                  }}
                />

                {/* Lineup Format Card */}
                {selectedTeam.lineup_format && (
                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
                      {/* Header with edit button */}
                      <div className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 dark:border-gray-700/50">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                            <Layout className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lineup Format</h3>
                        </div>
                        {canManageTeam && (
                          <Button
                            onClick={handleEditLineupFormatClick}
                            variant="outline"
                            size="sm"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        )}
                      </div>

                      {/* Summary stats — grid layout matching dashboard */}
                      <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 dark:border-gray-700/50">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-0.5">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Lines</span>
                              <span
                                className="inline-flex cursor-help"
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltipState({
                                    text: "The number of matches played on match day. For example, 5 lines means 5 unique matches are played (10 players total for doubles).",
                                    x: rect.left + rect.width / 2,
                                    y: rect.top - 10
                                  });
                                }}
                                onMouseLeave={() => setTooltipState(null)}
                              >
                                <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                              </span>
                            </div>
                            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{selectedTeam.lineup_format.total_lines}</span>
                          </div>
                          <div className="text-center border-x border-gray-100 dark:border-gray-700/50">
                            <div className="flex items-center justify-center gap-1 mb-0.5">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Max Sets</span>
                              <span
                                className="inline-flex cursor-help"
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltipState({
                                    text: "The maximum number of sets played per line/match. Typically 3 sets (even if the 3rd set is a tie-break).",
                                    x: rect.left + rect.width / 2,
                                    y: rect.top - 10
                                  });
                                }}
                                onMouseLeave={() => setTooltipState(null)}
                              >
                                <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                              </span>
                            </div>
                            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{selectedTeam.lineup_format.max_sets_per_line}</span>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-0.5">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Multi-Line</span>
                              <span
                                className="inline-flex cursor-help"
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltipState({
                                    text: "Allows a player to appear in multiple lineups for a match",
                                    x: rect.left + rect.width / 2,
                                    y: rect.top - 10
                                  });
                                }}
                                onMouseLeave={() => setTooltipState(null)}
                              >
                                <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                              </span>
                            </div>
                            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{(selectedTeam.lineup_format as any).allow_multi_line_selection ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Position rows with match type icons — matching dashboard */}
                      <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {(() => {
                          const sorted = [...selectedTeam.lineup_format.positions].sort((a, b) => a.position_number - b.position_number);
                          const typeCounts: Record<string, number> = {};
                          sorted.forEach(p => { typeCounts[p.match_type] = (typeCounts[p.match_type] || 0) + 1; });
                          const typeIndex: Record<string, number> = {};
                          return sorted.map(pos => {
                            typeIndex[pos.match_type] = (typeIndex[pos.match_type] || 0) + 1;
                            const label = typeCounts[pos.match_type] > 1
                              ? `${formatMatchType(pos.match_type)} ${typeIndex[pos.match_type]}`
                              : formatMatchType(pos.match_type);
                            return (
                              <div
                                key={pos.position_number}
                                className="flex items-center gap-2.5 px-4 py-2.5 md:px-6 md:py-3"
                              >
                                <MatchTypeIcon matchType={pos.match_type} iconSize="w-5 h-5" />
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                {/* Competitive Intelligence Card */}
                {isTennisRecordEnabled && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                      <Brain className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-200">Competitive Intelligence</h3>
                      <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Fetch player ratings from TennisRecord.com</p>
                    </div>
                  </div>
                  {canManageTeam && !isEditingIntelligence && (
                    <Button
                      onClick={handleEditIntelligence}
                      variant="outline"
                      size="md"
                      className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-400 dark:hover:border-purple-600"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
                
                {isEditingIntelligence ? (
                  <div className="space-y-4 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-purple-900 dark:text-purple-200 mb-2">
                        TennisRecord Team URL
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="url"
                          value={tennisRecordUrl}
                          onChange={(e) => setTennisRecordUrl(e.target.value)}
                          placeholder="https://www.tennisrecord.com/adult/teamprofile.aspx?..."
                          className="flex-1 px-4 py-3 border-2 border-purple-300 dark:border-purple-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleCancelIntelligenceEdit}
                            variant="outline"
                            size="lg"
                            className="sm:w-auto border-gray-300"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                          <Button
                            onClick={handleTennisRecordUrlUpdate}
                            variant="primary"
                            size="lg"
                            className="sm:w-auto bg-purple-600 hover:bg-purple-700 text-white border-purple-600"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Save
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                        Example: https://www.tennisrecord.com/adult/teamprofile.aspx?teamname=YourTeam&year=2026
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 mt-6">
                    {tennisRecordUrl ? (
                      <div>
                        <div className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-2">TennisRecord Team URL</div>
                        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-800">
                          <p className="text-sm text-purple-900 dark:text-purple-200 break-all font-medium">{tennisRecordUrl}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-800 text-center">
                        <p className="text-sm text-purple-700 dark:text-purple-300">No TennisRecord URL configured</p>
                      </div>
                    )}
                    
                    {canManageTeam && tennisRecordUrl && (
                      <div className="pt-4 border-t border-purple-200 dark:border-purple-800">
                        <Button
                          onClick={handleFetchIntelligence}
                          disabled={fetchingIntelligence}
                          variant="primary"
                          size="lg"
                          fullWidth
                          className="sm:w-auto bg-purple-600 hover:bg-purple-700 text-white border-purple-600"
                        >
                          {fetchingIntelligence ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Fetching Intelligence...
                            </>
                          ) : (
                            <>
                              <Target className="w-4 h-4 mr-2" />
                              Fetch Competitive Intelligence
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-3">
                          This will scrape player ratings for your team and all opponents from TennisRecord, and fetch past match results with scores.
                        </p>
                        {selectedTeam.competitive_intel_last_fetch && (
                          <p className="text-xs text-purple-500 dark:text-purple-400 mt-2">
                            Last fetched: {new Date(selectedTeam.competitive_intel_last_fetch).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
              </div>

              {/* Danger Zone Card */}
              {canManageTeam && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border-2 border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-red-900 dark:text-red-200">Danger Zone</h3>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">Irreversible and destructive actions</p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Button
                      onClick={() => {
                        setDeleteConfirmationName('');
                        setShowDeleteTeamModal(true);
                      }}
                      variant="outline"
                      size="lg"
                      className="border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-700 dark:hover:border-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Team
                    </Button>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && selectedTeam && (
            <AnalyticsTab teamId={selectedTeam.id} />
          )}
        </div>
      </div>

      {/* Import Report Modal */}
      {showImportReport && importReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-none sm:rounded-lg shadow-xl max-w-3xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">Import Complete</h2>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {importReport.updated_availability} availability records imported
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowImportReport(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0 ml-2"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-primary-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-primary-900">{importReport.created_matches}</div>
                  <div className="text-sm text-primary-700">Matches Created</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-900">{importReport.created_users}</div>
                  <div className="text-sm text-green-700">Players Created</div>
                </div>
              </div>

              {/* Matches in Import */}
              {importReport.created_matches_list && importReport.created_matches_list.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Matches in Import
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-64 overflow-y-auto">
                    {importReport.created_matches_list.map((match: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-gray-200 last:border-0">
                        <div className="flex items-center gap-2">
                          {match.is_new && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">NEW</span>
                          )}
                          <span className="font-medium text-gray-900">{match.opponent}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span>{match.date}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            match.home_away === 'home' ? 'bg-green-100 text-green-800' : 
                            match.home_away === 'away' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {match.home_away === 'home' ? 'Home' : match.home_away === 'away' ? 'Away' : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Matches Without Availability */}
              {importReport.matches_without_availability && importReport.matches_without_availability.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Matches Without Availability
                  </h3>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 mb-2">
                      No availability data found in the file for these matches:
                    </p>
                    <div className="space-y-1">
                      {importReport.matches_without_availability.map((match: any, idx: number) => (
                        <div key={idx} className="text-sm text-yellow-900 flex items-center justify-between py-1">
                          <span className="font-medium">{match.opponent}</span>
                          <span className="text-yellow-700">{match.date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Players in Import */}
              {importReport.created_players_list && importReport.created_players_list.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Players in Import
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                      {importReport.created_players_list.map((player: any, idx: number) => (
                        <div key={idx} className="text-sm text-gray-900 py-1 flex items-center gap-2">
                          <span>• {player.name}</span>
                          {player.is_new && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">NEW</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Players Without Availability */}
              {importReport.players_without_availability && importReport.players_without_availability.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Players Without Availability
                  </h3>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-800 mb-2">
                      These players were in the file but had no availability data:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {importReport.players_without_availability.map((player: any, idx: number) => (
                        <div key={idx} className="text-sm text-orange-900 py-1">
                          • {player.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Team Members Not in Import */}
              {importReport.team_members_not_in_import && importReport.team_members_not_in_import.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Team Members Not in Import
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800 mb-2">
                      These existing team members were not included in the import file:
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {importReport.team_members_not_in_import.map((member: any, idx: number) => (
                        <div key={idx} className="text-sm text-red-900 py-1 flex items-center justify-between">
                          <span>• {member.name}</span>
                          <span className="text-xs text-red-700">{member.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Team Matches Not in Import */}
              {importReport.team_matches_not_in_import && importReport.team_matches_not_in_import.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Team Matches Not in Import
                  </h3>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 mb-2">
                      These existing team matches were not included in the import file:
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {importReport.team_matches_not_in_import.map((match: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-purple-200 last:border-0">
                          <span className="font-medium text-purple-900">{match.opponent}</span>
                          <div className="flex items-center gap-2 text-purple-700">
                            <span>{match.date}</span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              {match.home_away === 'home' ? 'Home' : match.home_away === 'away' ? 'Away' : 'Unknown'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <Button
                onClick={() => setShowImportReport(false)}
                variant="primary"
                size="lg"
                fullWidth
                className="sm:w-auto"
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Blocking Progress Modal */}
      {(fetchingIntelligence || (competitiveIntel && competitiveIntel.team_players?.some((p: PlayerWithRating) => p.pending_ntrp_update))) && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 dark:bg-opacity-90 transition-opacity" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div>
                {fetchingIntelligence && (
                  <>
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/20">
                      <svg className="animate-spin h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100" id="modal-title">
                        Fetching Competitive Intelligence
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Please wait while we fetch player ratings and match data from TennisRecord.com. This may take a few minutes.
                        </p>
                      </div>
                    </div>
                  </>
                )}
                {!fetchingIntelligence && competitiveIntel && competitiveIntel.team_players?.some((p: PlayerWithRating) => p.pending_ntrp_update) && (
                  <div className="mt-3 text-center sm:mt-5">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
                      <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mt-3" id="modal-title">
                      Rating Updates Available
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Competitive intelligence fetch completed. Please review the pending rating updates below.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress Messages - only show when fetching */}
              {fetchingIntelligence && (
                <div 
                  id="modal-progress-container"
                  className="mt-6 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-md p-4 max-h-96 overflow-y-auto"
                >
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Progress</h4>
                  <div className="space-y-2">
                    {progressMessages.map((msg, idx) => {
                    // Only show spinner for the last message if it's in-progress and not complete/error
                    const isLastMessage = idx === progressMessages.length - 1;
                    const isInProgressType = ['start', 'fetching_team', 'scraping_opponent', 'creating_records', 'creating_users', 'fetching_results', 'processing_results', 'results_complete', 'fetching_gender'].includes(msg.type);
                    const showSpinner = isLastMessage && isInProgressType && fetchingIntelligence;
                    
                    return (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        {/* Icon based on message type and position */}
                        {showSpinner && (
                          <svg className="animate-spin w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        {!showSpinner && msg.type !== 'error' && (
                          <svg className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {msg.type === 'error' && (
                          <svg className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <div className="flex-1">
                          <p className={`${msg.type === 'error' ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                            {msg.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {msg.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Pending Rating Updates Section */}
              {!fetchingIntelligence && competitiveIntel && competitiveIntel.team_players?.some((p: PlayerWithRating) => p.pending_ntrp_update) && (
                <div className="mt-6 pt-6 border-t border-purple-200 dark:border-purple-700">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Pending Rating Updates
                    </h4>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                    The following players have rating updates from TennisRecord.com that need your approval:
                  </p>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50/80 dark:bg-gray-800/50/50 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Player</th>
                          <th className="text-center py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Current</th>
                          <th className="text-center py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Fetched</th>
                          {canManageTeam && (
                            <th className="text-center py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Action</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {competitiveIntel.team_players
                          .filter((p: PlayerWithRating) => p.pending_ntrp_update)
                          .map((player: PlayerWithRating) => (
                            <tr key={player.id} className="bg-white dark:bg-gray-800">
                              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                                {getDisplayName(player)}
                              </td>
                              <td className="py-2 px-3 text-center text-gray-700 dark:text-gray-300">
                                {player.current_ntrp_rating !== null && player.current_ntrp_rating !== undefined 
                                  ? formatNtrpRating(player.current_ntrp_rating) 
                                  : 'N/A'}
                              </td>
                              <td className="py-2 px-3 text-center font-medium text-purple-700 dark:text-purple-300">
                                {player.ntrp_rating !== null && player.ntrp_rating !== undefined 
                                  ? formatNtrpRating(player.ntrp_rating) 
                                  : 'N/A'}
                              </td>
                              {canManageTeam && (
                                <td className="py-2 px-3 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={() => handleConfirmRatingUpdate(player.id, 'apply')}
                                      className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                                      title="Apply rating update"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleConfirmRatingUpdate(player.id, 'ignore')}
                                      className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                                      title="Ignore rating update"
                                    >
                                      Ignore
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-5 sm:mt-6">
                {fetchingIntelligence ? (
                  <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                    Please do not close this window or navigate away during this process.
                  </p>
                ) : competitiveIntel && competitiveIntel.team_players?.some((p: PlayerWithRating) => p.pending_ntrp_update) ? (
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        // Check if there are pending changes to review
                        const hasPendingChanges = pendingChanges && pendingChanges.total_pending > 0;
                        
                        setFetchingIntelligence(false);
                        setProgressMessages([]);
                        setCompetitiveIntel(null);
                        
                        if (hasPendingChanges) {
                          // Transition to pending changes modal
                          setShowPendingChangesModal(true);
                        } else {
                          // Close and refresh
                          fetchMatches(selectedTeam?.id || 0);
                          if (activeTab === 'strategize') {
                            fetchStrategizeData(selectedTeam?.id || 0);
                          }
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      {pendingChanges && pendingChanges.total_pending > 0 ? 'Skip to Pending Changes' : 'Close Without Reviewing'}
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setFetchingIntelligence(false);
                        setProgressMessages([]);
                        fetchMatches(selectedTeam?.id || 0);
                        if (activeTab === 'strategize') {
                          fetchStrategizeData(selectedTeam?.id || 0);
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Changes Confirmation Modal */}
      {showPendingChangesModal && pendingChanges && pendingChanges.total_pending > 0 && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="pending-changes-modal" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 dark:bg-opacity-90 transition-opacity" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30">
                  <AlertTriangle className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100" id="pending-changes-modal">
                    Review Changes from TennisRecords.com
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {pendingChanges.total_pending} change{pendingChanges.total_pending !== 1 ? 's' : ''} found that need your approval. Review and approve/ignore each change below.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bulk Actions Bar */}
              {(selectedMatchIds.size > 0 || selectedUserIds.size > 0 || selectedLineupIds.size > 0) && (
                <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md flex items-center justify-between">
                  <span className="text-sm font-medium text-primary-900 dark:text-primary-300">
                    {selectedMatchIds.size + selectedUserIds.size + selectedLineupIds.size} item{(selectedMatchIds.size + selectedUserIds.size + selectedLineupIds.size) !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleBulkConfirm}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                    >
                      Approve Selected
                    </button>
                    <button
                      onClick={handleBulkIgnore}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      Ignore Selected
                    </button>
                  </div>
                </div>
              )}

              {/* Tabs for different change types */}
              <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {pendingChanges.matches?.length > 0 && (
                    <button
                      className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                    >
                      Matches ({pendingChanges.matches.length})
                    </button>
                  )}
                  {pendingChanges.users?.length > 0 && (
                    <button
                      className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                    >
                      Players ({pendingChanges.users.length})
                    </button>
                  )}
                  {pendingChanges.lineups?.length > 0 && (
                    <button
                      className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                    >
                      Lineups ({pendingChanges.lineups.length})
                    </button>
                  )}
                </nav>
              </div>

              <div className="mt-6 max-h-96 overflow-y-auto">
                {/* Pending Matches Section */}
                {pendingChanges.matches?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Matches</h4>
                    
                    {/* Desktop view */}
                    <div className="hidden md:block border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50/80 dark:bg-gray-800/50/50">
                          <tr>
                            <th className="w-10 px-3 py-2">
                              <input
                                type="checkbox"
                                checked={pendingChanges.matches.every((m: any) => selectedMatchIds.has(m.id))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedMatchIds(new Set(pendingChanges.matches.map((m: any) => m.id)));
                                  } else {
                                    setSelectedMatchIds(new Set());
                                  }
                                }}
                                className="rounded"
                              />
                            </th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Type</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Opponent</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Date</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">What's Changing</th>
                            <th className="text-center py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {pendingChanges.matches.map((match: any) => (
                            <tr key={match.id} className="bg-white dark:bg-gray-800">
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedMatchIds.has(match.id)}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedMatchIds);
                                    if (e.target.checked) {
                                      newSet.add(match.id);
                                    } else {
                                      newSet.delete(match.id);
                                    }
                                    setSelectedMatchIds(newSet);
                                  }}
                                  className="rounded"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-1 rounded ${match.change_type === 'create' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>
                                  {match.change_type === 'create' ? 'New' : 'Update'}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                                {match.opponent_name}
                              </td>
                              <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                                {new Date(match.match_date).toLocaleDateString()}
                              </td>
                              <td className="py-2 px-3">
                                {match.change_type === 'update' && match.differences ? (
                                  <div className="space-y-1 text-xs">
                                    {Object.entries(match.differences).map(([field, change]: [string, any]) => (
                                      <div key={field} className="flex items-start gap-1">
                                        <span className="font-semibold capitalize">{field.replace(/_/g, ' ')}:</span>
                                        <span className="text-red-600 dark:text-red-400 line-through">{change.old || 'N/A'}</span>
                                        <span>→</span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">{change.new || 'N/A'}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">New match</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleConfirmPendingMatch(match.id, 'approve')}
                                    className="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleConfirmPendingMatch(match.id, 'ignore')}
                                    className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile view */}
                    <div className="md:hidden space-y-3">
                      {pendingChanges.matches.map((match: any) => (
                        <div key={match.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-start gap-3 mb-3">
                            <input
                              type="checkbox"
                              checked={selectedMatchIds.has(match.id)}
                              onChange={(e) => {
                                const newSet = new Set(selectedMatchIds);
                                if (e.target.checked) {
                                  newSet.add(match.id);
                                } else {
                                  newSet.delete(match.id);
                                }
                                setSelectedMatchIds(newSet);
                              }}
                              className="rounded mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{match.opponent_name}</span>
                                <span className={`text-xs px-2 py-1 rounded ${match.change_type === 'create' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                  {match.change_type === 'create' ? 'New' : 'Update'}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                <div>{new Date(match.match_date).toLocaleDateString()}</div>
                                {match.location && <div>{match.location}</div>}
                              </div>
                              {match.change_type === 'update' && match.differences && (
                                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">What's Changing:</div>
                                  <div className="space-y-1 text-xs">
                                    {Object.entries(match.differences).map(([field, change]: [string, any]) => (
                                      <div key={field} className="flex flex-col">
                                        <span className="font-semibold capitalize text-gray-900 dark:text-gray-100">{field.replace(/_/g, ' ')}:</span>
                                        <div className="flex items-center gap-1 ml-2">
                                          <span className="text-red-600 dark:text-red-400 line-through">{change.old || 'N/A'}</span>
                                          <span>→</span>
                                          <span className="text-green-600 dark:text-green-400 font-medium">{change.new || 'N/A'}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmPendingMatch(match.id, 'approve')}
                              className="flex-1 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleConfirmPendingMatch(match.id, 'ignore')}
                              className="flex-1 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              Ignore
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Users Section */}
                {pendingChanges.users?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Players</h4>
                    
                    {/* Desktop view */}
                    <div className="hidden md:block border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50/80 dark:bg-gray-800/50/50">
                          <tr>
                            <th className="w-10 px-3 py-2">
                              <input
                                type="checkbox"
                                checked={pendingChanges.users.every((u: any) => selectedUserIds.has(u.id))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUserIds(new Set(pendingChanges.users.map((u: any) => u.id)));
                                  } else {
                                    setSelectedUserIds(new Set());
                                  }
                                }}
                                className="rounded"
                              />
                            </th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Action</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Name</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Rating</th>
                            <th className="text-center py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {pendingChanges.users.map((user: any) => (
                            <tr key={user.id} className="bg-white dark:bg-gray-800">
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedUserIds.has(user.id)}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedUserIds);
                                    if (e.target.checked) {
                                      newSet.add(user.id);
                                    } else {
                                      newSet.delete(user.id);
                                    }
                                    setSelectedUserIds(newSet);
                                  }}
                                  className="rounded"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-1 rounded ${user.change_type === 'both' || user.change_type === 'create_user' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                                  {user.change_type === 'both' ? 'New User' : user.change_type === 'create_user' ? 'Create' : 'Add to Team'}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                                {user.name}
                              </td>
                              <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                                {user.ntrp_rating ? user.ntrp_rating.toFixed(1) : '-'}
                              </td>
                              <td className="py-2 px-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleConfirmPendingUser(user.id, 'approve')}
                                    className="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleConfirmPendingUser(user.id, 'ignore')}
                                    className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile view */}
                    <div className="md:hidden space-y-3">
                      {pendingChanges.users.map((user: any) => (
                        <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-start gap-3 mb-3">
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(user.id)}
                              onChange={(e) => {
                                const newSet = new Set(selectedUserIds);
                                if (e.target.checked) {
                                  newSet.add(user.id);
                                } else {
                                  newSet.delete(user.id);
                                }
                                setSelectedUserIds(newSet);
                              }}
                              className="rounded mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{user.name}</span>
                                <span className={`text-xs px-2 py-1 rounded ${user.change_type === 'both' || user.change_type === 'create_user' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                  {user.change_type === 'both' ? 'New' : user.change_type === 'create_user' ? 'Create' : 'Add'}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {user.ntrp_rating && <div>Rating: {user.ntrp_rating.toFixed(1)}</div>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmPendingUser(user.id, 'approve')}
                              className="flex-1 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleConfirmPendingUser(user.id, 'ignore')}
                              className="flex-1 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              Ignore
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Lineups Section */}
                {pendingChanges.lineups?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Lineups (Player Changes)</h4>
                    
                    {/* Desktop view */}
                    <div className="hidden md:block border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50/80 dark:bg-gray-800/50/50">
                          <tr>
                            <th className="w-10 px-3 py-2">
                              <input
                                type="checkbox"
                                checked={pendingChanges.lineups.every((l: any) => selectedLineupIds.has(l.id))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedLineupIds(new Set(pendingChanges.lineups.map((l: any) => l.id)));
                                  } else {
                                    setSelectedLineupIds(new Set());
                                  }
                                }}
                                className="rounded"
                              />
                            </th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Match</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Position</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">New Players</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">What's Changing</th>
                            <th className="text-center py-2 px-3 font-semibold text-gray-900 dark:text-gray-100 text-xs">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {pendingChanges.lineups.map((lineup: any) => (
                            <tr key={lineup.id} className="bg-white dark:bg-gray-800">
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedLineupIds.has(lineup.id)}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedLineupIds);
                                    if (e.target.checked) {
                                      newSet.add(lineup.id);
                                    } else {
                                      newSet.delete(lineup.id);
                                    }
                                    setSelectedLineupIds(newSet);
                                  }}
                                  className="rounded"
                                />
                              </td>
                              <td className="py-2 px-3 text-gray-700 dark:text-gray-300 truncate max-w-xs">
                                {lineup.match_opponent}
                              </td>
                              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                                #{lineup.position_number}
                              </td>
                              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                                {lineup.player1_name}
                                {lineup.player2_name && ` / ${lineup.player2_name}`}
                              </td>
                              <td className="py-2 px-3">
                                {lineup.differences ? (
                                  <div className="space-y-1 text-xs">
                                    {lineup.differences.player1_id && (
                                      <div className="flex items-start gap-1">
                                        <span className="font-semibold">Player 1:</span>
                                        <span className="text-red-600 dark:text-red-400 line-through">{lineup.differences.player1_id.old_name || 'N/A'}</span>
                                        <span>→</span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">{lineup.differences.player1_id.new_name}</span>
                                      </div>
                                    )}
                                    {lineup.differences.player2_id && (
                                      <div className="flex items-start gap-1">
                                        <span className="font-semibold">Player 2:</span>
                                        <span className="text-red-600 dark:text-red-400 line-through">{lineup.differences.player2_id.old_name || 'N/A'}</span>
                                        <span>→</span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">{lineup.differences.player2_id.new_name}</span>
                                      </div>
                                    )}
                                    {lineup.differences.score && (
                                      <div className="flex items-start gap-1">
                                        <span className="font-semibold">Score:</span>
                                        <span className="text-red-600 dark:text-red-400 line-through">{lineup.differences.score.old || 'N/A'}</span>
                                        <span>→</span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">{lineup.differences.score.new || 'N/A'}</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">-</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleConfirmPendingLineup(lineup.id, 'approve')}
                                    className="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleConfirmPendingLineup(lineup.id, 'ignore')}
                                    className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile view */}
                    <div className="md:hidden space-y-3">
                      {pendingChanges.lineups.map((lineup: any) => (
                        <div key={lineup.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-start gap-3 mb-3">
                            <input
                              type="checkbox"
                              checked={selectedLineupIds.has(lineup.id)}
                              onChange={(e) => {
                                const newSet = new Set(selectedLineupIds);
                                if (e.target.checked) {
                                  newSet.add(lineup.id);
                                } else {
                                  newSet.delete(lineup.id);
                                }
                                setSelectedLineupIds(newSet);
                              }}
                              className="rounded mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">Position #{lineup.position_number}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{lineup.match_opponent}</span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                  {lineup.player1_name}
                                  {lineup.player2_name && ` / ${lineup.player2_name}`}
                                </div>
                                {lineup.score && <div>Score: {lineup.score}</div>}
                              </div>
                              {lineup.differences && (
                                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">What's Changing:</div>
                                  <div className="space-y-1 text-xs">
                                    {lineup.differences.player1_id && (
                                      <div className="flex flex-col">
                                        <span className="font-semibold text-gray-900 dark:text-gray-100">Player 1:</span>
                                        <div className="flex items-center gap-1 ml-2">
                                          <span className="text-red-600 dark:text-red-400 line-through">{lineup.differences.player1_id.old_name || 'N/A'}</span>
                                          <span>→</span>
                                          <span className="text-green-600 dark:text-green-400 font-medium">{lineup.differences.player1_id.new_name}</span>
                                        </div>
                                      </div>
                                    )}
                                    {lineup.differences.player2_id && (
                                      <div className="flex flex-col">
                                        <span className="font-semibold text-gray-900 dark:text-gray-100">Player 2:</span>
                                        <div className="flex items-center gap-1 ml-2">
                                          <span className="text-red-600 dark:text-red-400 line-through">{lineup.differences.player2_id.old_name || 'N/A'}</span>
                                          <span>→</span>
                                          <span className="text-green-600 dark:text-green-400 font-medium">{lineup.differences.player2_id.new_name}</span>
                                        </div>
                                      </div>
                                    )}
                                    {lineup.differences.score && (
                                      <div className="flex flex-col">
                                        <span className="font-semibold text-gray-900 dark:text-gray-100">Score:</span>
                                        <div className="flex items-center gap-1 ml-2">
                                          <span className="text-red-600 dark:text-red-400 line-through">{lineup.differences.score.old || 'N/A'}</span>
                                          <span>→</span>
                                          <span className="text-green-600 dark:text-green-400 font-medium">{lineup.differences.score.new || 'N/A'}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmPendingLineup(lineup.id, 'approve')}
                              className="flex-1 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleConfirmPendingLineup(lineup.id, 'ignore')}
                              className="flex-1 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              Ignore
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={() => {
                    setShowPendingChangesModal(false);
                    setPendingChanges(null);
                    setSelectedMatchIds(new Set());
                    setSelectedUserIds(new Set());
                    setSelectedLineupIds(new Set());
                    setFetchingIntelligence(false);
                    setProgressMessages([]);
                    fetchMatches(selectedTeam?.id || 0);
                    if (activeTab === 'strategize') {
                      fetchStrategizeData(selectedTeam?.id || 0);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
                >
                  Close and Skip Remaining
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Match Results Entry Modal */}
      {showResultsModal && selectedMatchForResults && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg shadow-xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">Enter Match Results</h2>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    vs {selectedMatchForResults.opponent_name}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseResultsModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0 ml-2"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-6">
              {loadingResults ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
                </div>
              ) : (
                <>
                  {/* Match Details */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="space-y-2">
                      {(() => {
                        const formatted = formatMatchDateFull(selectedMatchForResults.match_date);
                        return (
                          <>
                            <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              {formatted.date}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              {formatted.time}
                            </p>
                          </>
                        );
                      })()}
                      {(selectedMatchForResults.location || selectedMatchForResults.formatted_address) && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          {selectedMatchForResults.formatted_address || selectedMatchForResults.location}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Line Results */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                      Line Results
                    </label>
                    <div className="space-y-4 max-h-[calc(90vh-400px)] overflow-y-auto">
                      {resultsFormData.line_results.map((lineResult) => {
                        const positionInfo = selectedTeam.lineup_format?.positions?.find(
                          (p: any) => p.position_number === lineResult.position_number
                        );
                        const matchType = positionInfo?.match_type || '';
                        const isDoubles = matchType ? matchType.includes('doubles') : false;
                        
                        // Count how many positions have the same match type
                        const allPositions = selectedTeam.lineup_format?.positions || [];
                        const matchTypeCount = allPositions.filter(
                          (p: any) => p.match_type === matchType
                        ).length;
                        
                        // Determine the index within positions of the same match type
                        const sameTypePositions = allPositions.filter(
                          (p: any) => p.match_type === matchType
                        ).sort((a: any, b: any) => a.position_number - b.position_number);
                        const positionIndexWithinType = sameTypePositions.findIndex(
                          (p: any) => p.position_number === lineResult.position_number
                        ) + 1;
                        
                        // Create the display label
                        const formattedType = formatMatchType(matchType);
                        const displayLabel = matchTypeCount > 1 
                          ? `${formattedType} ${positionIndexWithinType}`
                          : formattedType;
                        
                        const hasScore = lineResult.score && typeof lineResult.score === 'string' && lineResult.score.trim().length > 0;
                        
                        return (
                          <div key={lineResult.position_number} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-1.5">
                                <MatchTypeIcon matchType={matchType} iconSize="w-5 h-5" />
                                {displayLabel || `Position ${lineResult.position_number}`}
                              </h4>
                            </div>
                            
                            <div className="space-y-4">
                              {/* Players: Team on Left, Opponents on Right */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Left Side: Team Players */}
                                <div className="space-y-4">
                                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Team</div>
                                  
                                  {/* Player 1 */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Player 1
                                    </label>
                                    {(() => {
                                      // Get eligible players for player1
                                      // Include current position state so filtering considers player2 if already set
                                      const currentLineup: ValidationLineupPosition[] = resultsFormData.line_results
                                        .filter(lr => lr.position_number !== lineResult.position_number)
                                        .map(lr => ({
                                          position_number: lr.position_number,
                                          player1_id: lr.player1_id || 0,
                                          player2_id: lr.player2_id || null
                                        }));
                                      
                                      // Add current position to lineup for filtering (includes player2 if set)
                                      currentLineup.push({
                                        position_number: lineResult.position_number,
                                        player1_id: 0, // We're selecting player1, so it's not set yet
                                        player2_id: lineResult.player2_id || null
                                      });
                                      
                                      const lineupFormatForValidation: ValidationLineupFormat | null = selectedTeam.lineup_format ? {
                                        total_lines: selectedTeam.lineup_format.total_lines,
                                        max_sets_per_line: selectedTeam.lineup_format.max_sets_per_line,
                                        allow_multi_line_selection: (selectedTeam.lineup_format as any).allow_multi_line_selection || false,
                                        positions: selectedTeam.lineup_format.positions.map(p => ({
                                          position_number: p.position_number,
                                          match_type: p.match_type
                                        }))
                                      } : null;
                                      
                                      const players = selectedTeam.members.map(m => ({
                                        id: m.user_id,
                                        name: getDisplayName(m.user),
                                        gender: (m.user as any).gender || null,
                                        ntrp_rating: (m.user as any).ntrp_rating || m.ntrp_rating || null
                                      }));

                                      const eligiblePlayers = lineupFormatForValidation && matchType
                                        ? getEligiblePlayersForPosition(
                                            players,
                                            lineResult.position_number,
                                            matchType,
                                            currentLineup,
                                            lineupFormatForValidation,
                                            'player1_id',
                                            undefined,
                                            selectedTeam?.max_combined_ntrp_rating || null
                                          )
                                        : players;

                                      const eligibleMembers = eligiblePlayers.map(eligiblePlayer =>
                                        selectedTeam.members.find(m => m.user_id === eligiblePlayer.id)!
                                      ).filter(Boolean);

                                      return (
                                        <select
                                          value={lineResult.player1_id || 0}
                                          onChange={(e) => handleResultsFormChange(lineResult.position_number, 'player1_id', parseInt(e.target.value) || 0)}
                                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                                        >
                                          <option value={0}>Select Player</option>
                                          {eligibleMembers.map((member) => {
                                            const ntrpRating = (member.user as any).ntrp_rating || member.ntrp_rating;
                                            const displayLabel = ntrpRating !== null && ntrpRating !== undefined
                                              ? `${getDisplayName(member.user)} (${ntrpRating.toFixed(1)})`
                                              : getDisplayName(member.user);
                                            return (
                                              <option key={member.user_id} value={member.user_id}>
                                                {displayLabel}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      );
                                    })()}
                                  </div>

                                  {/* Player 2 */}
                                  {isDoubles && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Player 2
                                    </label>
                                    {(() => {
                                      // Get eligible players for player2
                                      const currentLineup: ValidationLineupPosition[] = resultsFormData.line_results
                                        .filter(lr => lr.position_number !== lineResult.position_number)
                                        .map(lr => ({
                                          position_number: lr.position_number,
                                          player1_id: lr.player1_id || 0,
                                          player2_id: lr.player2_id || null
                                        }));
                                      
                                      // Add current position to lineup for filtering
                                      currentLineup.push({
                                        position_number: lineResult.position_number,
                                        player1_id: lineResult.player1_id || 0,
                                        player2_id: null
                                      });
                                      
                                      const lineupFormatForValidation: ValidationLineupFormat | null = selectedTeam.lineup_format ? {
                                        total_lines: selectedTeam.lineup_format.total_lines,
                                        max_sets_per_line: selectedTeam.lineup_format.max_sets_per_line,
                                        allow_multi_line_selection: (selectedTeam.lineup_format as any).allow_multi_line_selection || false,
                                        positions: selectedTeam.lineup_format.positions.map(p => ({
                                          position_number: p.position_number,
                                          match_type: p.match_type
                                        }))
                                      } : null;
                                      
                                      const players = selectedTeam.members.map(m => ({
                                        id: m.user_id,
                                        name: getDisplayName(m.user),
                                        gender: (m.user as any).gender || null,
                                        ntrp_rating: (m.user as any).ntrp_rating || m.ntrp_rating || null
                                      }));

                                      const eligiblePlayers = lineupFormatForValidation && matchType
                                        ? getEligiblePlayersForPosition(
                                            players,
                                            lineResult.position_number,
                                            matchType,
                                            currentLineup,
                                            lineupFormatForValidation,
                                            'player2_id',
                                            lineResult.player1_id || undefined,
                                            selectedTeam?.max_combined_ntrp_rating || null
                                          )
                                        : players.filter(p => p.id !== lineResult.player1_id);

                                      const eligibleMembers = eligiblePlayers.map(eligiblePlayer =>
                                        selectedTeam.members.find(m => m.user_id === eligiblePlayer.id)!
                                      ).filter(Boolean);

                                      return (
                                        <select
                                          value={lineResult.player2_id || ''}
                                          onChange={(e) => handleResultsFormChange(lineResult.position_number, 'player2_id', e.target.value ? parseInt(e.target.value) : null)}
                                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                                        >
                                          <option value="">Select Player</option>
                                          {eligibleMembers.map((member) => {
                                            const ntrpRating = (member.user as any).ntrp_rating || member.ntrp_rating;
                                            const displayLabel = ntrpRating !== null && ntrpRating !== undefined
                                              ? `${getDisplayName(member.user)} (${ntrpRating.toFixed(1)})`
                                              : getDisplayName(member.user);
                                            return (
                                              <option key={member.user_id} value={member.user_id}>
                                                {displayLabel}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      );
                                    })()}
                                  </div>
                                  )}
                                </div>

                                {/* Right Side: Opponent Players */}
                                <div className="space-y-4">
                                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Opponent Team</div>
                                  
                                  {/* Opponent Player 1 */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Opponent Player 1
                                    </label>
                                    <input
                                      type="text"
                                      value={lineResult.opponent_player1_name || ''}
                                      onChange={(e) => handleResultsFormChange(lineResult.position_number, 'opponent_player1_name', e.target.value)}
                                      placeholder="Enter opponent player name"
                                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                                    />
                                  </div>

                                  {/* Opponent Player 2 */}
                                  {isDoubles && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Opponent Player 2
                                    </label>
                                    <input
                                      type="text"
                                      value={lineResult.opponent_player2_name || ''}
                                      onChange={(e) => handleResultsFormChange(lineResult.position_number, 'opponent_player2_name', e.target.value)}
                                      placeholder="Enter opponent player name"
                                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                                    />
                                  </div>
                                  )}
                                </div>
                              </div>

                              {/* Score and Result */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                {/* Score */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Score
                                  </label>
                                  <input
                                    type="text"
                                    value={lineResult.score || ''}
                                    onChange={(e) => handleResultsFormChange(lineResult.position_number, 'score', e.target.value || null)}
                                    placeholder="e.g., 6-4, 6-2 (winner's score first)"
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                                  />
                                  {(lineResult.won !== null && lineResult.won !== undefined) && !hasScore && (
                                    <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                                      Consider entering a score to complete the match information
                                    </p>
                                  )}
                                </div>

                                {/* Won/Lost */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Result
                                  </label>
                                  <select
                                    value={lineResult.won === null || lineResult.won === undefined ? '' : (lineResult.won ? 'won' : 'lost')}
                                    onChange={(e) => handleResultsFormChange(lineResult.position_number, 'won', e.target.value === '' ? null : e.target.value === 'won')}
                                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                                  >
                                    <option value="">Select result</option>
                                    <option value="lost">Lost</option>
                                    <option value="won">Won</option>
                                  </select>
                                  {hasScore && (lineResult.won === null || lineResult.won === undefined) && (
                                    <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                                      Consider selecting a result (Won/Lost) to complete the match information
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex flex-col sm:flex-row justify-end gap-3">
              <Button
                onClick={handleCloseResultsModal}
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSaveMatchResults}
                disabled={savingResults}
                variant="primary"
                size="lg"
                className="w-full sm:w-auto"
              >
                {savingResults ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Trophy className="w-4 h-4 mr-2" />
                    Save Results
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Clear All Lineups Confirmation Modal */}
      {showClearLineupsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Clear All Lineups
                </h3>
                <button
                  onClick={() => setShowClearLineupsModal(false)}
                  className="text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to clear all lineups for upcoming matches? This will remove all players from all upcoming match lineups.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  You'll need to click "Save All Changes" to apply this action.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowClearLineupsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowClearLineupsModal(false);
                    handleClearAllLineups();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors min-h-[44px]"
                >
                  Clear All Lineups
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Icon Tooltip */}
      {tooltipState && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md px-3 py-2 shadow-lg z-[10000] max-w-xs pointer-events-none"
          style={{
            top: `${tooltipState.y}px`,
            left: `${tooltipState.x}px`,
            transform: 'translate(-50%, -100%)',
            marginTop: '-8px'
          }}
          onMouseEnter={() => {}} // Keep tooltip visible when hovering over it
          onMouseLeave={() => setTooltipState(null)}
        >
          {tooltipState.text}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgb(17 24 39)'
            }}
          />
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 dark:hidden"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgb(55 65 81)'
            }}
          />
        </div>,
        document.body
      )}

      {/* Match Details Tooltip Portal */}
      {matchTooltipPosition && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 z-[9999] min-w-[320px] max-w-[400px]"
          style={{ 
            top: `${matchTooltipPosition.top}px`,
            left: `${matchTooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            animation: 'fadeIn 0.2s ease-in-out',
          }}
          onMouseEnter={() => {}} // Keep tooltip open when hovering over it
          onMouseLeave={() => {
            setMatchTooltipPosition(null);
          }}
        >
          <div className="p-4">
            {(() => {
              const match = matchTooltipPosition.match;
              const isPast = isMatchInPast(match.match_date);
              
              return (
                <div className="flex flex-col gap-4">
                  {/* Match Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <h3 className={`text-base md:text-lg font-medium ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                          {match.opponent_name}
                        </h3>
                        {isPast && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0">
                            Past
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedTeam && (
                      <p className={`text-sm mb-2 flex items-center gap-2 ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        <Users className="w-4 h-4 flex-shrink-0" />
                        {selectedTeam.name}
                      </p>
                    )}
                    <div className="space-y-1">
                      {(() => {
                        const formatted = formatMatchDateFull(match.match_date);
                        const textClass = isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300';
                        return (
                          <>
                            <p className={`text-sm flex items-center gap-2 ${textClass}`}>
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              {formatted.date}
                            </p>
                            <p className={`text-sm flex items-center gap-2 ${textClass}`}>
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              {formatted.time}
                            </p>
                          </>
                        );
                      })()}
                      {match.location && (
                        <p className={`text-sm flex items-center gap-2 ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          {match.location}
                        </p>
                      )}
                    </div>
                    {match.home_away && (
                      <p className={`text-sm ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {match.home_away === 'home' ? 'Home' : 'Away'}
                      </p>
                    )}
                    {match.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className={`text-sm font-medium mb-1 ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                          Match Notes:
                        </p>
                        <p className={`text-sm ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                          {match.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          {/* Pointer arrow */}
          <div 
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-white dark:border-b-gray-800"
            style={{
              filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))'
            }}
          />
        </div>,
        document.body
      )}

      {/* User Merge Confirmation Modal */}
      {/* Edit Member Modal */}
      {editingMemberId && editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCancelEditMember}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Edit Member: {getDisplayName(editingMember.user)}
                </h3>
                <button
                  onClick={handleCancelEditMember}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  disabled={submitting}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  {(!editingMember.user.google_id || editingMember.user.google_id === null) && 
                   (user?.role === 'ADMIN' || !editingMember.team_memberships_count || editingMember.team_memberships_count <= 1) ? (
                    <input
                      type="email"
                      value={memberEditForm.email}
                      onChange={(e) => setMemberEditForm({ ...memberEditForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter email address"
                      disabled={submitting}
                    />
                  ) : (
                    <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      {editingMember.user.email || 'No email'}
                      <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                        Email cannot be changed if user belongs to multiple teams or has already authenticated
                      </p>
                    </div>
                  )}
                </div>

                {/* Gender Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gender
                  </label>
                  <select
                    value={memberEditForm.gender || ''}
                    onChange={(e) => setMemberEditForm({ ...memberEditForm, gender: e.target.value === '' ? null : (e.target.value as 'male' | 'female' | null) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={submitting}
                  >
                    <option value="">Not set</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                {/* NTRP Rating Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    NTRP Rating
                  </label>
                  <select
                    value={memberEditForm.ntrp_rating || ''}
                    onChange={(e) => setMemberEditForm({ ...memberEditForm, ntrp_rating: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={submitting}
                  >
                    <option value="">Not set</option>
                    {NTRP_RATINGS.map((rating) => (
                      <option key={rating} value={rating}>
                        {formatNtrpRating(rating)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Lineup Preference Field */}
                {selectedTeam?.lineup_format?.positions && (() => {
                  // Filter lineup types by player gender
                  const allUniqueTypes = Array.from(new Set(selectedTeam.lineup_format!.positions.map(p => p.match_type)));
                  const genderCompatibleTypes = allUniqueTypes.filter(t => {
                    if (!memberEditForm.gender) return true; // No gender set — show all
                    if (memberEditForm.gender === 'male') return !t.startsWith('womens_');
                    if (memberEditForm.gender === 'female') return !t.startsWith('mens_');
                    return true;
                  });
                  // Only show if there are 2+ gender-compatible types to choose from
                  if (genderCompatibleTypes.length <= 1) return null;
                  // Filter out any existing preferences that are no longer gender-compatible
                  const validPrefs = memberEditForm.lineup_type_preferences.filter(p => genderCompatibleTypes.includes(p));
                  if (validPrefs.length !== memberEditForm.lineup_type_preferences.length) {
                    // Auto-clean incompatible prefs (e.g., gender was just changed)
                    setTimeout(() => setMemberEditForm(prev => ({ ...prev, lineup_type_preferences: validPrefs })), 0);
                  }
                  return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Lineup Preference
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Leave blank if no preference. Order indicates priority (1st choice at top).
                    </p>
                    {/* Current ordered preferences */}
                    {validPrefs.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {validPrefs.map((pref, index) => (
                          <div key={pref} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded px-2 py-1">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">{index + 1}.</span>
                            <MatchTypeIcon matchType={pref} iconSize="w-4 h-4" />
                            <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{formatMatchType(pref)}</span>
                            <button
                              type="button"
                              disabled={submitting || index === 0}
                              onClick={() => {
                                const newPrefs = [...memberEditForm.lineup_type_preferences];
                                [newPrefs[index - 1], newPrefs[index]] = [newPrefs[index], newPrefs[index - 1]];
                                setMemberEditForm({ ...memberEditForm, lineup_type_preferences: newPrefs });
                              }}
                              className={`p-0.5 rounded ${index === 0 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                              title="Move up"
                            >
                              <ArrowUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              disabled={submitting || index === memberEditForm.lineup_type_preferences.length - 1}
                              onClick={() => {
                                const newPrefs = [...memberEditForm.lineup_type_preferences];
                                [newPrefs[index], newPrefs[index + 1]] = [newPrefs[index + 1], newPrefs[index]];
                                setMemberEditForm({ ...memberEditForm, lineup_type_preferences: newPrefs });
                              }}
                              className={`p-0.5 rounded ${index === memberEditForm.lineup_type_preferences.length - 1 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                              title="Move down"
                            >
                              <ArrowDown className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              disabled={submitting}
                              onClick={() => {
                                setMemberEditForm({
                                  ...memberEditForm,
                                  lineup_type_preferences: memberEditForm.lineup_type_preferences.filter((_, i) => i !== index)
                                });
                              }}
                              className="p-0.5 rounded text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400"
                              title="Remove"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Add preference dropdown */}
                    {(() => {
                      const availableTypes = genderCompatibleTypes.filter(t => !memberEditForm.lineup_type_preferences.includes(t));
                      if (availableTypes.length === 0) return null;
                      return (
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              setMemberEditForm({
                                ...memberEditForm,
                                lineup_type_preferences: [...memberEditForm.lineup_type_preferences, e.target.value]
                              });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          disabled={submitting}
                        >
                          <option value="">Add preference...</option>
                          {availableTypes.map(type => (
                            <option key={type} value={type}>{formatMatchType(type)}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                  );
                })()}

                {/* Alias Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Alias (TennisRecord Name)
                  </label>
                  <input
                    type="text"
                    value={memberEditForm.tennisrecord_name}
                    onChange={(e) => setMemberEditForm({ ...memberEditForm, tennisrecord_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter alternative name or alias"
                    disabled={submitting}
                  />
                  <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                    Optional alias for this player. Only needed if different from their display name.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={handleCancelEditMember}
                  variant="secondary"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveMemberChanges}
                  variant="primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMergeConfirmationModal && mergeConfirmationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-500" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Confirm User Merge
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    {mergeConfirmationData.message}
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 mb-4 space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Source User (will be merged):</span>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{mergeConfirmationData.sourceUser.name}</p>
                      {mergeConfirmationData.sourceUser.email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{mergeConfirmationData.sourceUser.email}</p>
                      )}
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Target User (will keep email):</span>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{mergeConfirmationData.targetUser.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{mergeConfirmationData.targetUser.email}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    All data from the source user (team memberships, availability, lineups, ratings, etc.) will be transferred to the target user. The source user account will be deleted.
                  </p>
                  {user?.role !== 'ADMIN' && mergeConfirmationData.targetUser.team_memberships_count && mergeConfirmationData.targetUser.team_memberships_count > 1 && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 dark:border-amber-500 p-4 rounded-md mb-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-amber-400 dark:text-amber-500" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                            Email Editing Restriction
                          </p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            After merging, the target user will belong to multiple teams. As a captain, you will not be able to edit their email address. Only administrators can edit emails for users who belong to multiple teams.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-4">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => {
                    setShowMergeConfirmationModal(false);
                    setMergeConfirmationData(null);
                  }}
                  variant="outline"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmMerge}
                  variant="primary"
                  disabled={submitting}
                >
                  {submitting ? 'Merging...' : 'Confirm Merge'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Team Confirmation Modal */}
      {showDeleteTeamModal && selectedTeam && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Team
                  </h3>
                </div>
                <button
                  onClick={() => setShowDeleteTeamModal(false)}
                  className="text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-900 font-medium mb-3">
                  Are you sure you want to delete <span className="font-semibold">"{selectedTeam.name}"</span>? This action cannot be undone.
                </p>
                
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-red-800 font-medium mb-2">This will permanently delete:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                    <li>The team and all its configuration</li>
                    <li>All matches and match history</li>
                    <li>All lineups and lineup data</li>
                    <li>All availability records</li>
                    <li>All competitive intelligence data</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <span className="font-semibold">⚠️ Warning:</span> Deleting a team is permanent and cannot be undone.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="font-semibold">"{selectedTeam.name}"</span> to confirm deletion:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmationName}
                    onChange={(e) => setDeleteConfirmationName(e.target.value)}
                    placeholder={selectedTeam.name}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[44px]"
                    disabled={deletingTeam}
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteTeamModal(false);
                    setDeleteConfirmationName('');
                  }}
                  disabled={deletingTeam}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTeam}
                  disabled={deletingTeam || deleteConfirmationName !== selectedTeam.name}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deletingTeam ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Team
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lineup Format Warning Dialog */}
      {showLineupFormatWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Warning: This Action Will Delete All Lineups
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Changing the lineup format will <strong>permanently delete all existing lineups</strong> for this team. This action cannot be undone.
                  </p>
                  {lineupCount !== null && (
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>{lineupCount}</strong> lineup{lineupCount !== 1 ? 's' : ''} will be deleted.
                    </p>
                  )}
                  <p className="text-sm text-gray-700">
                    Are you sure you want to continue?
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => {
                    setShowLineupFormatWarning(false);
                    setLineupCount(null);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmLineupFormatEdit}
                  variant="primary"
                >
                  I Understand, Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lineup Format Edit Modal */}
      {showLineupFormatEdit && lineupFormatFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Lineup Format</h2>
                <button
                  onClick={handleCancelLineupFormatEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="total_lines" className="flex items-center gap-2 mb-1">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Number of Lines <span className="text-red-500">*</span>
                    </span>
                    <div className="relative">
                      <Info
                        className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipState({
                            text: "The number of matches played on match day. For example, 5 lines means 5 unique matches are played (10 players total for doubles).",
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                        }}
                        onMouseLeave={() => setTooltipState(null)}
                      />
                    </div>
                  </label>
                  <select
                    id="total_lines"
                    value={lineupFormatFormData.total_lines}
                    onChange={(e) => handleLineupFormatTotalLinesChange(parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      lineupFormatErrors.total_lines ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {Array.from({ length: 15 }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                  {lineupFormatErrors.total_lines && (
                    <p className="mt-1 text-sm text-red-600">{lineupFormatErrors.total_lines}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="max_sets_per_line" className="flex items-center gap-2 mb-1">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Max Sets Per Line <span className="text-red-500">*</span>
                    </span>
                    <div className="relative">
                      <Info
                        className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipState({
                            text: "The maximum number of sets played per line/match. Typically 3 sets (even if the 3rd set is a tie-break).",
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                        }}
                        onMouseLeave={() => setTooltipState(null)}
                      />
                    </div>
                  </label>
                  <select
                    id="max_sets_per_line"
                    value={lineupFormatFormData.max_sets_per_line}
                    onChange={(e) => setLineupFormatFormData({
                      ...lineupFormatFormData,
                      max_sets_per_line: parseInt(e.target.value)
                    })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      lineupFormatErrors.max_sets_per_line ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value={1}>1</option>
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                  </select>
                  {lineupFormatErrors.max_sets_per_line && (
                    <p className="mt-1 text-sm text-red-600">{lineupFormatErrors.max_sets_per_line}</p>
                  )}
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={lineupFormatFormData.allow_multi_line_selection}
                      onChange={(e) =>
                        setLineupFormatFormData({
                          ...lineupFormatFormData,
                          allow_multi_line_selection: e.target.checked
                        })
                      }
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Allow Multi-Line Selection
                    </span>
                    <div className="relative">
                      <Info
                        className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipState({
                            text: "Allows a player to appear in multiple lineups for a match",
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                        }}
                        onMouseLeave={() => setTooltipState(null)}
                      />
                    </div>
                  </label>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 ml-6">
                    Can players be selected for multiple lines in a single match?
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Match Type for Each Line <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {lineupFormatFormData.positions.map((position) => (
                      <div key={position.position_number}>
                        <label className="block text-sm text-gray-600 mb-1">
                          Line {position.position_number}
                        </label>
                        <select
                          value={position.match_type}
                          onChange={(e) =>
                            handleLineupFormatPositionChange(position.position_number, e.target.value)
                          }
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            lineupFormatErrors[`position_${position.position_number}`]
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select match type...</option>
                          {MATCH_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        {lineupFormatErrors[`position_${position.position_number}`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {lineupFormatErrors[`position_${position.position_number}`]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                <Button
                  onClick={handleCancelLineupFormatEdit}
                  variant="outline"
                  disabled={savingLineupFormat}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveLineupFormat}
                  variant="primary"
                  disabled={savingLineupFormat}
                >
                  {savingLineupFormat ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Publish Lineup Dialog */}
      {publishDialogMatchId !== null && (() => {
        // Find match and lineup data
        const match = strategizeData?.matches.find(m => m.id === publishDialogMatchId);
        const lineupData = strategizeData?.lineups[publishDialogMatchId] || [];
        const membersMap = new Map(strategizeData?.members.map(m => [m.id, m]) || []);
        
        // Convert lineup data to format with player names
        const lineup = lineupData.map(pos => {
          const player1 = membersMap.get(pos.player1_id);
          const player2 = pos.player2_id ? membersMap.get(pos.player2_id) : undefined;
          
          return {
            position_number: pos.position_number,
            player1_id: pos.player1_id,
            player2_id: pos.player2_id || undefined,
            player1: player1 ? { id: player1.id, name: player1.name, email: player1.email } : undefined,
            player2: player2 ? { id: player2.id, name: player2.name, email: player2.email } : undefined
          };
        }).filter(pos => pos.player1); // Only include positions with at least player1
        
        const matchWithTeam = match ? {
          ...match,
          team: selectedTeam ? {
            name: selectedTeam.name,
            lineup_format: selectedTeam.lineup_format ? {
              positions: selectedTeam.lineup_format.positions || []
            } : undefined
          } : undefined
        } : null;
        
        return (
          <PublishLineupDialog
            open={true}
            onClose={() => {
              setPublishDialogMatchId(null);
              setPublishDialogIsResend(false);
            }}
            onPublish={async (sendEmail, emailNote, recipientType, recipientIds) => {
              if (publishDialogIsResend) {
                await handleResendLineupEmail(publishDialogMatchId, emailNote, recipientType, recipientIds);
              } else {
                await handlePublishLineup(publishDialogMatchId, sendEmail, emailNote, recipientType, recipientIds);
              }
            }}
            isResend={publishDialogIsResend}
            match={matchWithTeam}
            lineup={lineup}
            teamMembers={strategizeData?.members}
          />
        );
      })()}

      {/* Suggest Lineups Confirmation Modal */}
      <SuggestLineupsModal
        isOpen={showSuggestLineupsModal}
        onConfirm={() => {
          setShowSuggestLineupsModal(false);
          handleSuggestLineups();
        }}
        onCancel={() => setShowSuggestLineupsModal(false)}
      />

      {/* Clear Availability Confirmation Modal */}
      {showClearAvailabilityModal && pendingMatchUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Clear Availability?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    You've changed the match date, time, or location. Would you like to clear all existing availability for this match?
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {pendingMatchUpdate.availabilityCount} player{pendingMatchUpdate.availabilityCount !== 1 ? 's have' : ' has'} set their availability.
                  </p>
                </div>
                <button
                  onClick={handleClearAvailabilityCancel}
                  disabled={submitting}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleClearAvailabilityCancel}
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    No, Keep Availability
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAvailabilityConfirm}
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {submitting ? 'Updating...' : 'Yes, Clear Availability'}
                  </button>
                </div>
              </div>
            </div>
          </div>
      )}

      {/* Confirmation Toggle Modal */}
      {showConfirmationModal && pendingConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {pendingConfirmation.isConfirming ? 'Confirm Lineup Position' : 'Unconfirm Lineup Position'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {pendingConfirmation.isConfirming
                  ? `Are you sure you want to confirm the lineup position for ${pendingConfirmation.playerName}?`
                  : `Are you sure you want to unconfirm the lineup position for ${pendingConfirmation.playerName}?`}
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    setPendingConfirmation(null);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={performConfirmationToggle}
                  variant="primary"
                >
                  {pendingConfirmation.isConfirming ? 'Confirm' : 'Unconfirm'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lineup Text Copy Modal */}
      {showLineupTextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Lineup Text
              </h3>
              <button
                onClick={() => setShowLineupTextModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Click the Copy button below to copy the lineup to your clipboard:
              </p>
              <div
                className="w-full h-64 p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm overflow-y-auto whitespace-pre-wrap"
              >
                {lineupTextContent}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={async () => {
                  try {
                    // Try modern clipboard API (has fresh user gesture from button click)
                    if (navigator.clipboard) {
                      await navigator.clipboard.writeText(lineupTextContent);
                      toast.success('Copied to clipboard!');
                      return;
                    }
                    
                    // Fallback: create temporary textarea for execCommand
                    const textarea = document.createElement('textarea');
                    textarea.value = lineupTextContent;
                    textarea.style.position = 'fixed';
                    textarea.style.left = '-999999px';
                    document.body.appendChild(textarea);
                    textarea.select();
                    
                    try {
                      const successful = document.execCommand('copy');
                      if (successful) {
                        toast.success('Copied to clipboard!');
                      } else {
                        toast.error('Copy failed. Please try again.');
                      }
                    } finally {
                      document.body.removeChild(textarea);
                    }
                  } catch (error) {
                    console.error('Copy error:', error);
                    toast.error('Copy failed. Please try again.');
                  }
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 min-h-[44px] flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy to Clipboard
              </button>
              <button
                onClick={() => setShowLineupTextModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;